import { MobXProviderContext } from "mobx-react";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { Stepper } from 'primereact/stepper';
import { StepperPanel } from 'primereact/stepperpanel';
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from 'primereact/toast';
import { Children, isValidElement, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AI_MESSAGE_FOR_COMPLIANCE, getErrorTitle, getToastMessageForSubscriptionLimitReached, JOB_STATUS, JOB_TYPE, RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES, RESIDENT_CARE_PLAN_TYPE, WEBSITE_TEMPLATE_MAX_IMAGE_FILE_SIZE_IN_BYTES } from "../../../constants/constants";
import { BillingConstants } from "../../../constants/billing";
import { RouterAddress } from "../../../constants/RouterAddress";
import { SHOW_ADL_ENTRY_POINTS } from "../../../constants/uiFlags";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { normalizePlainText, openUrlInNewTab, sanitizeRichHtml } from "../../../utils/securityUtils";
import { calculateDeepAge, capitalizeFirstLetter, getStatusNameForDisplay } from "../../../utils/stringUtils";
import BillingAlert from "../../../components/general/billingAlert";
import ProviderSubscriptionDialog from "../../../components/general/ProviderSubscriptionDialog";
import CarePlanContactsDialog, { validateCarePlanContactsForm } from "../components/CarePlanContactsDialog";
import ActivitiesOfDailyLivingSection from "../careplan/activitiesOfDailyLivingSection";
import CommunicationSection from "../careplan/communicationSection";
import HealthIndicatorSection from "../careplan/healthIndicatorSection";
import InstrumentalActivitiesSection from "../careplan/instrumentalActivitiesSection";
import LeftAloneSection from "../careplan/leftAloneSection";
import MedicationManagementSection from "../careplan/medicationManagementSection";
import PsychSocialSection from "../careplan/psychSocialSection";
import ResponsiblePartiesSection from "../careplan/responsiblePartiesSection";
import SummarySection from "../careplan/summarySection";
import TreatmentProgramSection from "../careplan/treatmentProgramSection";
import UniversalPrecautionSection from "../careplan/universalPrecautionSection";
import DailyBehaviorLogPanel from "../components/DailyBehaviorLogPanel";
import ResidentSchedulesPage from "./ResidentSchedulesPage";
import { SHOW_RESIDENT_SCHEDULES } from "../../../constants/uiFlags";
import ResidentDailyLogImportDialog from "../components/ResidentDailyLogImportDialog";
import { buildBehaviorPrnMedicationOptions, buildBehaviorPrnSyncPayload } from "../utils/behaviorPrnUtils";
import {
  DEFAULT_ACTIVITY_SECTION, DEFAULT_COMMUNICATION_SECTION, DEFAULT_HEALTH_INDICATOR_SECTION,
  DEFAULT_INSTRUMENTAL_ACTIVITY_SECTION, DEFAULT_LEFT_ALONE_SECTION, DEFAULT_MEDICATION_SECTION,
  DEFAULT_PSYCH_SOCIAL_SECTION, DEFAULT_SUMMARY_SECTION,
  DEFAULT_TREATMENT_SECTION,
  DEFAULT_UNIVERSAL_PRECAUTIONS_SECTION
} from "../../../constants/carePlanConstants";
import { Divider } from "primereact/divider";
import { Binder, BinderDocument, BinderFolder } from "../../../models/binder";
import {
  appAccentPanelStyle,
  appConfirmDialogClasses,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appWideDialogStyle,
  appInnerCardStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  appSectionCardStyle,
  renderConfirmMessage,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import RelationshipCenterDialog from "../../relationship/components/RelationshipCenterDialog";
import {
  ensureResidentBinderBundle,
  getBinderFolderByCode,
  getFolderDocuments,
  getLatestBinderDocumentVersion,
  getPrimaryDocument,
  RESIDENT_BINDER_FOLDER_CODES,
  uploadNewBinderDocument,
  upsertBinderDocumentWithVersion,
} from "../../../utils/binderFeatureUtils";
import { UserProfile } from "../../../models/userProfile";
import { Tag } from "primereact/tag";
import { ReferenceType } from "../../../models/document";

type FeatureAccessDialogType = "summary" | "compare" | "carePlanCompare" | "assessmentDelegation";
type FeatureResultType = "summary" | "compare" | "carePlanCompare";
type BehaviorTimelineFilter = "7d" | "30d" | "90d" | "all";
type CarePlanTimelineFilter = "7d" | "30d" | "90d" | "all";
const RESIDENT_WORKSPACE_TABS = [
  "profile",
  "care-plan",
  "assessment",
  "delegation-forms",
  "emar",
  "adl-tracking",
  "daily-log",
  ...(SHOW_RESIDENT_SCHEDULES ? ["schedules"] : []),
  "ai-tools",
] as const;

const orderResidentWorkspaceTabPanels = (children: any) => {
  const panelChildren = isValidElement(children) ? (children as any).props?.children : children;
  const panels = Children.toArray(panelChildren);
  const panelOrder = ["Profile", "Care Plan (DSHS format)", "Client Assessment + CarePlan (Non-DSHS format)", "Delegation Forms", "eMAR", "ADL Tracking", "Daily Activities Tracker", "Schedules", "Helpful AI Tools"];
  return panelOrder
    .map((header) => panels.find((panel) => isValidElement(panel) && (panel as any).props?.header === header))
    .filter(Boolean);
};
const EMERGENCY_PACKET_SECTION_OPTIONS = [
  { label: "Face Sheet", value: "faceSheet" },
  { label: "Current MAR", value: "currentMar" },
  { label: "Vital Tracker History", value: "vitalTrackerHistory" },
  { label: "POLST Form", value: "polst" },
  { label: "Current Med List", value: "currentMedList" },
  { label: "Diagnosis", value: "diagnosis" },
  { label: "Allergies", value: "allergies" },
  { label: "Physician", value: "physician" },
  { label: "Pharmacy Details", value: "pharmacy" },
  { label: "Emergency Contacts", value: "emergencyContacts" },
  { label: "Progress Notes", value: "progressNotes" },
] as const;
type EmergencyPacketSectionValue = typeof EMERGENCY_PACKET_SECTION_OPTIONS[number]["value"];
const DEFAULT_EMERGENCY_PACKET_SECTIONS = EMERGENCY_PACKET_SECTION_OPTIONS.map((option) => option.value);
const EMERGENCY_PACKET_RANGE_OPTIONS = [
  { label: "Last 7 days", value: "last7Days" },
  { label: "Whole month", value: "wholeMonth" },
] as const;
const BINDER_FEATURE_FILE_ACCEPT = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const BINDER_FEATURE_ALLOWED_EXTENSIONS = ["pdf", "doc", "docx"];
const MAR_COMPARE_FILE_ACCEPT = ".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.tif,.tiff,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/png,image/jpeg,image/tiff";
const MAR_COMPARE_ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "txt", "png", "jpg", "jpeg", "tif", "tiff"];
const RESIDENT_AI_TOOL_QUERY_KEYS = [
  "summarize-assessment",
  "compare-assessments",
  "compare-care-plans",
  "compare-mar-sheet",
] as const;
type ResidentAiToolQueryKey = typeof RESIDENT_AI_TOOL_QUERY_KEYS[number];
type ResidentWorkspaceTabKey = typeof RESIDENT_WORKSPACE_TABS[number];

const getFirstNonEmptyString = (...values: any[]) => {
  for (const value of values) {
    const normalized = String(value || "").trim();
    if (normalized) {
      return normalized;
    }
  }
  return "";
};

const getFirstDefinedBoolean = (...values: any[]) => {
  for (const value of values) {
    if (typeof value === "boolean") {
      return value;
    }
  }
  return undefined;
};

const getDelegationSummaryThreadKey = (form: any) => {
  const normalizeToken = (value: any) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  const formCode = String(form?.formCode || "").trim().toUpperCase();
  const threadId = String(form?.formThreadId || "").trim() || "default";
  const staffIds = Array.isArray(form?.selectedStaffIds)
    ? form.selectedStaffIds.map((staffId: any) => String(staffId || "").trim()).filter(Boolean).sort()
    : [];
  const staffNames = Array.isArray(form?.selectedStaff)
    ? form.selectedStaff.map((staff: any) => normalizeToken(staff?.name)).filter(Boolean).sort()
    : [];
  const staffKey = staffIds.length
    ? `id:${staffIds.join(",")}`
    : staffNames.length
      ? `name:${staffNames.join(",")}`
      : "general";
  const taskKey = formCode === "13-678"
    ? String(form?.taskId || "").trim()
      ? `id:${String(form.taskId).trim()}`
      : (normalizeToken(form?.taskName) ? `name:${normalizeToken(form.taskName)}` : "general")
    : "general";
  return `${formCode}::${threadId}::${staffKey}::${taskKey}`;
};

const getLastSegmentFromPath = (value: any) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return "";
  }
  const segments = normalized.split("/").filter(Boolean);
  return segments.length ? segments[segments.length - 1] : normalized;
};

const formatSyncTimestamp = (value: any) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return "Never";
  }
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return "Never";
  }
  return parsed.toLocaleString();
};

const SEX_OPTIONS = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
  { label: "Other", value: "other" },
  { label: "Unknown", value: "unknown" },
];

const formatDateToCarePlanString = (dateValue: Date | null) => {
  if (!dateValue || Number.isNaN(dateValue.getTime())) {
    return "";
  }
  const month = String(dateValue.getMonth() + 1).padStart(2, "0");
  const day = String(dateValue.getDate()).padStart(2, "0");
  const year = String(dateValue.getFullYear());
  return `${month}/${day}/${year}`;
};

const parseCarePlanDateString = (value: any) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return null;
  }

  const directDate = new Date(normalized);
  if (!Number.isNaN(directDate.getTime())) {
    return directDate;
  }

  const match = normalized.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (!match) {
    return null;
  }

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const compactProfilePanelStyle = {
  ...appNeutralPanelStyle,
  padding: "0.95rem 1rem",
} as const;

const compactProfileCardStyle = {
  padding: "0.85rem 0.95rem",
  height: "100%",
} as const;

const compactProfileGridStyle = {
  rowGap: "0.45rem",
} as const;

const compactProfileFieldCardStyle = {
  padding: "0.55rem 0.7rem",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  background: "#ffffff",
  minHeight: "100%",
} as const;

const compactProfileLabelStyle = {
  fontWeight: 700,
  color: "#334155",
  marginBottom: "0.1rem",
  fontSize: "0.78rem",
  lineHeight: 1.25,
} as const;

const compactProfileValueStyle = {
  color: "#0f172a",
  wordBreak: "break-word" as const,
  lineHeight: 1.35,
  fontSize: "0.92rem",
} as const;

const MEDICATION_DIAGNOSIS_SYNC_VERSION = "2026-06-29T03:00:00.000Z";
const RESIDENT_DETAILS_ASSESSMENT_STEP_TITLES: Record<string, string> = {
  "STEP#01": "Resident Information, Contacts & Background",
  "STEP#02": "Emergency Evacuation & Safety Needs",
  "STEP#03": "Dementia / Specialty Care Screening",
  "STEP#04": "Current Medical Diagnoses",
  "STEP#05": "Medications, Allergies & Primary MD",
  "STEP#06": "Psychosocial Profile & History",
  "STEP#07": "Mental Health, Depression & Anxiety",
  "STEP#08": "Cognition, Memory & Decision-Making",
  "STEP#09": "Sensory, Vision, Hearing & Communication",
  "STEP#10": "Medical Systems Review",
  "STEP#11": "Activities of Daily Living",
  "STEP#12": "Behavioral Observations & Supports",
  "STEP#13": "Review, Care Planning & Signatures",
};

const getResidentDetailsAssessmentStatusLabel = (status?: string) => {
  const normalizedStatus = String(status || "draft").trim().toLowerCase();
  return normalizedStatus === "completed" ? "Completed" : "Draft";
};

const getResidentDetailsAssessmentStatusSeverity = (status?: string): "success" | "warning" => {
  const normalizedStatus = String(status || "draft").trim().toLowerCase();
  return normalizedStatus === "completed" ? "success" : "warning";
};

const buildResidentDetailsDiagnosisLines = (value: any) => {
  if (Array.isArray(value)) {
    return value
      .map((item: any) => String(
        item?.description
        || item?.diagnosis
        || item?.label
        || item?.title
        || ""
      ).replace(/\s+/g, " ").trim())
      .filter(Boolean);
  }

  return String(value || "")
    .replace(/\r?\n/g, "\n")
    .replace(/[•·]/g, "\n")
    .split(/\n|;/)
    .map((line) => line.replace(/^\s*\d+[\).\-\s]+/, "").trim())
    .filter(Boolean);
};

const normalizeResidentEmarTodaySummary = (response: any, residentId: string) => {
  const normalizedResidentId = String(residentId || "").trim();
  const summaryItems = Array.isArray(response?.items)
    ? response.items
    : Array.isArray(response?.body?.items)
      ? response.body.items
      : Array.isArray(response)
        ? response
        : response && typeof response === "object" && (response?.residentId || response?.statusLabel)
          ? [response]
          : [];

  if (!summaryItems.length) {
    return null;
  }

  return summaryItems.find((item: any) => String(item?.residentId || "").trim() === normalizedResidentId) || summaryItems[0] || null;
};

const ResidentDetailsPage = () => {
  const stepperRef = useRef<Stepper>(null);

  const { homeId, residentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const mobile = useResponsive().isMobile;
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const isAdminUser = authStore.isAdmin();
  const isProviderUser = authStore.getUserRoleType() === ROLE_TYPE_PROVIDER;
  const [residentFacilityType, setResidentFacilityType] = useState<string>("");
  const isEsfResident = residentFacilityType === "esf";
  const allowRecentEntryReuse = authExperience.featureFlags.dailyBehaviorLogReuseRecentEntries.enabled
    && !authExperience.featureFlags.dailyBehaviorLogReuseRecentEntries.excludedFacilityTypes.includes(residentFacilityType);
  const requestedAiTool = String(searchParams.get("tool") || "").trim().toLowerCase();
  const activeAiToolQuery: ResidentAiToolQueryKey | "" = RESIDENT_AI_TOOL_QUERY_KEYS.includes(requestedAiTool as ResidentAiToolQueryKey)
    ? requestedAiTool as ResidentAiToolQueryKey
    : "";
  const getResidentCarePlanPath = (targetHomeId: string, targetResidentId: string) =>
    RouterAddress.getResidentCarePlanPagePathForFacility(targetHomeId, targetResidentId, residentFacilityType);

  const [updateFlag, setUpdateFlag] = useState<boolean>(false);
  const [loadingProfile, setLoadingProfile] = useState<boolean>(false);
  const [residentProfileData, setResidentProfileData] = useState<any>(null);
  const [residentProfileImageUrl, setResidentProfileImageUrl] = useState<string>("");
  const [selectedResidentProfileImageFile, setSelectedResidentProfileImageFile] = useState<File | null>(null);
  const [requestedWorkspaceTabs, setRequestedWorkspaceTabs] = useState<string[]>([]);
  const [assessmentFormsSummary, setAssessmentFormsSummary] = useState<any[]>([]);
  const [delegationFormsSummary, setDelegationFormsSummary] = useState<any[]>([]);
  const [loadingAssessmentDelegationSummary, setLoadingAssessmentDelegationSummary] = useState<boolean>(false);
  const [hasLoadedAssessmentDelegationSummary, setHasLoadedAssessmentDelegationSummary] = useState<boolean>(false);
  const [showAssessmentVersionsDialog, setShowAssessmentVersionsDialog] = useState<boolean>(false);
  const [residentEmarTodaySummary, setResidentEmarTodaySummary] = useState<any>(null);
  const [loadingResidentEmarTodaySummary, setLoadingResidentEmarTodaySummary] = useState<boolean>(false);
  const [hasLoadedResidentEmarTodaySummary, setHasLoadedResidentEmarTodaySummary] = useState<boolean>(false);
  const [residentEmarMedicationCount, setResidentEmarMedicationCount] = useState<number>(0);
  const [loadingResidentEmarMedications, setLoadingResidentEmarMedications] = useState<boolean>(false);
  const [hasLoadedResidentEmarMedications, setHasLoadedResidentEmarMedications] = useState<boolean>(false);
  const [residentEmarDailyTrackingItemCount, setResidentEmarDailyTrackingItemCount] = useState<number>(0);
  const [loadingResidentEmarDailyTrackingItems, setLoadingResidentEmarDailyTrackingItems] = useState<boolean>(false);
  const [hasLoadedResidentEmarDailyTrackingItems, setHasLoadedResidentEmarDailyTrackingItems] = useState<boolean>(false);
  const [activeAssessmentStepRecords, setActiveAssessmentStepRecords] = useState<any[]>([]);
  const [loadingActiveAssessmentStepRecords, setLoadingActiveAssessmentStepRecords] = useState<boolean>(false);
  const [profileFormData, setProfileFormData] = useState<any>({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    active: true,
    isOnMedicaid: false,
    medicaidClientId: "",
    providerOneNumber: "",
    primaryPhysicianName: "",
    primaryPhysicianClinic: "",
    primaryPhysicianPhoneNumber: "",
    primaryPhysicianFaxNumber: "",
    primaryPhysicianEmail: "",
    primaryPhysicianAddress: "",
    pharmacyName: "",
    pharmacyPhoneNumber: "",
    pharmacyFaxNumber: "",
    pharmacyEmail: "",
    pharmacyAddress: "",
    primaryInsuranceName: "",
    primaryInsurancePlanType: "",
    primaryInsuranceMemberId: "",
    primaryInsuranceGroupNumber: "",
    primaryInsuranceSubscriberName: "",
    primaryInsuranceSubscriberRelationship: "",
    primaryInsurancePhone: "",
    primaryInsuranceClaimsPhone: "",
    secondaryInsuranceName: "",
    secondaryInsurancePlanType: "",
    secondaryInsuranceMemberId: "",
    secondaryInsuranceGroupNumber: "",
    secondaryInsuranceSubscriberName: "",
    secondaryInsuranceSubscriberRelationship: "",
    secondaryInsurancePhone: "",
    secondaryInsuranceClaimsPhone: "",
    insuranceNotes: "",
  });
  const [isEditingProfileSection, setIsEditingProfileSection] = useState<boolean>(false);
  const [savingProfileSection, setSavingProfileSection] = useState<boolean>(false);
  const [profileFormErrors, setProfileFormErrors] = useState<Record<string, string>>({});
  const [isEditingCarePlanContacts, setIsEditingCarePlanContacts] = useState<boolean>(false);
  const [isEditingCarePlanBasics, setIsEditingCarePlanBasics] = useState<boolean>(false);
  const [savingCarePlanContacts, setSavingCarePlanContacts] = useState<boolean>(false);
  const [savingCarePlanBasics, setSavingCarePlanBasics] = useState<boolean>(false);
  const [carePlanContactsErrors, setCarePlanContactsErrors] = useState<Record<string, string>>({});
  const [carePlanBasicsErrors, setCarePlanBasicsErrors] = useState<Record<string, string>>({});
  const [carePlanContactsFormData, setCarePlanContactsFormData] = useState<any>({
    primaryPhysicianName: "",
    primaryPhysicianClinic: "",
    primaryPhysicianPhone: "",
    primaryPhysicianFax: "",
    primaryPhysicianEmail: "",
    primaryPhysicianAddress: "",
    pharmacyName: "",
    pharmacyPhone: "",
    pharmacyFax: "",
    pharmacyEmail: "",
    pharmacyAddress: "",
  });
  const [carePlanBasicsFormData, setCarePlanBasicsFormData] = useState<any>({
    dateOfBirth: "",
    gender: "",
    anyAllergies: false,
    allergiesDescribed: "",
  });

  const [loadingCarePlan, setLoadingCarePlan] = useState<boolean>(false);
  const [hasLoadedResidentCarePlan, setHasLoadedResidentCarePlan] = useState<boolean>(false);
  const [fieldAutoSaveIndicator, setFieldAutoSaveIndicator] = useState<{
    visible: boolean;
    top: number;
    left: number;
    message: string;
    state: "saving" | "saved";
  }>({
    visible: false,
    top: 0,
    left: 0,
    message: "",
    state: "saved",
  });
  const [doesCarePlanExist, setDoesCarePlanExist] = useState<boolean>(false);
  const requestedWorkspaceTab = String(searchParams.get("tab") || "profile").trim().toLowerCase();
  const normalizedRequestedWorkspaceTab = !doesCarePlanExist && requestedWorkspaceTab === "profile"
    ? "care-plan"
    : isEsfResident && requestedWorkspaceTab === "delegation-forms"
      ? "profile"
      : requestedWorkspaceTab;
  const visibleWorkspaceTabs = (isEsfResident
    ? RESIDENT_WORKSPACE_TABS.filter((tab) => tab !== "delegation-forms")
    : RESIDENT_WORKSPACE_TABS
  ).filter((tab) => doesCarePlanExist || tab !== "profile");
  const activeWorkspaceTab: ResidentWorkspaceTabKey = RESIDENT_WORKSPACE_TABS.includes(normalizedRequestedWorkspaceTab as ResidentWorkspaceTabKey)
    ? normalizedRequestedWorkspaceTab as ResidentWorkspaceTabKey
    : doesCarePlanExist
      ? "profile"
      : "care-plan";
  const effectiveWorkspaceTab: ResidentWorkspaceTabKey = activeAiToolQuery ? "ai-tools" : activeWorkspaceTab;
  const activeWorkspaceTabIndex = visibleWorkspaceTabs.indexOf(effectiveWorkspaceTab);
  const [carePlanSummaryData, setCarePlanSummaryData] = useState<any>(null);
  const [carePlanResponsiblePartiesData, setCarePlanResponsiblePartiesData] = useState<any>(null);
  const [carePlanCommunicationData, setCarePlanCommunicationData] = useState<any>(null);
  const [carePlanMedicationManagementData, setCarePlanMedicationManagementData] = useState<any>(null);
  const [carePlanHealthIndicatorData, setCarePlanHealthIndicatorData] = useState<any>(null);
  const [carePlanInstrumentalActivitiesData, setCarePlanInstrumentalActivitiesData] = useState<any>(null);
  const [carePlanActivitiesOfDailyLivingData, setCarePlanActivitiesOfDailyLivingData] = useState<any>(null);
  const [carePlanUniversalPrecautionsData, setCarePlanUniversalPrecautionsData] = useState<any>(null);
  const [carePlanLeftAloneStatusData, setCarePlanLeftAloneStatusData] = useState<any>(null);
  const [carePlanPsychSocialCognitiveData, setCarePlanPsychSocialCognitiveData] = useState<any>(null);
  const [carePlanTreatmentProgramTherapiesData, setCarePlanTreatmentProgramTherapiesData] = useState<any>(null);
  const [showBehaviorInsightsDialog, setShowBehaviorInsightsDialog] = useState<boolean>(false);
  const [loadingBehaviorInsights, setLoadingBehaviorInsights] = useState<boolean>(false);
  const [residentBehaviorSnapshot, setResidentBehaviorSnapshot] = useState<any>(null);
  const [residentBehaviorTimeline, setResidentBehaviorTimeline] = useState<any[]>([]);
  const [showBehaviorLogDialog, setShowBehaviorLogDialog] = useState<boolean>(false);
  const [showDailyLogImportDialog, setShowDailyLogImportDialog] = useState<boolean>(false);
  const [loadingBehaviorLogEntries, setLoadingBehaviorLogEntries] = useState<boolean>(false);
  const [savingBehaviorLogEntry, setSavingBehaviorLogEntry] = useState<boolean>(false);
  const [loadingPrnMedicationOptions, setLoadingPrnMedicationOptions] = useState<boolean>(false);
  const [prnMedicationOptions, setPrnMedicationOptions] = useState<Array<{ label: string; value: string; medicationName?: string }>>([]);
  const [residentBehaviorLogEntries, setResidentBehaviorLogEntries] = useState<any[]>([]);
  const [residentBehaviorLogPage, setResidentBehaviorLogPage] = useState<number>(1);
  const [residentBehaviorLogPageTokens, setResidentBehaviorLogPageTokens] = useState<string[]>([""]);
  const [residentBehaviorLogNextToken, setResidentBehaviorLogNextToken] = useState<string>("");
  const residentBehaviorLogPageRef = useRef<number>(1);
  const residentBehaviorLogPageTokensRef = useRef<string[]>([""]);
  const [latestIncidentReportEntry, setLatestIncidentReportEntry] = useState<any>(null);
  const [latestDailyNoteEntry, setLatestDailyNoteEntry] = useState<any>(null);
  const [behaviorInsightsActiveTab, setBehaviorInsightsActiveTab] = useState<number>(0);
  const [behaviorTimelineFilter, setBehaviorTimelineFilter] = useState<BehaviorTimelineFilter>("30d");
  const [behaviorTimelineBehaviorFilter, setBehaviorTimelineBehaviorFilter] = useState<string>("all");
  const [importingBehaviorInsights, setImportingBehaviorInsights] = useState<boolean>(false);
  const [showCarePlanTrackingDialog, setShowCarePlanTrackingDialog] = useState<boolean>(false);
  const [loadingCarePlanTracking, setLoadingCarePlanTracking] = useState<boolean>(false);
  const [residentCarePlanTrackingSnapshot, setResidentCarePlanTrackingSnapshot] = useState<any>(null);
  const [residentCarePlanTrackingTimeline, setResidentCarePlanTrackingTimeline] = useState<any[]>([]);
  const [carePlanTimelineFilter, setCarePlanTimelineFilter] = useState<CarePlanTimelineFilter>("30d");
  const [carePlanTimelineSectionFilter, setCarePlanTimelineSectionFilter] = useState<string>("all");
  const [importingCarePlanTracking, setImportingCarePlanTracking] = useState<boolean>(false);
  const behaviorInsightsAutoImportedRef = useRef(false);
  const carePlanTrackingAutoImportedRef = useRef(false);
  const medicationCompareFileInputRef = useRef<HTMLInputElement | null>(null);
  const clinicalContactAutoSyncAttemptedResidentIdRef = useRef<string>("");
  const [syncingClinicalContactsFromCarePlan, setSyncingClinicalContactsFromCarePlan] = useState<boolean>(false);
  const [syncingMedicationDiagnosisFromCarePlan, setSyncingMedicationDiagnosisFromCarePlan] = useState<boolean>(false);
  const [showFullMedicationDiagnosisDetails, setShowFullMedicationDiagnosisDetails] = useState<boolean>(false);

  const [generatingCarePlanDocument, setGeneratingCarePlanDocument] = useState<boolean>(false);
  const [generatingResidentProfileReportDocument, setGeneratingResidentProfileReportDocument] = useState<boolean>(false);
  const [showEmergencyPacketDialog, setShowEmergencyPacketDialog] = useState<boolean>(false);
  const [generatingEmergencyPacketDocument, setGeneratingEmergencyPacketDocument] = useState<boolean>(false);
  const [emergencyPacketRangeType, setEmergencyPacketRangeType] = useState<"last7Days" | "wholeMonth">("last7Days");
  const [selectedEmergencyPacketSections, setSelectedEmergencyPacketSections] = useState<EmergencyPacketSectionValue[]>(DEFAULT_EMERGENCY_PACKET_SECTIONS);
  const [emergencyPolstUploadFile, setEmergencyPolstUploadFile] = useState<File | null>(null);
  const [emergencyPolstUploadKey, setEmergencyPolstUploadKey] = useState<number>(0);
  const hasSelectedEmergencyPolstUploadFile = Boolean(String(emergencyPolstUploadFile?.name || "").trim());
  const [carePlanDocumentDownloadLink, setCarePlanDocumentDownloadLink] = useState<string>("");
  const [carePlanTotalTimeTaken, setCarePlanTotalTimeTaken] = useState<number>(0);

  const [showHelpDialog, setShowHelpDialog] = useState<boolean>(false);
  const [helpMessage, setHelpMessage] = useState<string>("");
  const [isUserSubscribed, setIsUserSubscribed] = useState<boolean>(true);
  const [hasResolvedSubscriptionStatus, setHasResolvedSubscriptionStatus] = useState<boolean>(false);
  const canArchiveResident = isAdminUser || (isProviderUser && hasResolvedSubscriptionStatus && isUserSubscribed);
  const [showProviderSubscriptionDialog, setShowProviderSubscriptionDialog] = useState<boolean>(false);
  const [showResidentArchiveLimitDialog, setShowResidentArchiveLimitDialog] = useState(false);
  const [residentArchiveLimitDetails, setResidentArchiveLimitDetails] = useState<{ billingPeriodStart?: string | null; billingPeriodEnd?: string | null }>({});
  const [providerSubscriptionFeatureName, setProviderSubscriptionFeatureName] = useState<string>("this feature");

  const [ncpJobCompletedSuccessfully, setNcpJobCompletedSuccessfully] = useState<boolean>(false);

  const [doesNCPExtractedTextExist, setDoesNCPExtractedTextExist] = useState<boolean>(false);
  const [assessmentSummaryData, setAssessmentSummaryData] = useState<any>(null);
  const [processingAssessmentSummary, setProcessingAssessmentSummary] = useState<boolean>(false);
  const [showAssessmentSummary, setShowAssessmentSummary] = useState<boolean>(false);
  const [doesAssessmentSummaryExist, setDoesAssessmentSummaryExist] = useState<boolean>(false);
  const [doesNextAssessmentSummaryExist, setDoesNextAssessmentSummaryExist] = useState<boolean>(false);

  // NCP generated successfully
  const [ncpGeneratedSuccessfully, setNcpGeneratedSuccessfully] = useState<boolean>(false);

  // Last asessment details

  // compare assessment
  const [showCompareAssessmentPopUp, setShowCompareAssessmentPopUp] = useState<boolean>(false);
  const [nextAssessmentNCPGenerated, setNextAssessmentNCPGenerated] = useState<boolean>(false);
  const [doesNextNCPExtractedTextExist, setDoesNextNCPExtractedTextExist] = useState<boolean>(false);
  const [compareAssessmentSummaryData, setCompareAssessmentSummaryData] = useState<any>(null);
  const [processingCompareAssessmentSummary, setProcessingCompareAssessmentSummary] = useState<boolean>(false);
  const [doesCompareAssessmentSummaryExist, setDoesCompareAssessmentSummaryExist] = useState<boolean>(false);
  const [showStandaloneSummaryDialog, setShowStandaloneSummaryDialog] = useState<boolean>(false);
  const [showStandaloneCompareDialog, setShowStandaloneCompareDialog] = useState<boolean>(false);
  const [showFeatureAccessDialog, setShowFeatureAccessDialog] = useState<boolean>(false);
  const [featureAccessDialogMessage, setFeatureAccessDialogMessage] = useState<string>("");
  const [featureAccessDialogType, setFeatureAccessDialogType] = useState<FeatureAccessDialogType>("summary");
  const [featureAccessDialogAvailableCredits, setFeatureAccessDialogAvailableCredits] = useState<number>(0);
  const [featureAccessDialogForSubscribedUser, setFeatureAccessDialogForSubscribedUser] = useState<boolean>(false);
  const [featureAccessDialogCloseAction, setFeatureAccessDialogCloseAction] = useState<(() => void) | null>(null);
  const [residentBinder, setResidentBinder] = useState<Binder | null>(null);
  const [residentBinderFolders, setResidentBinderFolders] = useState<BinderFolder[]>([]);
  const [residentBinderDocuments, setResidentBinderDocuments] = useState<BinderDocument[]>([]);
  const [selectedSummaryAssessmentDocumentId, setSelectedSummaryAssessmentDocumentId] = useState<string>("");
  const [selectedCompareFirstAssessmentDocumentId, setSelectedCompareFirstAssessmentDocumentId] = useState<string>("");
  const [selectedCompareSecondAssessmentDocumentId, setSelectedCompareSecondAssessmentDocumentId] = useState<string>("");
  const [selectedCarePlanCompareFirstDocumentId, setSelectedCarePlanCompareFirstDocumentId] = useState<string>("");
  const [selectedCarePlanCompareSecondDocumentId, setSelectedCarePlanCompareSecondDocumentId] = useState<string>("");
  const [summaryUploadFile, setSummaryUploadFile] = useState<File | null>(null);
  const [compareFirstUploadFile, setCompareFirstUploadFile] = useState<File | null>(null);
  const [compareSecondUploadFile, setCompareSecondUploadFile] = useState<File | null>(null);
  const [carePlanCompareFirstUploadFile, setCarePlanCompareFirstUploadFile] = useState<File | null>(null);
  const [carePlanCompareSecondUploadFile, setCarePlanCompareSecondUploadFile] = useState<File | null>(null);
  const [uploadingBinderDialogFile, setUploadingBinderDialogFile] = useState<string>("");
  const [processingStandaloneSummary, setProcessingStandaloneSummary] = useState<boolean>(false);
  const [processingStandaloneCompare, setProcessingStandaloneCompare] = useState<boolean>(false);
  const [processingStandaloneCarePlanCompare, setProcessingStandaloneCarePlanCompare] = useState<boolean>(false);
  const [latestStandaloneSummaryJobId, setLatestStandaloneSummaryJobId] = useState<string>("");
  const [summaryProcessingStartedAt, setSummaryProcessingStartedAt] = useState<number | null>(null);
  const [compareProcessingStartedAt, setCompareProcessingStartedAt] = useState<number | null>(null);
  const [carePlanCompareProcessingStartedAt, setCarePlanCompareProcessingStartedAt] = useState<number | null>(null);
  const [summaryElapsedSeconds, setSummaryElapsedSeconds] = useState<number>(0);
  const [compareElapsedSeconds, setCompareElapsedSeconds] = useState<number>(0);
  const [carePlanCompareElapsedSeconds, setCarePlanCompareElapsedSeconds] = useState<number>(0);
  const [summaryJobStatus, setSummaryJobStatus] = useState<string>("");
  const [summaryJobSubStatus, setSummaryJobSubStatus] = useState<string>("");
  const [compareJobStatus, setCompareJobStatus] = useState<string>("");
  const [compareJobSubStatus, setCompareJobSubStatus] = useState<string>("");
  const [carePlanCompareJobStatus, setCarePlanCompareJobStatus] = useState<string>("");
  const [carePlanCompareJobSubStatus, setCarePlanCompareJobSubStatus] = useState<string>("");
  const [latestStandaloneCompareJobId, setLatestStandaloneCompareJobId] = useState<string>("");
  const [latestStandaloneCarePlanCompareJobId, setLatestStandaloneCarePlanCompareJobId] = useState<string>("");
  const [latestMedicationCompareJobId, setLatestMedicationCompareJobId] = useState<string>("");
  const [showStandaloneCarePlanCompareDialog, setShowStandaloneCarePlanCompareDialog] = useState<boolean>(false);
  const [showCarePlanCompareResultDialog, setShowCarePlanCompareResultDialog] = useState<boolean>(false);
  const [carePlanCompareSummaryData, setCarePlanCompareSummaryData] = useState<any>(null);
  const [showMedicationCompareDialog, setShowMedicationCompareDialog] = useState<boolean>(false);
  const [medicationCompareUploadFile, setMedicationCompareUploadFile] = useState<File | null>(null);
  const [processingMedicationCompare, setProcessingMedicationCompare] = useState<boolean>(false);
  const [applyingMedicationCompareChanges, setApplyingMedicationCompareChanges] = useState<boolean>(false);
  const [medicationCompareJobId, setMedicationCompareJobId] = useState<string>("");
  const [medicationCompareCompletedJobId, setMedicationCompareCompletedJobId] = useState<string>("");
  const [medicationCompareJobStatus, setMedicationCompareJobStatus] = useState<string>("");
  const [medicationCompareJobSubStatus, setMedicationCompareJobSubStatus] = useState<string>("");
  const [medicationCompareProcessingStartedAt, setMedicationCompareProcessingStartedAt] = useState<number | null>(null);
  const [medicationCompareElapsedSeconds, setMedicationCompareElapsedSeconds] = useState<number>(0);
  const [medicationCompareFileName, setMedicationCompareFileName] = useState<string>("");
  const [medicationCompareWarnings, setMedicationCompareWarnings] = useState<string[]>([]);
  const [medicationCompareResultData, setMedicationCompareResultData] = useState<any>(null);
  const [medicationCompareReportHtml, setMedicationCompareReportHtml] = useState<string>("");
  const [showShareResidentDialog, setShowShareResidentDialog] = useState<boolean>(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<UserProfile | null>(null);
  const [showProviderOneIdDialog, setShowProviderOneIdDialog] = useState<boolean>(false);
  const [providerOneIdDraft, setProviderOneIdDraft] = useState<string>("");
  const [savingProviderOneId, setSavingProviderOneId] = useState<boolean>(false);
  const [showResidentDobDialog, setShowResidentDobDialog] = useState<boolean>(false);
  const [residentDobDraft, setResidentDobDraft] = useState<string>("");
  const [savingResidentDob, setSavingResidentDob] = useState<boolean>(false);
  const [selectedCbhsMonth, setSelectedCbhsMonth] = useState<string>(() => {
    const currentDate = new Date();
    return `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
  });
  const [generatingResidentCbhsReport, setGeneratingResidentCbhsReport] = useState<boolean>(false);
  const [checkingArchivedResidentRestore, setCheckingArchivedResidentRestore] = useState<boolean>(false);
  const [restoringArchivedResident, setRestoringArchivedResident] = useState<boolean>(false);
  const [canRestoreArchivedResident, setCanRestoreArchivedResident] = useState<boolean>(true);
  const [archivedResidentRestoreMessage, setArchivedResidentRestoreMessage] = useState<string>("");
  const residentProfileImageRefreshAttemptsRef = useRef<Set<string>>(new Set());

  const openProviderSubscriptionDialog = (featureName: string) => {
    setProviderSubscriptionFeatureName(featureName);
    setShowProviderSubscriptionDialog(true);
  };

  const runSubscribedProviderAction = (featureName: string, action: () => void) => {
    if (isAdminUser) {
      action();
      return;
    }
    if (!isUserSubscribed) {
      openProviderSubscriptionDialog(featureName);
      return;
    }
    action();
  };

  const behaviorTimelineFilterOptions = [
    { label: "Last 7 days", value: "7d" },
    { label: "Last 30 days", value: "30d" },
    { label: "Last 90 days", value: "90d" },
    { label: "All time", value: "all" },
  ];
  const carePlanTimelineFilterOptions = [
    { label: "Last 7 days", value: "7d" },
    { label: "Last 30 days", value: "30d" },
    { label: "Last 90 days", value: "90d" },
    { label: "All time", value: "all" },
  ];

  const toast = useRef<Toast>(null);
  const autoSaveHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const residentIsArchived = residentProfileData?.active === false;
  const hasCarePlanClinicalContactSyncSource = !!residentProfileData?.nCPExtractedTextS3Key || !!residentProfileData?.nextNCPExtractedTextS3Key;
  const hasMissingResidentClinicalContacts =
    !String(residentProfileData?.primaryPhysicianName || "").trim()
    || !String(residentProfileData?.pharmacyName || "").trim();
  const clinicalContactsLastSyncedAt = formatSyncTimestamp(residentProfileData?.clinicalContactsSyncedAt);
  const medicationDiagnosisText = String(
    carePlanSummaryData?.medicalDiagnosesDetails
    || carePlanMedicationManagementData?.medicationAmountReasonableDetails
    || ""
  ).trim();
  const medicationDiagnosisLastSyncedAt = formatSyncTimestamp(carePlanMedicationManagementData?.medicationDiagnosisSyncedAt);
  const medicationDiagnosisLines = medicationDiagnosisText
    ? medicationDiagnosisText
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
    : [];
  const hasMoreMedicationDiagnosisLines = medicationDiagnosisLines.length > 5;
  const visibleMedicationDiagnosisText = medicationDiagnosisText
    ? (showFullMedicationDiagnosisDetails || !hasMoreMedicationDiagnosisLines
      ? medicationDiagnosisLines.join("\n")
      : medicationDiagnosisLines.slice(0, 5).join("\n"))
    : "";
  const medicationDiagnosisSyncRequired = residentProfileData?.medicationDiagnosisSyncRequired !== false;
  const medicationDiagnosisIsCurrent =
    residentProfileData?.medicationDiagnosisSyncRequired === false
    || String(carePlanMedicationManagementData?.medicationDiagnosisSyncVersion || "").trim() === MEDICATION_DIAGNOSIS_SYNC_VERSION;
  const assessmentSummaryFormId = String(searchParams.get("formId") || "").trim();
  const latestAssessmentSummaryForm = assessmentFormsSummary
    .slice()
    .sort((left: any, right: any) =>
      String(right?.modifiedAt || right?.createdAt || "").localeCompare(String(left?.modifiedAt || left?.createdAt || ""))
    )[0] || null;
  const activeAssessmentSummaryForm = assessmentSummaryFormId
    ? assessmentFormsSummary.find((form: any) => String(form?.formId || "").trim() === assessmentSummaryFormId) || latestAssessmentSummaryForm
    : latestAssessmentSummaryForm;
  const activeAssessmentStepSections = activeAssessmentStepRecords
    .filter((record: any) => String(record?.recordType || "").startsWith("STEP#"))
    .sort((left: any, right: any) => String(left?.recordType || "").localeCompare(String(right?.recordType || "")));
  const activeAssessmentStepCount = activeAssessmentStepSections.length;
  const activeAssessmentStep01 = activeAssessmentStepSections.find((record: any) => record?.recordType === "STEP#01") || null;
  const activeAssessmentStep04 = activeAssessmentStepSections.find((record: any) => record?.recordType === "STEP#04") || null;
  const activeAssessmentStep05 = activeAssessmentStepSections.find((record: any) => record?.recordType === "STEP#05") || null;
  const activeAssessmentDiagnosisLines = buildResidentDetailsDiagnosisLines(activeAssessmentStep04?.diagnoses);
  const activeAssessmentSectionPreviewTitles = activeAssessmentStepSections
    .slice(0, 5)
    .map((record: any) => RESIDENT_DETAILS_ASSESSMENT_STEP_TITLES[String(record?.recordType || "")] || String(record?.recordType || "").trim())
    .filter(Boolean);
  const delegationSummaryUniqueForms = Array.from(new Map(
    delegationFormsSummary
      .map((form: any) => {
        const formKey = getDelegationSummaryThreadKey(form);
        const title = String(form?.formTitle || form?.title || form?.formCode || "Delegation Form").trim();
        return formKey.startsWith("::") ? null : [formKey, { ...form, summaryTitle: title }];
      })
      .filter(Boolean) as any[],
  ).values());

  const syncResidentClinicalContactsFromCarePlan = useCallback(async (options?: { showToast?: boolean }) => {
    if (!residentId || syncingClinicalContactsFromCarePlan) {
      return;
    }

    setSyncingClinicalContactsFromCarePlan(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.syncResidentClinicalContactsFromCarePlan(residentId, false);
      const updatedFields = Array.isArray(response?.updatedFields) ? response.updatedFields : [];
      const didSync = response?.synced === true || updatedFields.length > 0;
      if (didSync) {
        if (options?.showToast !== false) {
          toast.current?.show({
            severity: "success",
            summary: "Synced from Care Plan",
            detail: `Updated ${updatedFields.length} physician/pharmacy field${updatedFields.length === 1 ? "" : "s"}.`,
            life: 3200,
          });
        }
        setUpdateFlag((previousValue) => !previousValue);
        return;
      }

      if (options?.showToast !== false) {
        toast.current?.show({
          severity: "info",
          summary: "No sync updates",
          detail: response?.reason || "No missing physician or pharmacy values were found to update.",
          life: 3200,
        });
      }
    } catch (error: any) {
      if (options?.showToast !== false) {
        toast.current?.show({
          severity: "error",
          summary: getErrorTitle(error),
          detail: error?.message || "Unable to sync physician and pharmacy from care plan right now.",
          life: 4000,
        });
      }
    } finally {
      setSyncingClinicalContactsFromCarePlan(false);
    }
  }, [backendAPIStore, residentId, syncingClinicalContactsFromCarePlan]);

  const syncResidentMedicationDiagnosisFromCarePlan = useCallback(async (options?: { showToast?: boolean; forceOverwrite?: boolean }) => {
    if (!residentId || syncingMedicationDiagnosisFromCarePlan) {
      return;
    }

    setSyncingMedicationDiagnosisFromCarePlan(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.syncResidentMedicationDiagnosisFromCarePlan(residentId, options?.forceOverwrite === true);
      if (response?.medicationSection) {
        setCarePlanMedicationManagementData(response.medicationSection);
      }
      if (response?.resident) {
        setResidentProfileData(response.resident);
      }

      if (response?.synced) {
        if (options?.showToast !== false) {
          toast.current?.show({
            severity: "success",
            summary: "Medical Diagnosis Synced",
            detail: `Saved ${Number(response?.diagnosisCount || 0)} diagnosis ${Number(response?.diagnosisCount || 0) === 1 ? "entry" : "entries"} from the latest assessment.`,
            life: 3200,
          });
        }
        return;
      }

      if (options?.showToast !== false) {
        toast.current?.show({
          severity: "info",
          summary: "No sync updates",
          detail: response?.reason || "Medication diagnosis is already up to date.",
          life: 3200,
        });
      }
    } catch (error: any) {
      if (options?.showToast !== false) {
        toast.current?.show({
          severity: "error",
          summary: getErrorTitle(error),
          detail: error?.message || "Unable to sync medical diagnosis right now.",
          life: 4000,
        });
      }
    } finally {
      setSyncingMedicationDiagnosisFromCarePlan(false);
    }
  }, [backendAPIStore, residentId, syncingMedicationDiagnosisFromCarePlan]);

  const checkArchivedResidentRestoreAvailability = useCallback(async () => {
    if (isAdminUser) {
      setCanRestoreArchivedResident(true);
      setArchivedResidentRestoreMessage("");
      return true;
    }

    setCheckingArchivedResidentRestore(true);
    try {
      await backendAPIStore.init();
      const [residentCountResponse, subscriptionDetails] = await Promise.all([
        backendAPIStore.getResidentCountsCreatedBy(),
        backendAPIStore.getSubscription(authStore.getUserId(), ROLE_TYPE_PROVIDER),
      ]);
      const activeResidentCount = Number(residentCountResponse?.count || 0);
      const allowedResidents = Number(subscriptionDetails?.allowedResidents || 0);
      const hasRoom = allowedResidents > activeResidentCount;
      setCanRestoreArchivedResident(hasRoom);
      setArchivedResidentRestoreMessage(
        hasRoom
          ? ""
          : "No resident slots are available right now. Free up a slot or update billing before restoring this resident.",
      );
      return hasRoom;
    } catch (error: any) {
      setCanRestoreArchivedResident(false);
      setArchivedResidentRestoreMessage(error?.message || "Unable to verify resident slot availability right now.");
      return false;
    } finally {
      setCheckingArchivedResidentRestore(false);
    }
  }, [authStore, backendAPIStore, isAdminUser]);

  const restoreArchivedResident = async () => {
    if (!residentProfileData?.residentId) {
      return;
    }

    const canRestore = await checkArchivedResidentRestoreAvailability();
    if (!canRestore) {
      return;
    }

    setRestoringArchivedResident(true);
    try {
      await backendAPIStore.init();
      const updatedResidentProfile = await backendAPIStore.updateResident({
        ...residentProfileData,
        active: true,
      });
      setResidentProfileData(updatedResidentProfile || {
        ...residentProfileData,
        active: true,
      });
      setArchivedResidentRestoreMessage("");
      setCanRestoreArchivedResident(true);
      setUpdateFlag((previousValue) => !previousValue);
      toast.current?.show({ severity: "success", summary: "Resident Restored", detail: "Resident was restored to the active list.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to restore resident.", life: 3000 });
    } finally {
      setRestoringArchivedResident(false);
    }
  };

  useEffect(() => {
    const loadResidentDetails = async () => {
      setResidentFacilityType("");
      setLoadingProfile(true);
      try {
        await backendAPIStore.init();
        const residentProfileData = await backendAPIStore.getResidentDetails(residentId!);
        const homeProfileData = homeId
          ? await backendAPIStore.getBusinessHomeDetails(homeId).catch(() => null)
          : null;
        setResidentFacilityType(String(homeProfileData?.facilityType || "").trim().toLowerCase());
        setResidentProfileData(residentProfileData);
        setSelectedResidentProfileImageFile(null);
        setProfileFormData({
          firstName: residentProfileData?.firstName || "",
          lastName: residentProfileData?.lastName || "",
          dateOfBirth: residentProfileData?.dateOfBirth || "",
          gender: residentProfileData?.gender || "",
          active: residentProfileData?.active !== false,
          isOnMedicaid: !!residentProfileData?.isOnMedicaid,
          primaryPhysicianName: residentProfileData?.primaryPhysicianName || "",
          primaryPhysicianClinic: residentProfileData?.primaryPhysicianClinic || "",
          primaryPhysicianPhoneNumber: residentProfileData?.primaryPhysicianPhoneNumber || "",
          primaryPhysicianFaxNumber: residentProfileData?.primaryPhysicianFaxNumber || "",
          primaryPhysicianEmail: residentProfileData?.primaryPhysicianEmail || "",
          primaryPhysicianAddress: residentProfileData?.primaryPhysicianAddress || "",
          pharmacyName: residentProfileData?.pharmacyName || "",
          pharmacyPhoneNumber: residentProfileData?.pharmacyPhoneNumber || "",
          pharmacyFaxNumber: residentProfileData?.pharmacyFaxNumber || "",
          pharmacyEmail: residentProfileData?.pharmacyEmail || "",
          pharmacyAddress: residentProfileData?.pharmacyAddress || "",
        });
        setResidentDobDraft(String(residentProfileData?.dateOfBirth || "").trim());
        setDoesNCPExtractedTextExist(!!residentProfileData.nCPExtractedTextS3Key);
        setDoesAssessmentSummaryExist(residentProfileData.assessmentSummaryCompleted);
        setNcpGeneratedSuccessfully(residentProfileData.ncpGenerated);

        setNextAssessmentNCPGenerated(residentProfileData.nextAssessmentNCPGenerated);
        setDoesNextAssessmentSummaryExist(residentProfileData.nextAssessmentSummaryCompleted);
        setDoesNextNCPExtractedTextExist(!!residentProfileData.nextNCPExtractedTextS3Key);
        setDoesCompareAssessmentSummaryExist(residentProfileData.compareAssessmentSummaryCompleted);
        await loadResidentProfileImage(residentId!);

        setHasLoadedResidentCarePlan(false);
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      } finally {
        setLoadingProfile(false);
      }
    };
    loadResidentDetails();
  }, [updateFlag, homeId, residentId, backendAPIStore, authStore, ncpJobCompletedSuccessfully, assessmentSummaryData]);

  useEffect(() => {
    clinicalContactAutoSyncAttemptedResidentIdRef.current = "";
  }, [residentId]);

  useEffect(() => {
    setRequestedWorkspaceTabs([activeWorkspaceTab]);
    setDoesCarePlanExist(false);
    setHasLoadedResidentCarePlan(false);
    setLoadingCarePlan(false);
    setAssessmentFormsSummary([]);
    setDelegationFormsSummary([]);
    setHasLoadedAssessmentDelegationSummary(false);
    setResidentEmarTodaySummary(null);
    setHasLoadedResidentEmarTodaySummary(false);
    setResidentEmarMedicationCount(0);
    setHasLoadedResidentEmarMedications(false);
    setLoadingResidentEmarMedications(false);
    setResidentEmarDailyTrackingItemCount(0);
    setHasLoadedResidentEmarDailyTrackingItems(false);
    setLoadingResidentEmarDailyTrackingItems(false);
    setActiveAssessmentStepRecords([]);
    setLoadingAssessmentDelegationSummary(false);
    setLoadingResidentEmarTodaySummary(false);
    setLoadingActiveAssessmentStepRecords(false);
  }, [residentId]);

  useEffect(() => {
    if (
      activeWorkspaceTab !== "profile"
      && activeWorkspaceTab !== "care-plan"
      && activeWorkspaceTab !== "assessment"
      && activeWorkspaceTab !== "delegation-forms"
      && activeWorkspaceTab !== "emar"
    ) {
      return;
    }

    setRequestedWorkspaceTabs((previousTabs) => (
      previousTabs.includes(activeWorkspaceTab)
        ? previousTabs
        : [...previousTabs, activeWorkspaceTab]
    ));
  }, [activeWorkspaceTab]);

  useEffect(() => {
    if (!residentId || loadingProfile || !residentProfileData) {
      return;
    }

    if (clinicalContactAutoSyncAttemptedResidentIdRef.current === residentId) {
      return;
    }

    const canAutoSync = hasMissingResidentClinicalContacts && hasCarePlanClinicalContactSyncSource;
    if (!canAutoSync) {
      return;
    }

    clinicalContactAutoSyncAttemptedResidentIdRef.current = residentId;
    void syncResidentClinicalContactsFromCarePlan({ showToast: false });
  }, [
    hasCarePlanClinicalContactSyncSource,
    hasMissingResidentClinicalContacts,
    loadingProfile,
    residentId,
    residentProfileData,
    syncResidentClinicalContactsFromCarePlan,
  ]);

  useEffect(() => {
    if (loadingProfile || !residentId) {
      return;
    }

    const shouldLoadResidentCarePlan =
      requestedWorkspaceTabs.includes("profile")
      || requestedWorkspaceTabs.includes("care-plan");
    if (!shouldLoadResidentCarePlan || hasLoadedResidentCarePlan || loadingCarePlan) {
      return;
    }

    void getResidentCarePlanData();
  }, [hasLoadedResidentCarePlan, loadingCarePlan, loadingProfile, requestedWorkspaceTabs, residentId]);

  useEffect(() => {
    if (loadingProfile || !residentId) {
      return;
    }

    const shouldLoadWorkflowSummary =
      requestedWorkspaceTabs.includes("assessment")
      || requestedWorkspaceTabs.includes("delegation-forms");
    if (!shouldLoadWorkflowSummary || loadingAssessmentDelegationSummary || hasLoadedAssessmentDelegationSummary) {
      return;
    }

    const loadAssessmentDelegationSummary = async () => {
      try {
        setLoadingAssessmentDelegationSummary(true);
        await backendAPIStore.init();
        const [assessmentResponse, delegationResponse] = await Promise.all([
          backendAPIStore.getProviderAssessmentForms(residentId),
          isEsfResident ? Promise.resolve(null) : backendAPIStore.getProviderDelegationForms(residentId),
        ]);
        const nextAssessmentForms = Array.isArray(assessmentResponse?.forms)
          ? assessmentResponse.forms
          : Array.isArray(assessmentResponse)
            ? assessmentResponse
            : [];
        const nextDelegationForms = Array.isArray(delegationResponse?.forms)
          ? delegationResponse.forms
          : Array.isArray(delegationResponse)
            ? delegationResponse
            : [];
        setAssessmentFormsSummary(nextAssessmentForms);
        setDelegationFormsSummary(nextDelegationForms);
      } catch (error) {
        console.error("Unable to load resident workflow summary", error);
      } finally {
        setHasLoadedAssessmentDelegationSummary(true);
        setLoadingAssessmentDelegationSummary(false);
      }
    };

    void loadAssessmentDelegationSummary();
  }, [backendAPIStore, hasLoadedAssessmentDelegationSummary, isEsfResident, loadingAssessmentDelegationSummary, loadingProfile, requestedWorkspaceTabs, residentId]);

  useEffect(() => {
    if (!residentId || activeWorkspaceTab !== "assessment") {
      setActiveAssessmentStepRecords([]);
      setLoadingActiveAssessmentStepRecords(false);
      return;
    }

    const targetFormId = String(activeAssessmentSummaryForm?.formId || "").trim();
    if (!targetFormId) {
      setActiveAssessmentStepRecords([]);
      setLoadingActiveAssessmentStepRecords(false);
      return;
    }

    let isCancelled = false;

    const loadActiveAssessmentSteps = async () => {
      try {
        setLoadingActiveAssessmentStepRecords(true);
        await backendAPIStore.init();
        const response = await backendAPIStore.getProviderAssessmentFormSteps(targetFormId);
        if (isCancelled) {
          return;
        }
        setActiveAssessmentStepRecords(Array.isArray(response) ? response : []);
      } catch (error) {
        if (!isCancelled) {
          console.error("Unable to load assessment step summary", error);
          setActiveAssessmentStepRecords([]);
        }
      } finally {
        if (!isCancelled) {
          setLoadingActiveAssessmentStepRecords(false);
        }
      }
    };

    void loadActiveAssessmentSteps();

    return () => {
      isCancelled = true;
    };
  }, [activeAssessmentSummaryForm?.formId, activeWorkspaceTab, backendAPIStore, residentId]);

  useEffect(() => {
    const normalizedResidentId = String(residentId || "").trim();
    if (!normalizedResidentId) {
      return;
    }

    if (!requestedWorkspaceTabs.includes("emar") || loadingResidentEmarTodaySummary || hasLoadedResidentEmarTodaySummary) {
      return;
    }

    let isCancelled = false;
    const todayDate = new Date().toISOString().slice(0, 10);

    const loadResidentEmarTodaySummary = async () => {
      try {
        setLoadingResidentEmarTodaySummary(true);
        await backendAPIStore.init();
        const response = await backendAPIStore.getResidentEmarBoardSummary([normalizedResidentId], todayDate);
        if (isCancelled) {
          return;
        }
        setResidentEmarTodaySummary(normalizeResidentEmarTodaySummary(response, normalizedResidentId));
      } catch (error) {
        if (!isCancelled) {
          console.error("Unable to load resident eMAR today summary", error);
          setResidentEmarTodaySummary(null);
        }
      } finally {
        if (!isCancelled) {
          setHasLoadedResidentEmarTodaySummary(true);
          setLoadingResidentEmarTodaySummary(false);
        }
      }
    };

    void loadResidentEmarTodaySummary();

    return () => {
      isCancelled = true;
    };
  }, [
    backendAPIStore,
    hasLoadedResidentEmarTodaySummary,
    requestedWorkspaceTabs,
    residentId,
  ]);

  useEffect(() => {
    const normalizedResidentId = String(residentId || "").trim();
    if (!normalizedResidentId || !showEmergencyPacketDialog) {
      return;
    }

    if (loadingResidentEmarMedications || hasLoadedResidentEmarMedications) {
      return;
    }

    let isCancelled = false;

    const loadResidentEmarMedications = async () => {
      try {
        setLoadingResidentEmarMedications(true);
        await backendAPIStore.init();
        const response = await backendAPIStore.getResidentEmarMedications(normalizedResidentId);
        if (isCancelled) {
          return;
        }
        const items = Array.isArray(response?.items)
          ? response.items
          : Array.isArray(response?.body?.items)
            ? response.body.items
            : Array.isArray(response)
              ? response
              : [];
        const activeItems = items.filter((item: any) => item?.active !== false);
        setResidentEmarMedicationCount(activeItems.length);
      } catch (error) {
        if (!isCancelled) {
          console.error("Unable to load resident eMAR medications", error);
          setResidentEmarMedicationCount(0);
        }
      } finally {
        if (!isCancelled) {
          setHasLoadedResidentEmarMedications(true);
          setLoadingResidentEmarMedications(false);
        }
      }
    };

    void loadResidentEmarMedications();

    return () => {
      isCancelled = true;
    };
  }, [
    backendAPIStore,
    hasLoadedResidentEmarMedications,
    loadingResidentEmarMedications,
    residentId,
    showEmergencyPacketDialog,
  ]);

  useEffect(() => {
    const normalizedResidentId = String(residentId || "").trim();
    if (!normalizedResidentId) {
      return;
    }

    if (!requestedWorkspaceTabs.includes("emar") || loadingResidentEmarDailyTrackingItems || hasLoadedResidentEmarDailyTrackingItems) {
      return;
    }

    let isCancelled = false;

    const loadResidentEmarDailyTrackingItems = async () => {
      try {
        setLoadingResidentEmarDailyTrackingItems(true);
        await backendAPIStore.init();
        const response = await backendAPIStore.getResidentEmarDailyTrackingItems(normalizedResidentId);
        if (isCancelled) {
          return;
        }
        const items = Array.isArray(response?.items)
          ? response.items
          : Array.isArray(response?.body?.items)
            ? response.body.items
            : Array.isArray(response)
              ? response
              : [];
        const activeItems = items.filter((item: any) => item?.active !== false);
        setResidentEmarDailyTrackingItemCount(activeItems.length);
      } catch (error) {
        if (!isCancelled) {
          console.error("Unable to load resident eMAR daily tracking items", error);
          setResidentEmarDailyTrackingItemCount(0);
        }
      } finally {
        if (!isCancelled) {
          setHasLoadedResidentEmarDailyTrackingItems(true);
          setLoadingResidentEmarDailyTrackingItems(false);
        }
      }
    };

    void loadResidentEmarDailyTrackingItems();

    return () => {
      isCancelled = true;
    };
  }, [
    backendAPIStore,
    hasLoadedResidentEmarDailyTrackingItems,
    loadingResidentEmarDailyTrackingItems,
    requestedWorkspaceTabs,
    residentId,
  ]);

  useEffect(() => {
    const normalizedResidentId = String(residentId || "").trim();
    if (!normalizedResidentId || !showEmergencyPacketDialog) {
      return;
    }

    if (loadingResidentEmarDailyTrackingItems || hasLoadedResidentEmarDailyTrackingItems) {
      return;
    }

    let isCancelled = false;

    const loadResidentEmarDailyTrackingItems = async () => {
      try {
        setLoadingResidentEmarDailyTrackingItems(true);
        await backendAPIStore.init();
        const response = await backendAPIStore.getResidentEmarDailyTrackingItems(normalizedResidentId);
        if (isCancelled) {
          return;
        }
        const items = Array.isArray(response?.items)
          ? response.items
          : Array.isArray(response?.body?.items)
            ? response.body.items
            : Array.isArray(response)
              ? response
              : [];
        const activeItems = items.filter((item: any) => item?.active !== false);
        setResidentEmarDailyTrackingItemCount(activeItems.length);
      } catch (error) {
        if (!isCancelled) {
          console.error("Unable to load resident eMAR daily tracking items", error);
          setResidentEmarDailyTrackingItemCount(0);
        }
      } finally {
        if (!isCancelled) {
          setHasLoadedResidentEmarDailyTrackingItems(true);
          setLoadingResidentEmarDailyTrackingItems(false);
        }
      }
    };

    void loadResidentEmarDailyTrackingItems();

    return () => {
      isCancelled = true;
    };
  }, [
    backendAPIStore,
    hasLoadedResidentEmarDailyTrackingItems,
    loadingResidentEmarDailyTrackingItems,
    residentId,
    showEmergencyPacketDialog,
  ]);

  useEffect(() => {
    if (loadingProfile || !residentId) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const hydrateDeferredResidentData = async () => {
        try {
          const [userProfileResult, binderResult, lastJobsResponse, subscriptionResult] = await Promise.allSettled([
            backendAPIStore.getUserProfile(authStore.getUserId()),
            loadResidentBinderData(),
            backendAPIStore.getLastJobs(residentId),
            isProviderUser ? backendAPIStore.getSubscription(authStore.getUserId(), ROLE_TYPE_PROVIDER) : Promise.resolve(null),
          ]);

          if (userProfileResult.status === "fulfilled") {
            setCurrentUserProfile(userProfileResult.value || null);
            setProviderOneIdDraft(String(userProfileResult.value?.providerOneId || "").trim());
          } else {
            console.error("Error loading current user profile for resident details", userProfileResult.reason);
          }

          if (binderResult.status === "rejected") {
            console.error("Error loading resident binder bundle", binderResult.reason);
          }

          if (lastJobsResponse.status === "fulfilled" && lastJobsResponse.value.jobs && lastJobsResponse.value.jobs.length > 0) {
            const latestSummaryJob = lastJobsResponse.value.jobs.find((job: any) => job.jobType === JOB_TYPE.ASSESSMENT_SUMMARY && job.resultOutputKey);
            const latestCompareJob = lastJobsResponse.value.jobs.find((job: any) => job.jobType === JOB_TYPE.COMPARE_ASSESSMENT && job.resultOutputKey);
            const latestCarePlanCompareJob = lastJobsResponse.value.jobs.find((job: any) => job.jobType === JOB_TYPE.COMPARE_CARE_PLAN && job.resultOutputKey);
            const latestMedicationCompareJob = lastJobsResponse.value.jobs.find((job: any) =>
              (job.jobType === "MAR_SHEET_COMPARE" || job.jobType === "MAR_SHEET_BINDER_COMPARE")
              && (
                String(job?.jobStatus || "").trim().toUpperCase() === "COMPLETED"
                || !!job?.completedAt
                || !!job?.medicationComparison
                || !!job?.carePlanMedicationComparison
              )
            );
            setLatestStandaloneSummaryJobId(latestSummaryJob?.jobId || "");
            setLatestStandaloneCompareJobId(latestCompareJob?.jobId || "");
            setLatestStandaloneCarePlanCompareJobId(latestCarePlanCompareJob?.jobId || "");
            setLatestMedicationCompareJobId(latestMedicationCompareJob?.jobId || "");
          } else if (lastJobsResponse.status === "rejected") {
            console.error("Error loading recent resident jobs", lastJobsResponse.reason);
          }

          if (subscriptionResult.status === "fulfilled" && isProviderUser) {
            setIsUserSubscribed(isAdminUser || !!subscriptionResult.value?.isSubscribed);
          } else if (subscriptionResult.status === "rejected") {
            console.error("Error loading subscription details for resident page", subscriptionResult.reason);
          }
          setHasResolvedSubscriptionStatus(true);
        } catch (error: any) {
          console.error("Error hydrating deferred resident details data", error);
          setHasResolvedSubscriptionStatus(true);
        }
      };

      void hydrateDeferredResidentData();
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [loadingProfile, residentId, backendAPIStore, authStore, updateFlag, ncpJobCompletedSuccessfully, assessmentSummaryData, isProviderUser]);

  useEffect(() => {
    if (!residentIsArchived) {
      setCanRestoreArchivedResident(true);
      setArchivedResidentRestoreMessage("");
      return;
    }

    void checkArchivedResidentRestoreAvailability();
  }, [checkArchivedResidentRestoreAvailability, residentIsArchived]);

  useEffect(() => {
    const getIndicatorTarget = (fieldName?: string) => {
      if (fieldName) {
        const byId = document.getElementById(fieldName);
        if (byId) {
          return byId as HTMLElement;
        }

        const byName = document.querySelector(`[name="${fieldName}"]`) as HTMLElement | null;
        if (byName) {
          return byName;
        }
      }

      return document.activeElement instanceof HTMLElement ? document.activeElement : null;
    };

    const handleAutoSaveStatus = (event: Event) => {
      const customEvent = event as CustomEvent<{
        state: "saving" | "saved";
        fieldName?: string;
        timestamp?: string;
      }>;
      const target = getIndicatorTarget(customEvent.detail.fieldName);
      const rect = target?.getBoundingClientRect();

      if (autoSaveHideTimeoutRef.current) {
        clearTimeout(autoSaveHideTimeoutRef.current);
      }

      setFieldAutoSaveIndicator({
        visible: true,
        top: rect ? Math.max(16, rect.top + Math.min(rect.height / 2, 24)) : 120,
        left: rect ? Math.min(window.innerWidth - 190, rect.right + 12) : window.innerWidth - 200,
        message:
          customEvent.detail.state === "saving"
            ? "Autosaving..."
            : `Saved${customEvent.detail.timestamp ? ` at ${customEvent.detail.timestamp}` : ""}`,
        state: customEvent.detail.state,
      });

      if (customEvent.detail.state === "saved") {
        autoSaveHideTimeoutRef.current = setTimeout(() => {
          setFieldAutoSaveIndicator((prev) => ({ ...prev, visible: false }));
        }, 2200);
      }
    };

    window.addEventListener("careplan-autosave-status", handleAutoSaveStatus);

    return () => {
      window.removeEventListener("careplan-autosave-status", handleAutoSaveStatus);
      if (autoSaveHideTimeoutRef.current) {
        clearTimeout(autoSaveHideTimeoutRef.current);
      }
    };
  }, []);

  const getResidentCarePlanData = async () => {
    if (!residentId) {
      return;
    }

    setLoadingCarePlan(true);
    try {
      await backendAPIStore.init();
      const residentCarePlanData = await backendAPIStore.getResidentCarePlan(
        residentId,
      );
      setCarePlanSummaryData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.SUMMARY)[0]);
      setCarePlanResponsiblePartiesData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES)[0]);
      setCarePlanCommunicationData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.COMMUNICATION)[0]);
      setCarePlanMedicationManagementData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.MEDICATION_MANAGEMENT)[0]);
      setCarePlanHealthIndicatorData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.HEALTH_INDICATORS)[0]);
      setCarePlanInstrumentalActivitiesData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.INSTRUMENTAL_ACTIVITIES_OF_DAILY_LIVING)[0]);
      setCarePlanActivitiesOfDailyLivingData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.ACTIVITIES_OF_DAILY_LIVING)[0]);
      setCarePlanUniversalPrecautionsData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.UNIVERSAL_PRECAUTIONS)[0]);
      setCarePlanLeftAloneStatusData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.LEFT_ALONE_STATUS)[0]);
      setCarePlanPsychSocialCognitiveData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.PSYCH_SOCIAL_COGNITIVE)[0]);
      setCarePlanTreatmentProgramTherapiesData(residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.TREATMENT_PROGRAM_THERAPIES)[0]);

      setDoesCarePlanExist(residentCarePlanData && residentCarePlanData.length > 0);

      if (residentCarePlanData && residentCarePlanData.length > 0) {
        const summaryData = residentCarePlanData.filter((item: any) => item.carePlanType === RESIDENT_CARE_PLAN_TYPE.SUMMARY)[0];
      }
      setHasLoadedResidentCarePlan(true);
    } catch (error: any) {
      setHasLoadedResidentCarePlan(false);
      throw error;
    } finally {
      setLoadingCarePlan(false);
    }
  };

  const applyDefaultBinderSelections = (folders: BinderFolder[], documents: BinderDocument[]) => {
    const assessmentFolder = getBinderFolderByCode(folders, RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT);
    const carePlanFolder = getBinderFolderByCode(folders, RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN);
    const assessmentDocuments = getFolderDocuments(documents, assessmentFolder?.folderId);
    const carePlanDocuments = getFolderDocuments(documents, carePlanFolder?.folderId);

    setSelectedSummaryAssessmentDocumentId((currentValue) =>
      assessmentDocuments.some((document) => document.binderDocumentId === currentValue)
        ? currentValue
        : ""
    );
    setSelectedCompareFirstAssessmentDocumentId((currentValue) =>
      assessmentDocuments.some((document) => document.binderDocumentId === currentValue)
        ? currentValue
        : ""
    );
    setSelectedCompareSecondAssessmentDocumentId((currentValue) =>
      assessmentDocuments.some((document) => document.binderDocumentId === currentValue)
        ? currentValue
        : ""
    );
    setSelectedCarePlanCompareFirstDocumentId((currentValue) =>
      carePlanDocuments.some((document) => document.binderDocumentId === currentValue)
        ? currentValue
        : ""
    );
    setSelectedCarePlanCompareSecondDocumentId((currentValue) =>
      carePlanDocuments.some((document) => document.binderDocumentId === currentValue)
        ? currentValue
        : ""
    );
  };

  const loadResidentBinderData = async () => {
    if (!residentId) {
      return;
    }

    const bundle = await ensureResidentBinderBundle(backendAPIStore, residentId);
    setResidentBinder(bundle.binder);
    setResidentBinderFolders(bundle.folders);
    setResidentBinderDocuments(bundle.documents);
    applyDefaultBinderSelections(bundle.folders, bundle.documents);
  };

  useEffect(() => {
    if (!residentId) {
      return;
    }
    if (!showMedicationCompareDialog && effectiveWorkspaceTab !== "ai-tools") {
      return;
    }
    if (residentBinder?.binderId && residentBinderDocuments.length > 0) {
      return;
    }
    void loadResidentBinderData();
  }, [effectiveWorkspaceTab, residentBinder?.binderId, residentBinderDocuments.length, residentId, showMedicationCompareDialog]);

  useEffect(() => {
    if (!residentId || !activeAiToolQuery) {
      return;
    }
    if (activeAiToolQuery === "summarize-assessment") {
      setShowStandaloneSummaryDialog(true);
    } else if (activeAiToolQuery === "compare-assessments") {
      setShowStandaloneCompareDialog(true);
    } else if (activeAiToolQuery === "compare-care-plans") {
      setShowStandaloneCarePlanCompareDialog(true);
    } else if (activeAiToolQuery === "compare-mar-sheet") {
      setShowMedicationCompareDialog(true);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", "ai-tools");
    nextParams.delete("tool");
    setSearchParams(nextParams, { replace: true });
  }, [activeAiToolQuery, residentId, searchParams, setSearchParams]);

  const getResidentBinderFolder = (folderCode: string) => {
    return getBinderFolderByCode(residentBinderFolders, folderCode);
  };

  const saveGeneratedObjectKeyToResidentBinder = async (input: {
    folderCode: string;
    title: string;
    fileName: string;
    objectKey: string;
    mimeType: string;
  }) => {
    if (!residentId) {
      return;
    }

    const bundle = residentBinder?.binderId
      ? { binder: residentBinder, folders: residentBinderFolders, documents: residentBinderDocuments }
      : await ensureResidentBinderBundle(backendAPIStore, residentId);
    const targetFolder = getBinderFolderByCode(bundle.folders, input.folderCode);
    if (!bundle.binder?.binderId || !targetFolder?.folderId) {
      throw new Error("Resident binder folder is not available.");
    }

    await upsertBinderDocumentWithVersion({
      backendAPIStore,
      binderId: bundle.binder.binderId,
      folderId: targetFolder.folderId,
      folderName: targetFolder.folderName || input.title,
      referenceId: residentId,
      title: input.title,
      fileName: input.fileName,
      mimeType: input.mimeType,
      existingObjectKey: input.objectKey,
      existingDocuments: bundle.documents,
    });

    await loadResidentBinderData();
  };

  const saveGeneratedHtmlToResidentBinder = async (input: {
    folderCode: string;
    title: string;
    fileNamePrefix: string;
    htmlContent: string;
  }) => {
    if (!residentId) {
      return;
    }

    const bundle = residentBinder?.binderId
      ? { binder: residentBinder, folders: residentBinderFolders, documents: residentBinderDocuments }
      : await ensureResidentBinderBundle(backendAPIStore, residentId);
    const targetFolder = getBinderFolderByCode(bundle.folders, input.folderCode);
    if (!bundle.binder?.binderId || !targetFolder?.folderId) {
      throw new Error("Resident binder folder is not available.");
    }

    const fileName = `${input.fileNamePrefix}_${new Date().toISOString().replace(/[:.]/g, "-")}.html`;
    const documentHtml = buildWordFriendlyHtmlDocument(input.title, input.htmlContent);
    const file = new File([documentHtml], fileName, { type: "text/html" });

    await upsertBinderDocumentWithVersion({
      backendAPIStore,
      binderId: bundle.binder.binderId,
      folderId: targetFolder.folderId,
      folderName: targetFolder.folderName || input.title,
      referenceId: residentId,
      title: input.title,
      fileName,
      mimeType: "text/html",
      file,
      existingDocuments: bundle.documents,
    });

    await loadResidentBinderData();
  };

  const normalizeHtmlForWordExport = (htmlContent: string) => {
    return sanitizeRichHtml(String(htmlContent || ""))
      .replace(/linear-gradient\([^)]+\)/gi, "#eef4ff")
      .replace(/background\s*:\s*#1a3a5c/gi, "background: #eef4ff")
      .replace(/background\s*:\s*#2a5298/gi, "background: #eef4ff")
      .replace(/background-color\s*:\s*#1a3a5c/gi, "background-color: #eef4ff")
      .replace(/background-color\s*:\s*#2a5298/gi, "background-color: #eef4ff")
      .replace(/color\s*:\s*#ffffff/gi, "color: #111827")
      .replace(/color\s*:\s*white/gi, "color: #111827")
      .replace(/color\s*:\s*#fff\b/gi, "color: #111827");
  };

  const buildWordFriendlyHtmlDocument = (title: string, htmlContent: string) => {
    const normalizedTitle = title || "Generated Document";
    const safeHtmlContent = normalizeHtmlForWordExport(htmlContent);
    return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
  <head>
    <meta charset="utf-8" />
    <meta name="ProgId" content="Word.Document" />
    <meta name="Generator" content="AFH Hub" />
    <meta name="Originator" content="AFH Hub" />
    <title>${normalizedTitle}</title>
    <style>
      body {
        font-family: Arial, sans-serif;
        font-size: 11pt;
        line-height: 1.45;
        color: #111827;
        margin: 24px;
      }
      table {
        border-collapse: collapse;
        width: 100%;
      }
      th, td {
        vertical-align: top;
      }
      img {
        max-width: 100%;
      }
      * {
        color: inherit;
      }
    </style>
  </head>
  <body>
    ${safeHtmlContent}
  </body>
</html>`;
  };

  const formatBinderDocumentOptionLabel = (binderDocument: BinderDocument) => {
    const pieces = [binderDocument.title || "Untitled"];
    if (binderDocument.isPrimaryDocument) {
      pieces.push("Current");
    }
    if (binderDocument.latestVersionNumber) {
      pieces.push(`v${binderDocument.latestVersionNumber}`);
    }
    return pieces.join(" • ");
  };

  const validateBinderUploadFile = (file: File | null) => {
    if (!file) {
      return "Please choose a PDF, DOC, or DOCX file.";
    }

    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    if (file.size > RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES || !BINDER_FEATURE_ALLOWED_EXTENSIONS.includes(extension)) {
      return `Please upload a PDF, DOC, or DOCX file smaller than ${RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES / (1024 * 1024)} MB.`;
    }

    return null;
  };

  const uploadDialogFileToBinder = async (input: {
    uploadSlot: string;
    file: File | null;
    folderCode: string;
    selectDocumentId: (binderDocumentId: string) => void;
    isPrimaryDocument?: boolean;
  }) => {
    const validationMessage = validateBinderUploadFile(input.file);
    if (validationMessage) {
      toast.current?.show({ severity: 'error', summary: 'Invalid file', detail: validationMessage, life: 4000 });
      return;
    }
    if (!residentId) {
      return;
    }

    setUploadingBinderDialogFile(input.uploadSlot);
    try {
      const bundle = residentBinder?.binderId
        ? { binder: residentBinder, folders: residentBinderFolders, documents: residentBinderDocuments }
        : await ensureResidentBinderBundle(backendAPIStore, residentId);
      const targetFolder = getBinderFolderByCode(bundle.folders, input.folderCode);
      if (!bundle.binder?.binderId || !targetFolder?.folderId) {
        throw new Error("The target resident binder folder is not available.");
      }

      const createdDocument = await uploadNewBinderDocument({
        backendAPIStore,
        binderId: bundle.binder.binderId,
        folderId: targetFolder.folderId,
        folderName: targetFolder.folderName || input.file!.name,
        referenceId: residentId,
        title: input.file!.name,
        file: input.file!,
        isPrimaryDocument: !!input.isPrimaryDocument,
      });

      await loadResidentBinderData();
      if (createdDocument?.binderDocumentId) {
        input.selectDocumentId(createdDocument.binderDocumentId);
      }
      toast.current?.show({ severity: 'success', summary: 'Uploaded', detail: 'Binder document uploaded successfully.', life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error?.message || "Unable to upload the binder document.", life: 4000 });
    } finally {
      if (input.uploadSlot === "summary") {
        setSummaryUploadFile(null);
      } else if (input.uploadSlot === "compare-first") {
        setCompareFirstUploadFile(null);
      } else if (input.uploadSlot === "compare-second") {
        setCompareSecondUploadFile(null);
      } else if (input.uploadSlot === "careplan-first") {
        setCarePlanCompareFirstUploadFile(null);
      } else if (input.uploadSlot === "careplan-second") {
        setCarePlanCompareSecondUploadFile(null);
      } else if (input.uploadSlot === "emergency-polst") {
        setEmergencyPolstUploadFile(null);
      }
      setUploadingBinderDialogFile("");
    }
  };

  const closeMedicationCompareDialog = () => {
    if (processingMedicationCompare || applyingMedicationCompareChanges) {
      return;
    }
    setShowMedicationCompareDialog(false);
    setMedicationCompareUploadFile(null);
    setMedicationCompareJobId("");
    setMedicationCompareCompletedJobId("");
    setMedicationCompareJobStatus("");
    setMedicationCompareJobSubStatus("");
    setMedicationCompareProcessingStartedAt(null);
    setMedicationCompareElapsedSeconds(0);
    setMedicationCompareWarnings([]);
    setMedicationCompareResultData(null);
    setMedicationCompareReportHtml("");
    setMedicationCompareFileName("");
    if (medicationCompareFileInputRef.current) {
      medicationCompareFileInputRef.current.value = "";
    }
  };

  const startMedicationCompareReview = async (file: File, useExistingCredit: boolean = false) => {
    if (!residentId) {
      return;
    }
    const extension = String(file.name.split(".").pop() || "").toLowerCase();
    if (!MAR_COMPARE_ALLOWED_EXTENSIONS.includes(extension)) {
      toast.current?.show({
        severity: "warn",
        summary: "Unsupported file",
        detail: "Use a PDF, image, DOC, DOCX, or TXT MAR sheet file.",
        life: 4000,
      });
      return;
    }

    setProcessingMedicationCompare(true);
    setMedicationCompareUploadFile(file);
    setMedicationCompareFileName(file.name);
    setMedicationCompareWarnings([]);
    setMedicationCompareResultData(null);
    setMedicationCompareReportHtml("");
    setMedicationCompareCompletedJobId("");
    setMedicationCompareJobStatus("UPLOADING");
    setMedicationCompareJobSubStatus("Uploading MAR sheet");
    setMedicationCompareProcessingStartedAt(Date.now());
    setMedicationCompareElapsedSeconds(0);
    try {
      const objectKey = `resident/${residentId}/medication-compare/${crypto.randomUUID()}-${file.name}`;
      const presignedUrl = await backendAPIStore.getPresignedUrlForUpload(
        residentId,
        ReferenceType.RESIDENT,
        objectKey,
      );
      await backendAPIStore.uploadFileToS3(presignedUrl, file);

      setMedicationCompareJobStatus("SUBMITTING");
      setMedicationCompareJobSubStatus("Preparing medication comparison");
      const response = await backendAPIStore.compareResidentCarePlanMedicationsFromMarSheetFile({
        residentId,
        fileName: file.name,
        s3ObjectKey: objectKey,
        previewOnly: true,
        useExistingCredit,
      });
      setMedicationCompareJobId(String(response?.jobId || "").trim());
      setMedicationCompareJobStatus(String(response?.jobStatus || "SUBMITTED").trim());
      setMedicationCompareJobSubStatus(String(response?.jobSubStatus || "Preparing medication comparison").trim());
    } catch (error: any) {
      setProcessingMedicationCompare(false);
      setMedicationCompareProcessingStartedAt(null);
      setMedicationCompareElapsedSeconds(0);
      if (error?.code === "SUBSCRIPTION_LIMIT_REACHED" || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
        const availableCredits = Number(
          error?.automationCreditsAvailable
          || error?.details?.automationCreditsAvailable
          || error?.details?.featureCreditBalances?.ncp_generation
          || 0,
        );
        if (availableCredits > 0 && !useExistingCredit) {
          confirmDialog({
            message: renderConfirmMessage(
              "Use 1 automation credit",
              "You have used the 2 included MAR comparison runs for this resident in the current billing period. Continue now and use 1 automation credit?",
            ),
            icon: "pi pi-wallet",
            ...appConfirmDialogClasses,
            acceptLabel: "Use 1 Credit",
            acceptClassName: "p-button-warning",
            rejectLabel: "Cancel",
            accept: () => {
              void startMedicationCompareReview(file, true);
            },
          });
          return;
        }
      }
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to review this MAR sheet.",
        life: 4500,
      });
    }
  };

  const applyMedicationCompareChangesToCarePlan = async () => {
    if (!residentId || !medicationCompareCompletedJobId) {
      return;
    }

    setApplyingMedicationCompareChanges(true);
    try {
      const response = await backendAPIStore.applyResidentCarePlanMedicationCompareJob({
        residentId,
        jobId: medicationCompareCompletedJobId,
      });
      await getResidentCarePlanData();
      toast.current?.show({
        severity: "success",
        summary: "Care plan updated",
        detail: `${Number(response?.appliedCount || 0)} medication ${Number(response?.appliedCount || 0) === 1 ? "entry was" : "entries were"} updated from the uploaded MAR sheet.`,
        life: 4500,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to update the care plan medication list.",
        life: 4500,
      });
    } finally {
      setApplyingMedicationCompareChanges(false);
    }
  };

  const initializeDefaultCarePlanData = async () => {
    const updatedCarePlanData = {
      ...DEFAULT_SUMMARY_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedCarePlanData);

    const responsiblePartiesData = { carePlanType: RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES };
    const updatedResponsiblePartiesData = {
      ...responsiblePartiesData,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedResponsiblePartiesData);

    const updatedCommunicationData = {
      ...DEFAULT_COMMUNICATION_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedCommunicationData);

    const updatedMedicationManagementData = {
      ...DEFAULT_MEDICATION_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedMedicationManagementData);

    const updatedHealthIndicatorData = {
      ...DEFAULT_HEALTH_INDICATOR_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedHealthIndicatorData);

    const updatedInstrumentalActivitiesData = {
      ...DEFAULT_INSTRUMENTAL_ACTIVITY_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedInstrumentalActivitiesData);

    const updatedActivitiesOfDailyLivingData = {
      ...DEFAULT_ACTIVITY_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedActivitiesOfDailyLivingData);

    const updatedLeftAloneStatusData = {
      ...DEFAULT_LEFT_ALONE_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedLeftAloneStatusData);

    const updatedPsychSocialCognitiveData = {
      ...DEFAULT_PSYCH_SOCIAL_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedPsychSocialCognitiveData);

    const updatedTreatmentProgramTherapiesData = {
      ...DEFAULT_TREATMENT_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedTreatmentProgramTherapiesData);

    const updatedUniversalPrecautionsData = {
      ...DEFAULT_UNIVERSAL_PRECAUTIONS_SECTION,
      residentId: residentId,
      homeId: homeId,
    };
    await backendAPIStore.updateResidentCarePlan(updatedUniversalPrecautionsData);

    await getResidentCarePlanData();
    setDoesCarePlanExist(true);
  }

  const showGenerateNCPDocumentConfirmation = () => {
    confirmDialog({
      message: renderConfirmMessage(
        "Generate care plan document",
        "Choose whether to download the care plan document or save it directly to the resident binder.",
      ),
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: "Download",
      rejectClassName: "p-button-info p-button-outlined p-button-rounded",
      acceptLabel: "Save to Binder",
      acceptClassName: appConfirmDialogClasses.acceptClassName,
      accept: async () => {
        await generateCarePlanDocument(true);
      },
      reject: async () => {
        await generateCarePlanDocument(false);
      }
    });
  }

  const generateCarePlanDocument = async (addToResidentProfile: boolean) => {
    const startTime: number = Date.now();
    try {
      setGeneratingCarePlanDocument(true);
      if (!doesCarePlanExist) {
        await initializeDefaultCarePlanData();
      }
      await backendAPIStore.init();
      const responseNCP = await backendAPIStore.generateResidentCarePlanDocument(residentId!, homeId!);

      if (addToResidentProfile) {
        await saveGeneratedObjectKeyToResidentBinder({
          folderCode: RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN,
          title: "Negotiated Care Plan",
          fileName: responseNCP.fileName || `NegotiatedCarePlan_${new Date().toISOString()}.docx`,
          objectKey: responseNCP.outputKey,
          mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
        toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Care plan document saved to resident binder.', life: 5000 });
      } else {
        openUrlInNewTab(responseNCP.documentUrl);
      }
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 5000 });
    } finally {
      setGeneratingCarePlanDocument(false);
      const endTime: number = Date.now();
      setCarePlanTotalTimeTaken((endTime - startTime) / 1000); // time in seconds
      await getResidentCarePlanData();
    }
  };

  const showGenerateResidentProfileReportConfirmation = () => {
    confirmDialog({
      message: renderConfirmMessage(
        "Generate resident face sheet",
        "Choose whether to download the face sheet or save it directly to the resident binder.",
      ),
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: "Download",
      rejectClassName: "p-button-info p-button-outlined p-button-rounded",
      acceptLabel: "Save to Binder",
      acceptClassName: appConfirmDialogClasses.acceptClassName,
      accept: async () => {
        await generateResidentProfileReportDocument(true);
      },
      reject: async () => {
        await generateResidentProfileReportDocument(false);
      }
    });
  }

  const generateResidentProfileReportDocument = async (addToResidentProfile: boolean) => {
    try {
      setGeneratingResidentProfileReportDocument(true);
      await backendAPIStore.init();
      const response = await backendAPIStore.generateResidentProfileReportDocument(residentId!, homeId!);

      if (addToResidentProfile) {
        await saveGeneratedObjectKeyToResidentBinder({
          folderCode: RESIDENT_BINDER_FOLDER_CODES.RESIDENT_INFORMATION,
          title: "Resident Face Sheet",
          fileName: response.fileName || `ResidentFaceSheet_${new Date().toISOString()}.docx`,
          objectKey: response.outputKey,
          mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
        toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Resident face sheet saved to resident binder.', life: 5000 });
      } else {
        openUrlInNewTab(response.documentUrl);
      }
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 5000 });
    } finally {
      setGeneratingResidentProfileReportDocument(false);
    }
  };

  const uploadEmergencyPolstToBinder = async () => {
    if (!emergencyPolstUploadFile) {
      toast.current?.show({ severity: "warn", summary: "POLST required", detail: "Choose the POLST file before uploading.", life: 4000 });
      return;
    }

    await uploadDialogFileToBinder({
      uploadSlot: "emergency-polst",
      file: emergencyPolstUploadFile,
      folderCode: RESIDENT_BINDER_FOLDER_CODES.LEGAL_DOCUMENTS,
      isPrimaryDocument: true,
      selectDocumentId: () => undefined,
    });
  };

  const generateEmergencyPacketDocument = async () => {
    if (!residentId || !homeId) {
      return;
    }
    const enabledSelectedSections = selectedEmergencyPacketSections.filter((section) => emergencyPacketSectionMeta[section]?.enabled);
    if (enabledSelectedSections.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "No sections ready",
        detail: "Set up at least one required section before generating the emergency packet.",
        life: 4000,
      });
      return;
    }

    try {
      setGeneratingEmergencyPacketDocument(true);
      await backendAPIStore.init();
      const response = await backendAPIStore.generateEmergencyPacketDocument({
        residentId,
        homeId,
        packetRangeType: emergencyPacketRangeType,
        selectedSections: enabledSelectedSections,
      });
      openUrlInNewTab(response.documentUrl);
      setShowEmergencyPacketDialog(false);
      toast.current?.show({
        severity: "success",
        summary: "Generated",
        detail: response.fileName || "Emergency packet generated successfully.",
        life: 4000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to generate the emergency packet.",
        life: 5000,
      });
    } finally {
      setGeneratingEmergencyPacketDocument(false);
    }
  };

  const onViewAssessmentSummaryClick = async (useExistingCredit: boolean = false) => {
    try {
      setShowAssessmentSummary(true);
      await backendAPIStore.init();

      if (nextAssessmentNCPGenerated) {
        if (!doesNextAssessmentSummaryExist) {
          console.log("Generating next assessment summary as it does not exist");
          await backendAPIStore.generateAssessmentSummary(residentId!, useExistingCredit);
        } else {
          console.log("Retrieving next assessment summary as it already exists");
          const assessmentSummaryResponse = await backendAPIStore.retrieveAssessmentSummary(residentId!);
          const summaryResponse = await fetch(assessmentSummaryResponse.preSignedUrl!);
          const summaryData = await summaryResponse.json();
          setAssessmentSummaryData(summaryData.response);
          return;
        }
      } else {
        if (!doesAssessmentSummaryExist) {
          await backendAPIStore.generateAssessmentSummary(residentId!, useExistingCredit);
        } else {
          const assessmentSummaryResponse = await backendAPIStore.retrieveAssessmentSummary(residentId!);
          const summaryResponse = await fetch(assessmentSummaryResponse.preSignedUrl!);
          const summaryData = await summaryResponse.json();
          setAssessmentSummaryData(summaryData.response);
          return;
        }
      }

      const PROCESSING_STATUS = 'processing';
      const COMPLETED_STATUS = 'completed';
      const FAILED_STATUS = 'failed';
      const SUCCESS_STATUS = 'success';
      let statusCheck = PROCESSING_STATUS;
      do {
        await new Promise(resolve => setTimeout(resolve, 5000));
        const response = await backendAPIStore.retrieveAssessmentSummary(residentId!);
        if (response.status === COMPLETED_STATUS) {
          const summaryResponse = await fetch(response.preSignedUrl!);
          const summaryData = await summaryResponse.json();
          if (summaryData && summaryData.status === SUCCESS_STATUS) {
            setAssessmentSummaryData(summaryData.response);
            statusCheck = SUCCESS_STATUS;
            setDoesAssessmentSummaryExist(true);
          } else {
            toast.current?.show({ severity: 'error', summary: 'Failed', detail: 'Assessment summary generation failed. Please try again.', life: 5000 });
            statusCheck = FAILED_STATUS;
          }
        } else {
          setProcessingAssessmentSummary(true);
        }
      } while (statusCheck === PROCESSING_STATUS);
    } catch (error: any) {
      if (error?.code === "SUBSCRIPTION_LIMIT_REACHED" || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
        setShowAssessmentSummary(false);
        promptForFeatureCredits("summary", error, () => {
          setShowAssessmentSummary(false);
        });
      } else {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 5000 });
      }
    }
  }

  const submitCarePlanData = async (carePlanData: any, options?: { showSuccessToast?: boolean }) => {
    try {
      setLoadingCarePlan(true);
      await backendAPIStore.init();
      const updatedCarePlanData = {
        ...carePlanData,
        residentId: residentId,
        homeId: homeId,
      };
      await backendAPIStore.updateResidentCarePlan(updatedCarePlanData);
      await getResidentCarePlanData();
      if (options?.showSuccessToast) {
        toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Care plan changes saved successfully.', life: 2500 });
      }
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
    } finally {
      setLoadingCarePlan(false);
    }
  };

  const sectionHeaderTemplate = (title: string) => (
    renderSurfaceHeader(
      title,
      undefined,
      "pi pi-file-edit",
    )
  );

  const onDeactivateProfile = () => {
    confirmDialog({
      message: renderConfirmMessage(
        "Deactivate resident profile",
        "This archives the resident from the active list while keeping the underlying data for record-keeping and possible recovery.",
      ),
      icon: 'pi pi-exclamation-triangle',
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          await backendAPIStore.deactivateResident(residentId!);
          toast.current?.show({ severity: 'success', summary: 'Profile Deactivated', detail: 'Resident profile has been deactivated.', life: 3000 });
          navigate(RouterAddress.PROVIDER_HOME);
        } catch (error: any) {
          if (String(error?.code || "").toUpperCase() === "RESIDENT_ARCHIVE_LIMIT_REACHED") {
            const details = error?.details?.details || error?.details || {};
            setResidentArchiveLimitDetails({
              billingPeriodStart: details?.billingPeriodStart,
              billingPeriodEnd: details?.billingPeriodEnd,
            });
            setShowResidentArchiveLimitDialog(true);
          } else {
            toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
          }
        }
      },
      reject: () => {
        // Do nothing on reject
      }
    });
  };

  const onDeleteProfile = () => {
    confirmDialog({
      message: renderConfirmMessage(
        "Delete resident profile",
        "This permanently removes the resident profile and all related information from the system. This action cannot be undone.",
      ),
      icon: 'pi pi-exclamation-triangle',
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          await backendAPIStore.deleteResident(residentId!);
          toast.current?.show({ severity: 'success', summary: 'Profile Deleted', detail: 'Resident profile has been deleted.', life: 3000 });
          navigate(RouterAddress.getAFHDetailsPagePath(homeId!));
        } catch (error: any) {
          toast.current?.show(getToastMessageForSubscriptionLimitReached(error));
        }
      },
      reject: () => {
        // Do nothing on reject
      }
    });
  };

  const profileFooterTemplate = () => (
    <div className="flex flex-wrap justify-content-end gap-2">
    </div>
  );

  const formatAgeInYearsOnly = (value?: string | null) => {
    const rawValue = String(value || "").trim();
    if (!rawValue) {
      return "";
    }
    const matchedYears = rawValue.match(/\d+/);
    return matchedYears ? `${matchedYears[0]} yrs` : rawValue;
  };

  const residentOverviewFacts = [
    residentProfileData?.dateOfBirth
      ? {
        label: "Age",
        value: formatAgeInYearsOnly(calculateDeepAge(residentProfileData.dateOfBirth)),
      }
      : null,
    residentProfileData?.dateOfBirth
      ? {
        label: "DOB",
        value: residentProfileData.dateOfBirth,
      }
      : null,
    residentProfileData?.gender
      ? {
        label: "Gender",
        value: capitalizeFirstLetter(residentProfileData.gender),
      }
      : null,
    residentProfileData?.status
      ? {
        label: "Resident Type",
        value: getStatusNameForDisplay(residentProfileData.status),
      }
      : null,
    typeof residentProfileData?.isOnMedicaid === "boolean"
      ? {
        label: "Medicaid",
        value: residentProfileData.isOnMedicaid ? "Yes" : "No",
      }
      : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  const resolvedProfileSnapshot = {
    firstName: getFirstNonEmptyString(
      residentProfileData?.firstName,
      carePlanSummaryData?.residentName?.split(" ").slice(0, -1).join(" "),
    ),
    lastName: getFirstNonEmptyString(
      residentProfileData?.lastName,
      carePlanSummaryData?.residentName?.split(" ").slice(-1)[0],
    ),
    dateOfBirth: getFirstNonEmptyString(
      carePlanSummaryData?.residentDateOfBirth,
      residentProfileData?.dateOfBirth,
    ),
    age: getFirstNonEmptyString(
      residentProfileData?.dateOfBirth ? formatAgeInYearsOnly(calculateDeepAge(residentProfileData.dateOfBirth)) : "",
      formatAgeInYearsOnly(carePlanSummaryData?.residentAge),
    ),
    gender: getFirstNonEmptyString(
      carePlanSummaryData?.residentGender,
      residentProfileData?.gender,
    ),
    isOnMedicaid: getFirstDefinedBoolean(
      residentProfileData?.isOnMedicaid,
      carePlanSummaryData?.hasMedicaidInsurance,
    ),
    primaryPhysicianName: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.primaryPhysicianName,
      carePlanSummaryData?.primaryPhysicianName,
      residentProfileData?.primaryPhysicianName,
    ),
    primaryPhysicianClinic: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.primaryPhysicianClinic,
      carePlanSummaryData?.primaryPhysicianClinic,
      residentProfileData?.primaryPhysicianClinic,
    ),
    primaryPhysicianPhoneNumber: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.primaryPhysicianPhone,
      carePlanResponsiblePartiesData?.primaryPhysicianPhoneNumber,
      carePlanSummaryData?.primaryPhysicianPhone,
      carePlanSummaryData?.primaryPhysicianPhoneNumber,
      residentProfileData?.primaryPhysicianPhoneNumber,
    ),
    primaryPhysicianFaxNumber: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.primaryPhysicianFax,
      carePlanResponsiblePartiesData?.primaryPhysicianFaxNumber,
      carePlanSummaryData?.primaryPhysicianFax,
      carePlanSummaryData?.primaryPhysicianFaxNumber,
      residentProfileData?.primaryPhysicianFaxNumber,
    ),
    primaryPhysicianEmail: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.primaryPhysicianEmail,
      carePlanSummaryData?.primaryPhysicianEmail,
      residentProfileData?.primaryPhysicianEmail,
    ),
    primaryPhysicianAddress: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.primaryPhysicianAddress,
      carePlanSummaryData?.primaryPhysicianAddress,
      residentProfileData?.primaryPhysicianAddress,
    ),
    pharmacyName: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.pharmacyName,
      carePlanSummaryData?.pharmacyName,
      residentProfileData?.pharmacyName,
    ),
    pharmacyPhoneNumber: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.pharmacyPhone,
      carePlanResponsiblePartiesData?.pharmacyPhoneNumber,
      carePlanSummaryData?.pharmacyPhone,
      carePlanSummaryData?.pharmacyPhoneNumber,
      residentProfileData?.pharmacyPhoneNumber,
    ),
    pharmacyFaxNumber: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.pharmacyFax,
      carePlanResponsiblePartiesData?.pharmacyFaxNumber,
      carePlanSummaryData?.pharmacyFax,
      carePlanSummaryData?.pharmacyFaxNumber,
      residentProfileData?.pharmacyFaxNumber,
    ),
    pharmacyEmail: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.pharmacyEmail,
      carePlanSummaryData?.pharmacyEmail,
      residentProfileData?.pharmacyEmail,
    ),
    pharmacyAddress: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.pharmacyAddress,
      carePlanSummaryData?.pharmacyAddress,
      residentProfileData?.pharmacyAddress,
    ),
    medicaidClientId: getFirstNonEmptyString(
      residentProfileData?.medicaidClientId,
    ),
    providerOneNumber: getFirstNonEmptyString(
      residentProfileData?.providerOneNumber,
    ),
    primaryInsuranceName: getFirstNonEmptyString(
      residentProfileData?.primaryInsuranceName,
    ),
    primaryInsurancePlanType: getFirstNonEmptyString(
      residentProfileData?.primaryInsurancePlanType,
    ),
    primaryInsuranceMemberId: getFirstNonEmptyString(
      residentProfileData?.primaryInsuranceMemberId,
    ),
    primaryInsuranceGroupNumber: getFirstNonEmptyString(
      residentProfileData?.primaryInsuranceGroupNumber,
    ),
    primaryInsuranceSubscriberName: getFirstNonEmptyString(
      residentProfileData?.primaryInsuranceSubscriberName,
    ),
    primaryInsuranceSubscriberRelationship: getFirstNonEmptyString(
      residentProfileData?.primaryInsuranceSubscriberRelationship,
    ),
    primaryInsurancePhone: getFirstNonEmptyString(
      residentProfileData?.primaryInsurancePhone,
    ),
    primaryInsuranceClaimsPhone: getFirstNonEmptyString(
      residentProfileData?.primaryInsuranceClaimsPhone,
    ),
    secondaryInsuranceName: getFirstNonEmptyString(
      residentProfileData?.secondaryInsuranceName,
    ),
    secondaryInsurancePlanType: getFirstNonEmptyString(
      residentProfileData?.secondaryInsurancePlanType,
    ),
    secondaryInsuranceMemberId: getFirstNonEmptyString(
      residentProfileData?.secondaryInsuranceMemberId,
    ),
    secondaryInsuranceGroupNumber: getFirstNonEmptyString(
      residentProfileData?.secondaryInsuranceGroupNumber,
    ),
    secondaryInsuranceSubscriberName: getFirstNonEmptyString(
      residentProfileData?.secondaryInsuranceSubscriberName,
    ),
    secondaryInsuranceSubscriberRelationship: getFirstNonEmptyString(
      residentProfileData?.secondaryInsuranceSubscriberRelationship,
    ),
    secondaryInsurancePhone: getFirstNonEmptyString(
      residentProfileData?.secondaryInsurancePhone,
    ),
    secondaryInsuranceClaimsPhone: getFirstNonEmptyString(
      residentProfileData?.secondaryInsuranceClaimsPhone,
    ),
    insuranceNotes: getFirstNonEmptyString(
      residentProfileData?.insuranceNotes,
    ),
    anyAllergies: getFirstDefinedBoolean(
      carePlanMedicationManagementData?.anyAllergies,
      carePlanHealthIndicatorData?.anyAllergies,
    ),
    allergiesDescribed: getFirstNonEmptyString(
      carePlanMedicationManagementData?.allergiesDescribed,
      carePlanHealthIndicatorData?.allergySubstanceDetails,
      carePlanSummaryData?.residentAllergies,
    ),
  };

  const collateralContacts = Array.isArray(carePlanResponsiblePartiesData?.collateralContacts)
    ? carePlanResponsiblePartiesData.collateralContacts
    : [];

  const buildCarePlanContactsFormState = () => ({
    primaryPhysicianName: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.primaryPhysicianName,
      carePlanSummaryData?.primaryPhysicianName,
      residentProfileData?.primaryPhysicianName,
    ),
    primaryPhysicianClinic: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.primaryPhysicianClinic,
      carePlanSummaryData?.primaryPhysicianClinic,
      residentProfileData?.primaryPhysicianClinic,
    ),
    primaryPhysicianPhone: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.primaryPhysicianPhone,
      carePlanResponsiblePartiesData?.primaryPhysicianPhoneNumber,
      carePlanSummaryData?.primaryPhysicianPhone,
      carePlanSummaryData?.primaryPhysicianPhoneNumber,
      residentProfileData?.primaryPhysicianPhoneNumber,
    ),
    primaryPhysicianFax: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.primaryPhysicianFax,
      carePlanResponsiblePartiesData?.primaryPhysicianFaxNumber,
      carePlanSummaryData?.primaryPhysicianFax,
      carePlanSummaryData?.primaryPhysicianFaxNumber,
      residentProfileData?.primaryPhysicianFaxNumber,
    ),
    primaryPhysicianEmail: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.primaryPhysicianEmail,
      carePlanSummaryData?.primaryPhysicianEmail,
      residentProfileData?.primaryPhysicianEmail,
    ),
    primaryPhysicianAddress: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.primaryPhysicianAddress,
      carePlanSummaryData?.primaryPhysicianAddress,
      residentProfileData?.primaryPhysicianAddress,
    ),
    pharmacyName: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.pharmacyName,
      carePlanSummaryData?.pharmacyName,
      residentProfileData?.pharmacyName,
    ),
    pharmacyPhone: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.pharmacyPhone,
      carePlanResponsiblePartiesData?.pharmacyPhoneNumber,
      carePlanSummaryData?.pharmacyPhone,
      carePlanSummaryData?.pharmacyPhoneNumber,
      residentProfileData?.pharmacyPhoneNumber,
    ),
    pharmacyFax: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.pharmacyFax,
      carePlanResponsiblePartiesData?.pharmacyFaxNumber,
      carePlanSummaryData?.pharmacyFax,
      carePlanSummaryData?.pharmacyFaxNumber,
      residentProfileData?.pharmacyFaxNumber,
    ),
    pharmacyEmail: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.pharmacyEmail,
      carePlanSummaryData?.pharmacyEmail,
      residentProfileData?.pharmacyEmail,
    ),
    pharmacyAddress: getFirstNonEmptyString(
      carePlanResponsiblePartiesData?.pharmacyAddress,
      carePlanSummaryData?.pharmacyAddress,
      residentProfileData?.pharmacyAddress,
    ),
    collateralContacts: collateralContacts.map((contact: any) => ({
      fullName: String(contact?.fullName || "").trim(),
      relationship: String(contact?.relationship || "").trim(),
      preferredContactMethod: String(contact?.preferredContactMethod || "").trim(),
      phoneNumber: String(contact?.phoneNumber || "").trim(),
      email: String(contact?.email || "").trim(),
    })),
  });

  const buildCarePlanBasicsFormState = () => ({
    dateOfBirth: getFirstNonEmptyString(
      carePlanSummaryData?.residentDateOfBirth,
      residentProfileData?.dateOfBirth,
    ),
    gender: getFirstNonEmptyString(
      carePlanSummaryData?.residentGender,
      residentProfileData?.gender,
    ),
    anyAllergies: !!getFirstDefinedBoolean(
      carePlanMedicationManagementData?.anyAllergies,
      carePlanHealthIndicatorData?.anyAllergies,
    ),
    allergiesDescribed: getFirstNonEmptyString(
      carePlanMedicationManagementData?.allergiesDescribed,
      carePlanHealthIndicatorData?.allergySubstanceDetails,
      carePlanSummaryData?.residentAllergies,
    ),
  });

  const openCarePlanContactsDialog = () => {
    setCarePlanContactsFormData(buildCarePlanContactsFormState());
    setCarePlanContactsErrors({});
    setIsEditingCarePlanContacts(true);
  };

  const openCarePlanBasicsDialog = () => {
    setCarePlanBasicsFormData(buildCarePlanBasicsFormState());
    setCarePlanBasicsErrors({});
    setIsEditingCarePlanBasics(true);
  };

  const saveCarePlanContactsFromProfile = async () => {
    if (!residentId || !homeId) {
      return;
    }

    const { normalizedContacts, errors: nextContactErrors } = validateCarePlanContactsForm(carePlanContactsFormData);

    if (Object.keys(nextContactErrors).length > 0) {
      setCarePlanContactsErrors(nextContactErrors);
      toast.current?.show({
        severity: "warn",
        summary: "Complete required fields",
        detail: "Fix highlighted contact fields before saving.",
        life: 3500,
      });
      return;
    }

    setCarePlanContactsErrors({});

    setSavingCarePlanContacts(true);
    try {
      await backendAPIStore.init();
      const updatedResponsiblePartiesData = {
        ...(carePlanResponsiblePartiesData || { carePlanType: RESIDENT_CARE_PLAN_TYPE.RESPONSIBLE_PARTIES }),
        residentId,
        homeId,
        primaryPhysicianName: String(carePlanContactsFormData?.primaryPhysicianName || "").trim(),
        primaryPhysicianClinic: String(carePlanContactsFormData?.primaryPhysicianClinic || "").trim(),
        primaryPhysicianPhone: normalizedContacts.primaryPhysicianPhone,
        primaryPhysicianPhoneNumber: normalizedContacts.primaryPhysicianPhone,
        primaryPhysicianFax: normalizedContacts.primaryPhysicianFax,
        primaryPhysicianFaxNumber: normalizedContacts.primaryPhysicianFax,
        primaryPhysicianEmail: normalizedContacts.primaryPhysicianEmail,
        primaryPhysicianAddress: String(carePlanContactsFormData?.primaryPhysicianAddress || "").trim(),
        pharmacyName: String(carePlanContactsFormData?.pharmacyName || "").trim(),
        pharmacyPhone: normalizedContacts.pharmacyPhone,
        pharmacyPhoneNumber: normalizedContacts.pharmacyPhone,
        pharmacyFax: normalizedContacts.pharmacyFax,
        pharmacyFaxNumber: normalizedContacts.pharmacyFax,
        pharmacyEmail: normalizedContacts.pharmacyEmail,
        pharmacyAddress: String(carePlanContactsFormData?.pharmacyAddress || "").trim(),
        pharmacyPreferredContactMethod: String(carePlanResponsiblePartiesData?.pharmacyPreferredContactMethod || "").trim(),
        collateralContacts: Array.isArray(carePlanContactsFormData?.collateralContacts)
          ? carePlanContactsFormData.collateralContacts.map((contact: any) => ({
            fullName: String(contact?.fullName || "").trim(),
            relationship: String(contact?.relationship || "").trim(),
            preferredContactMethod: String(contact?.preferredContactMethod || "").trim(),
            phoneNumber: String(contact?.phoneNumber || "").trim(),
            email: String(contact?.email || "").trim(),
          })).filter((contact: any) =>
            contact.fullName
            || contact.relationship
            || contact.preferredContactMethod
            || contact.phoneNumber
            || contact.email,
          )
          : [],
      };

      await backendAPIStore.updateResidentCarePlan(updatedResponsiblePartiesData);
      await getResidentCarePlanData();
      setIsEditingCarePlanContacts(false);
      toast.current?.show({
        severity: "success",
        summary: "Care plan updated",
        detail: "Primary physician, pharmacy, and point of contact details were saved to Care Plan.",
        life: 3200,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save contact details.",
        life: 4200,
      });
    } finally {
      setSavingCarePlanContacts(false);
    }
  };

  const saveCarePlanBasicsFromProfile = async () => {
    if (!residentId || !homeId) {
      return;
    }

    const hasAllergies = !!carePlanBasicsFormData?.anyAllergies;
    const allergyDetails = String(carePlanBasicsFormData?.allergiesDescribed || "").trim();
    if (hasAllergies && !allergyDetails) {
      setCarePlanBasicsErrors({ allergiesDescribed: "Allergies described is required when Any Allergies is Yes." });
      toast.current?.show({
        severity: "warn",
        summary: "Complete required fields",
        detail: "Add allergies details before saving.",
        life: 3500,
      });
      return;
    }

    setCarePlanBasicsErrors({});
    setSavingCarePlanBasics(true);
    try {
      await backendAPIStore.init();

      const parsedDate = parseCarePlanDateString(carePlanBasicsFormData?.dateOfBirth);
      const updatedSummaryData = {
        ...(carePlanSummaryData || { carePlanType: RESIDENT_CARE_PLAN_TYPE.SUMMARY }),
        residentId,
        homeId,
        residentDateOfBirth: parsedDate ? formatDateToCarePlanString(parsedDate) : "",
        residentGender: String(carePlanBasicsFormData?.gender || "").trim(),
      };

      const updatedMedicationManagementData = {
        ...(carePlanMedicationManagementData || { carePlanType: RESIDENT_CARE_PLAN_TYPE.MEDICATION_MANAGEMENT }),
        residentId,
        homeId,
        anyAllergies: hasAllergies,
        allergiesDescribed: hasAllergies ? allergyDetails : "",
      };

      await Promise.all([
        backendAPIStore.updateResidentCarePlan(updatedSummaryData),
        backendAPIStore.updateResidentCarePlan(updatedMedicationManagementData),
      ]);
      await getResidentCarePlanData();
      setIsEditingCarePlanBasics(false);
      toast.current?.show({
        severity: "success",
        summary: "Care plan updated",
        detail: "DOB, sex, and allergies were saved to Care Plan.",
        life: 3200,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save care plan basic details.",
        life: 4200,
      });
    } finally {
      setSavingCarePlanBasics(false);
    }
  };

  const buildProfileFormState = () => ({
    firstName: getFirstNonEmptyString(
      residentProfileData?.firstName,
      profileFormData?.firstName,
    ),
    lastName: getFirstNonEmptyString(
      residentProfileData?.lastName,
      profileFormData?.lastName,
    ),
    isOnMedicaid: residentProfileData?.isOnMedicaid === true,
    medicaidClientId: getFirstNonEmptyString(residentProfileData?.medicaidClientId),
    providerOneNumber: getFirstNonEmptyString(residentProfileData?.providerOneNumber),
    primaryInsuranceName: getFirstNonEmptyString(residentProfileData?.primaryInsuranceName),
    primaryInsurancePlanType: getFirstNonEmptyString(residentProfileData?.primaryInsurancePlanType),
    primaryInsuranceMemberId: getFirstNonEmptyString(residentProfileData?.primaryInsuranceMemberId),
    primaryInsuranceGroupNumber: getFirstNonEmptyString(residentProfileData?.primaryInsuranceGroupNumber),
    primaryInsuranceSubscriberName: getFirstNonEmptyString(residentProfileData?.primaryInsuranceSubscriberName),
    primaryInsuranceSubscriberRelationship: getFirstNonEmptyString(residentProfileData?.primaryInsuranceSubscriberRelationship),
    primaryInsurancePhone: getFirstNonEmptyString(residentProfileData?.primaryInsurancePhone),
    primaryInsuranceClaimsPhone: getFirstNonEmptyString(residentProfileData?.primaryInsuranceClaimsPhone),
    secondaryInsuranceName: getFirstNonEmptyString(residentProfileData?.secondaryInsuranceName),
    secondaryInsurancePlanType: getFirstNonEmptyString(residentProfileData?.secondaryInsurancePlanType),
    secondaryInsuranceMemberId: getFirstNonEmptyString(residentProfileData?.secondaryInsuranceMemberId),
    secondaryInsuranceGroupNumber: getFirstNonEmptyString(residentProfileData?.secondaryInsuranceGroupNumber),
    secondaryInsuranceSubscriberName: getFirstNonEmptyString(residentProfileData?.secondaryInsuranceSubscriberName),
    secondaryInsuranceSubscriberRelationship: getFirstNonEmptyString(residentProfileData?.secondaryInsuranceSubscriberRelationship),
    secondaryInsurancePhone: getFirstNonEmptyString(residentProfileData?.secondaryInsurancePhone),
    secondaryInsuranceClaimsPhone: getFirstNonEmptyString(residentProfileData?.secondaryInsuranceClaimsPhone),
    insuranceNotes: getFirstNonEmptyString(residentProfileData?.insuranceNotes),
    active: residentProfileData?.active !== false,
  });

  const loadResidentProfileImage = useCallback(async (targetResidentId: string) => {
    try {
      const imageUrl = await backendAPIStore.handlePresignedUrlForDownloadResidentPic(targetResidentId);
      residentProfileImageRefreshAttemptsRef.current.clear();
      setResidentProfileImageUrl(String(imageUrl || "").trim());
    } catch {
      setResidentProfileImageUrl("");
    }
  }, [backendAPIStore]);

  const refreshResidentProfileImageOnError = useCallback((failedUrl?: string) => {
    const normalizedResidentId = String(residentId || "").trim();
    const normalizedFailedUrl = String(failedUrl || residentProfileImageUrl || "").trim();
    if (!normalizedResidentId || !normalizedFailedUrl) {
      return;
    }

    if (residentProfileImageRefreshAttemptsRef.current.has(normalizedFailedUrl)) {
      return;
    }

    residentProfileImageRefreshAttemptsRef.current.add(normalizedFailedUrl);
    void loadResidentProfileImage(normalizedResidentId);
  }, [loadResidentProfileImage, residentId, residentProfileImageUrl]);

  const uploadResidentProfileImage = useCallback(async (targetResidentId: string, file: File) => {
    const presignedUrl = await backendAPIStore.handlePresignedUrlForUploadResidentPic(targetResidentId);
    await backendAPIStore.uploadFileToS3(presignedUrl, file);
  }, [backendAPIStore]);

  const handleResidentProfileImageSelection = (event: any) => {
    const file = event?.files?.[0] || null;
    setSelectedResidentProfileImageFile(file);
    if (file) {
      setResidentProfileImageUrl(URL.createObjectURL(file));
    }
  };

  const physicianContactLines = [
    resolvedProfileSnapshot.primaryPhysicianClinic || "",
    resolvedProfileSnapshot.primaryPhysicianPhoneNumber ? `Phone: ${resolvedProfileSnapshot.primaryPhysicianPhoneNumber}` : "",
    resolvedProfileSnapshot.primaryPhysicianFaxNumber ? `Fax: ${resolvedProfileSnapshot.primaryPhysicianFaxNumber}` : "",
    resolvedProfileSnapshot.primaryPhysicianEmail ? `Email: ${resolvedProfileSnapshot.primaryPhysicianEmail}` : "",
    resolvedProfileSnapshot.primaryPhysicianAddress || "",
  ].filter(Boolean);

  const pharmacyContactLines = [
    resolvedProfileSnapshot.pharmacyPhoneNumber ? `Phone: ${resolvedProfileSnapshot.pharmacyPhoneNumber}` : "",
    resolvedProfileSnapshot.pharmacyFaxNumber ? `Fax: ${resolvedProfileSnapshot.pharmacyFaxNumber}` : "",
    resolvedProfileSnapshot.pharmacyEmail ? `Email: ${resolvedProfileSnapshot.pharmacyEmail}` : "",
    resolvedProfileSnapshot.pharmacyAddress || "",
  ].filter(Boolean);
  const primaryInsuranceLines = [
    resolvedProfileSnapshot.primaryInsurancePlanType ? `Plan: ${resolvedProfileSnapshot.primaryInsurancePlanType}` : "",
    resolvedProfileSnapshot.primaryInsuranceMemberId ? `Member ID: ${resolvedProfileSnapshot.primaryInsuranceMemberId}` : "",
    resolvedProfileSnapshot.primaryInsuranceGroupNumber ? `Group: ${resolvedProfileSnapshot.primaryInsuranceGroupNumber}` : "",
    resolvedProfileSnapshot.primaryInsuranceSubscriberName ? `Subscriber: ${resolvedProfileSnapshot.primaryInsuranceSubscriberName}` : "",
    resolvedProfileSnapshot.primaryInsuranceSubscriberRelationship ? `Relationship: ${resolvedProfileSnapshot.primaryInsuranceSubscriberRelationship}` : "",
    resolvedProfileSnapshot.primaryInsurancePhone ? `Phone: ${resolvedProfileSnapshot.primaryInsurancePhone}` : "",
    resolvedProfileSnapshot.primaryInsuranceClaimsPhone ? `Claims: ${resolvedProfileSnapshot.primaryInsuranceClaimsPhone}` : "",
  ].filter(Boolean);
  const secondaryInsuranceLines = [
    resolvedProfileSnapshot.secondaryInsurancePlanType ? `Plan: ${resolvedProfileSnapshot.secondaryInsurancePlanType}` : "",
    resolvedProfileSnapshot.secondaryInsuranceMemberId ? `Member ID: ${resolvedProfileSnapshot.secondaryInsuranceMemberId}` : "",
    resolvedProfileSnapshot.secondaryInsuranceGroupNumber ? `Group: ${resolvedProfileSnapshot.secondaryInsuranceGroupNumber}` : "",
    resolvedProfileSnapshot.secondaryInsuranceSubscriberName ? `Subscriber: ${resolvedProfileSnapshot.secondaryInsuranceSubscriberName}` : "",
    resolvedProfileSnapshot.secondaryInsuranceSubscriberRelationship ? `Relationship: ${resolvedProfileSnapshot.secondaryInsuranceSubscriberRelationship}` : "",
    resolvedProfileSnapshot.secondaryInsurancePhone ? `Phone: ${resolvedProfileSnapshot.secondaryInsurancePhone}` : "",
    resolvedProfileSnapshot.secondaryInsuranceClaimsPhone ? `Claims: ${resolvedProfileSnapshot.secondaryInsuranceClaimsPhone}` : "",
  ].filter(Boolean);

  useEffect(() => {
    const handleResidentDeleteRequest = () => {
      void onDeleteProfile();
    };

    window.addEventListener("resident-details:request-delete", handleResidentDeleteRequest);
    return () => {
      window.removeEventListener("resident-details:request-delete", handleResidentDeleteRequest);
    };
  }, [onDeleteProfile]);

  const saveProfileSection = async () => {
    if (!residentProfileData?.residentId) {
      return;
    }

    const normalizedFirstName = String(profileFormData?.firstName || "").trim();
    const normalizedLastName = String(profileFormData?.lastName || "").trim();
    const normalizedProfileFormData = {
      firstName: normalizedFirstName,
      lastName: normalizedLastName,
    };
    const nextProfileFormErrors: Record<string, string> = {};
    if (!normalizedProfileFormData.firstName) {
      nextProfileFormErrors.firstName = "First name is required.";
    }
    if (!normalizedProfileFormData.lastName) {
      nextProfileFormErrors.lastName = "Last name is required.";
    }
    const hasPrimaryInsuranceDetails = [
      profileFormData?.primaryInsuranceName,
      profileFormData?.primaryInsurancePlanType,
      profileFormData?.primaryInsuranceMemberId,
      profileFormData?.primaryInsuranceGroupNumber,
      profileFormData?.primaryInsuranceSubscriberName,
      profileFormData?.primaryInsuranceSubscriberRelationship,
      profileFormData?.primaryInsurancePhone,
      profileFormData?.primaryInsuranceClaimsPhone,
    ].some((value) => String(value || "").trim());
    const hasSecondaryInsuranceDetails = [
      profileFormData?.secondaryInsuranceName,
      profileFormData?.secondaryInsurancePlanType,
      profileFormData?.secondaryInsuranceMemberId,
      profileFormData?.secondaryInsuranceGroupNumber,
      profileFormData?.secondaryInsuranceSubscriberName,
      profileFormData?.secondaryInsuranceSubscriberRelationship,
      profileFormData?.secondaryInsurancePhone,
      profileFormData?.secondaryInsuranceClaimsPhone,
    ].some((value) => String(value || "").trim());
    if (profileFormData?.isOnMedicaid && !String(profileFormData?.medicaidClientId || "").trim() && !String(profileFormData?.providerOneNumber || "").trim()) {
      nextProfileFormErrors.medicaidClientId = "Add Medicaid Client ID or ProviderOne Number.";
      nextProfileFormErrors.providerOneNumber = "Add ProviderOne Number or Medicaid Client ID.";
    }
    if (hasPrimaryInsuranceDetails) {
      if (!String(profileFormData?.primaryInsuranceName || "").trim()) {
        nextProfileFormErrors.primaryInsuranceName = "Primary insurance carrier is required.";
      }
      if (!String(profileFormData?.primaryInsuranceMemberId || "").trim()) {
        nextProfileFormErrors.primaryInsuranceMemberId = "Primary insurance member ID is required.";
      }
      if (!String(profileFormData?.primaryInsuranceSubscriberName || "").trim()) {
        nextProfileFormErrors.primaryInsuranceSubscriberName = "Primary insurance subscriber name is required.";
      }
    }
    if (hasSecondaryInsuranceDetails) {
      if (!String(profileFormData?.secondaryInsuranceName || "").trim()) {
        nextProfileFormErrors.secondaryInsuranceName = "Secondary insurance carrier is required.";
      }
      if (!String(profileFormData?.secondaryInsuranceMemberId || "").trim()) {
        nextProfileFormErrors.secondaryInsuranceMemberId = "Secondary insurance member ID is required.";
      }
    }

    if (Object.keys(nextProfileFormErrors).length > 0) {
      setProfileFormErrors(nextProfileFormErrors);
      toast.current?.show({
        severity: "warn",
        summary: "Complete required fields",
        detail: "Fix the highlighted Profile fields before saving.",
        life: 3500,
      });
      return;
    }

    setProfileFormErrors({});
    setSavingProfileSection(true);
    try {
      await backendAPIStore.init();
      const updatedResidentProfile = await backendAPIStore.updateResident({
        ...residentProfileData,
        ...profileFormData,
        firstName: normalizedProfileFormData.firstName,
        lastName: normalizedProfileFormData.lastName,
        active: profileFormData?.active !== false,
      });
      if (residentId && selectedResidentProfileImageFile) {
        await uploadResidentProfileImage(residentId, selectedResidentProfileImageFile);
      }
      const nextResidentProfile = updatedResidentProfile || {
        ...residentProfileData,
        ...profileFormData,
        firstName: normalizedProfileFormData.firstName,
        lastName: normalizedProfileFormData.lastName,
        active: profileFormData?.active !== false,
      };
      setResidentProfileData(nextResidentProfile);
      setResidentDobDraft(String(nextResidentProfile?.dateOfBirth || "").trim());
      if (residentId) {
        await loadResidentProfileImage(residentId);
      }
      setProfileFormData({
        firstName: nextResidentProfile?.firstName || "",
        lastName: nextResidentProfile?.lastName || "",
        isOnMedicaid: nextResidentProfile?.isOnMedicaid === true,
        medicaidClientId: nextResidentProfile?.medicaidClientId || "",
        providerOneNumber: nextResidentProfile?.providerOneNumber || "",
        primaryInsuranceName: nextResidentProfile?.primaryInsuranceName || "",
        primaryInsurancePlanType: nextResidentProfile?.primaryInsurancePlanType || "",
        primaryInsuranceMemberId: nextResidentProfile?.primaryInsuranceMemberId || "",
        primaryInsuranceGroupNumber: nextResidentProfile?.primaryInsuranceGroupNumber || "",
        primaryInsuranceSubscriberName: nextResidentProfile?.primaryInsuranceSubscriberName || "",
        primaryInsuranceSubscriberRelationship: nextResidentProfile?.primaryInsuranceSubscriberRelationship || "",
        primaryInsurancePhone: nextResidentProfile?.primaryInsurancePhone || "",
        primaryInsuranceClaimsPhone: nextResidentProfile?.primaryInsuranceClaimsPhone || "",
        secondaryInsuranceName: nextResidentProfile?.secondaryInsuranceName || "",
        secondaryInsurancePlanType: nextResidentProfile?.secondaryInsurancePlanType || "",
        secondaryInsuranceMemberId: nextResidentProfile?.secondaryInsuranceMemberId || "",
        secondaryInsuranceGroupNumber: nextResidentProfile?.secondaryInsuranceGroupNumber || "",
        secondaryInsuranceSubscriberName: nextResidentProfile?.secondaryInsuranceSubscriberName || "",
        secondaryInsuranceSubscriberRelationship: nextResidentProfile?.secondaryInsuranceSubscriberRelationship || "",
        secondaryInsurancePhone: nextResidentProfile?.secondaryInsurancePhone || "",
        secondaryInsuranceClaimsPhone: nextResidentProfile?.secondaryInsuranceClaimsPhone || "",
        insuranceNotes: nextResidentProfile?.insuranceNotes || "",
        active: nextResidentProfile?.active !== false,
      });
      setIsEditingProfileSection(false);
      setUpdateFlag((previousValue) => !previousValue);
      toast.current?.show({
        severity: "success",
        summary: "Profile updated",
        detail: "Resident profile details were saved.",
        life: 2500,
      });
      window.location.reload();
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save the resident profile.",
        life: 4000,
      });
    } finally {
      setSavingProfileSection(false);
    }
  };

  const renderProfileDialogError = (fieldName: string) =>
    profileFormErrors[fieldName] ? (
      <small style={{ color: "#b91c1c", display: "block", marginTop: "0.35rem" }}>
        {profileFormErrors[fieldName]}
      </small>
    ) : null;

  const updateProfileFormField = (fieldName: string, value: any) => {
    setProfileFormData((prev: any) => ({ ...prev, [fieldName]: value }));
    setProfileFormErrors((prev) => {
      if (!prev[fieldName]) {
        return prev;
      }

      const nextErrors = { ...prev };
      delete nextErrors[fieldName];
      return nextErrors;
    });
  };

  const showHelpMessage = (message: string) => {
    setHelpMessage(message);
    setShowHelpDialog(true);
  }

  const prevCallback = () => {
    if (stepperRef.current) {
      stepperRef.current.prevCallback();
    }
  };

  const nextCallback = () => {
    if (stepperRef.current) {
      stepperRef.current.nextCallback();
    }
  };

  const openResidentNcpImporter = () => {
    navigate(getResidentCarePlanPath(homeId!, residentId!), {
      state: { openAssessmentToNcp: true },
    });
  };

  const onCompareAssessment = async (useExistingCredit: boolean = false) => {
    if (!doesNCPExtractedTextExist || !doesNextNCPExtractedTextExist) {
      toast.current?.show({ severity: 'info', summary: 'Comparison not available', detail: 'Comparison is only available when both current and next assessment data exist.', life: 5000 });
      return;
    }

    setShowCompareAssessmentPopUp(true);

    try {
      await backendAPIStore.init();

      if (!doesCompareAssessmentSummaryExist) {
        await backendAPIStore.compareAssessmentSummary(residentId!, useExistingCredit);
      } else {
        const assessmentSummaryResponse = await backendAPIStore.retrieveCompareAssessmentSummary(residentId!);
        const compareSummaryResponse = await fetch(assessmentSummaryResponse.preSignedUrl!);
        const compareSummaryData = await compareSummaryResponse.json();
        setCompareAssessmentSummaryData(compareSummaryData.response);
        return;
      }

      const PROCESSING_STATUS = 'processing';
      const COMPLETED_STATUS = 'completed';
      const FAILED_STATUS = 'failed';
      const SUCCESS_STATUS = 'success';
      let statusCheck = PROCESSING_STATUS;
      do {
        await new Promise(resolve => setTimeout(resolve, 5000));
        const response = await backendAPIStore.retrieveCompareAssessmentSummary(residentId!);
        if (response.status === COMPLETED_STATUS) {
          const summaryResponse = await fetch(response.preSignedUrl!);
          const summaryData = await summaryResponse.json();
          if (summaryData && summaryData.status === SUCCESS_STATUS) {
            setCompareAssessmentSummaryData(summaryData.response);
            statusCheck = SUCCESS_STATUS;
            setDoesCompareAssessmentSummaryExist(true);
          } else {
            toast.current?.show({ severity: 'error', summary: 'Failed', detail: 'Compare assessment summaries generation failed. Please try again.', life: 5000 });
            statusCheck = FAILED_STATUS;
          }
        } else {
          setProcessingCompareAssessmentSummary(true);
        }
      } while (statusCheck === PROCESSING_STATUS);
    } catch (error: any) {
      if (error?.code === "SUBSCRIPTION_LIMIT_REACHED" || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
        setShowCompareAssessmentPopUp(false);
        promptForFeatureCredits("compare", error, () => {
          setShowCompareAssessmentPopUp(false);
        });
      } else {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 5000 });
      }
    }
  }

  const getRequiredLatestVersion = async (
    binderDocumentId: string,
    missingDetail: string,
  ) => {
    const latestVersion = await getLatestBinderDocumentVersion(backendAPIStore, binderDocumentId);
    if (!latestVersion?.s3Key) {
      throw new Error(missingDetail);
    }
    return latestVersion;
  };

  const pollForFeatureJobResult = async (jobId: string, feature: FeatureResultType) => {
    const maxAttempts = feature === "carePlanCompare" ? 120 : 60;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, 5000));
      const jobDetails = await backendAPIStore.getJobDetails(jobId);
      if (feature === "summary") {
        setSummaryJobStatus(jobDetails?.jobStatus || "");
        setSummaryJobSubStatus(jobDetails?.jobSubStatus || "");
      } else if (feature === "carePlanCompare") {
        setCarePlanCompareJobStatus(jobDetails?.jobStatus || "");
        setCarePlanCompareJobSubStatus(jobDetails?.jobSubStatus || "");
      } else {
        setCompareJobStatus(jobDetails?.jobStatus || "");
        setCompareJobSubStatus(jobDetails?.jobSubStatus || "");
      }
      if (jobDetails?.jobStatus === JOB_STATUS.FAILED) {
        throw new Error("Assessment processing failed. Please try again.");
      }
      if (jobDetails?.jobStatus === JOB_STATUS.COMPLETED) {
        const result = feature === "summary"
          ? await backendAPIStore.getStandaloneAssessmentSummaryResult(jobId)
          : feature === "carePlanCompare"
            ? await backendAPIStore.getStandaloneCarePlanCompareResult(jobId)
            : await backendAPIStore.getStandaloneAssessmentCompareResult(jobId);
        if (result?.status === "completed" && result?.preSignedUrl) {
          const response = await fetch(result.preSignedUrl);
          const resultData = await response.json();
          if (resultData?.status === "success") {
            return resultData.response;
          }
          throw new Error("AI generation finished, but the result could not be read.");
        }
      }
    }
    throw new Error(feature === "carePlanCompare"
      ? "Care plan comparison is taking longer than expected. You can reopen the latest result in a few minutes."
      : "The feature took too long to complete. Please try again.");
  };

  const formatElapsedTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  const downloadHtmlContent = (title: string, htmlContent: string, fileName: string) => {
    const documentHtml = buildWordFriendlyHtmlDocument(title, htmlContent);
    const blob = new Blob([documentHtml], { type: "application/msword" });
    const blobUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(blobUrl);
  };

  const escapeHtml = (value: any) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const exportBehaviorTimeline = () => {
    const residentName = residentProfileData
      ? `${residentProfileData.firstName || ""} ${residentProfileData.lastName || ""}`.trim() || "Resident"
      : "Resident";
    const residentProfileRows = [
      ["Resident", residentName],
      ["Date of Birth", residentProfileData?.dateOfBirth || "Not entered"],
      ["Age", residentProfileData?.dateOfBirth ? formatAgeInYearsOnly(calculateDeepAge(residentProfileData.dateOfBirth)) : "Not entered"],
      ["Gender", residentProfileData?.gender ? capitalizeFirstLetter(residentProfileData.gender) : "Not entered"],
      ["Status", residentProfileData?.status ? getStatusNameForDisplay(residentProfileData.status) : "Not entered"],
    ];

    const profileTableHtml = `
      <table style="width:100%; border-collapse:collapse; margin-bottom:20px;">
        <tbody>
          ${residentProfileRows.map(([label, value]) => `
            <tr>
              <td style="width:180px; border:1px solid #d9e2ec; padding:8px; background:#f8fafc; font-weight:700;">${escapeHtml(label)}</td>
              <td style="border:1px solid #d9e2ec; padding:8px;">${escapeHtml(value)}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    `;

    const exportRows = visibleBehaviorTimelineEntries.flatMap(({ entry, behaviorChangeCards }: any) =>
      behaviorChangeCards.map((changeCard: any) => ({
        date: formatBehaviorTimestamp(entry.sourceCarePlanUpdatedAt || entry.recordedAt),
        changeType: entry.eventType === "manual_save"
          ? "Manual"
          : entry.eventType === "ncp_regenerated"
            ? "Care Plan"
            : "Care Plan",
        behavior: changeCard.behaviorTitle,
        description: changeCard.nextRow?.residentDetails ? String(changeCard.nextRow.residentDetails) : "",
        instructions: changeCard.nextRow?.caregiverInstructions ? String(changeCard.nextRow.caregiverInstructions) : "",
      }))
    );

    const filterSummary = [
      `Time range: ${behaviorTimelineFilterOptions.find((option) => option.value === behaviorTimelineFilter)?.label || "All time"}`,
      `Behavior: ${behaviorTimelineBehaviorOptions.find((option) => option.value === behaviorTimelineBehaviorFilter)?.label || "All behaviors"}`,
      `Exported: ${formatBehaviorTimestamp(new Date().toISOString())}`,
    ];

    const timelineTableHtml = exportRows.length > 0 ? `
      <table style="width:100%; border-collapse:collapse;">
        <thead>
          <tr style="background:#f8fafc;">
            ${["Date", "Change Type", "Behavior", "Description", "Instructions"]
        .map((heading) => `<th style="border:1px solid #d9e2ec; padding:8px; text-align:left; font-weight:700;">${escapeHtml(heading)}</th>`)
        .join("")}
          </tr>
        </thead>
        <tbody>
          ${exportRows.map((row) => `
            <tr>
              <td style="border:1px solid #d9e2ec; padding:8px; vertical-align:top;">${escapeHtml(row.date)}</td>
              <td style="border:1px solid #d9e2ec; padding:8px; vertical-align:top;">${escapeHtml(row.changeType)}</td>
              <td style="border:1px solid #d9e2ec; padding:8px; vertical-align:top; font-weight:700;">${escapeHtml(row.behavior)}</td>
              <td style="border:1px solid #d9e2ec; padding:8px; vertical-align:top; white-space:pre-wrap;">${escapeHtml(row.description || "")}</td>
              <td style="border:1px solid #d9e2ec; padding:8px; vertical-align:top; white-space:pre-wrap;">${escapeHtml(row.instructions || "")}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    ` : `<p>No behavior timeline entries matched the selected filters.</p>`;

    const htmlContent = `
      <div>
        <h2 style="margin:0 0 16px; color:#0f172a;">Resident Behavior Timeline Export</h2>
        ${profileTableHtml}
        <div style="margin-bottom:16px; color:#475569;">
          ${filterSummary.map((line) => `<div>${escapeHtml(line)}</div>`).join("")}
        </div>
        ${timelineTableHtml}
      </div>
    `;

    downloadHtmlContent(
      "Resident Behavior Timeline Export",
      htmlContent,
      `resident-behavior-timeline-${residentId || "resident"}.doc`
    );
  };

  const exportBehaviorSnapshot = () => {
    const residentName = residentProfileData
      ? `${residentProfileData.firstName || ""} ${residentProfileData.lastName || ""}`.trim() || "Resident"
      : "Resident";
    const residentProfileRows = [
      ["Resident", residentName],
      ["Date of Birth", residentProfileData?.dateOfBirth || "Not entered"],
      ["Age", residentProfileData?.dateOfBirth ? formatAgeInYearsOnly(calculateDeepAge(residentProfileData.dateOfBirth)) : "Not entered"],
      ["Gender", residentProfileData?.gender ? capitalizeFirstLetter(residentProfileData.gender) : "Not entered"],
      ["Status", residentProfileData?.status ? getStatusNameForDisplay(residentProfileData.status) : "Not entered"],
    ];

    const profileTableHtml = `
      <table style="width:100%; border-collapse:collapse; margin-bottom:20px;">
        <tbody>
          ${residentProfileRows.map(([label, value]) => `
            <tr>
              <td style="width:180px; border:1px solid #d9e2ec; padding:8px; background:#f8fafc; font-weight:700;">${escapeHtml(label)}</td>
              <td style="border:1px solid #d9e2ec; padding:8px;">${escapeHtml(value)}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    `;

    const snapshotRows = currentBehaviorRows.map((row: any) => ({
      behavior: row.behaviorTitle,
      status: row.present ? "Active" : "Not documented",
      description: row.residentDetails || "",
      instructions: row.caregiverInstructions || "",
    }));

    const snapshotTableHtml = snapshotRows.length > 0 ? `
      <table style="width:100%; border-collapse:collapse;">
        <thead>
          <tr style="background:#f8fafc;">
            ${["Behavior", "Status", "Description", "Instructions"]
        .map((heading) => `<th style="border:1px solid #d9e2ec; padding:8px; text-align:left; font-weight:700;">${escapeHtml(heading)}</th>`)
        .join("")}
          </tr>
        </thead>
        <tbody>
          ${snapshotRows.map((row) => `
            <tr>
              <td style="border:1px solid #d9e2ec; padding:8px; vertical-align:top; font-weight:700;">${escapeHtml(row.behavior)}</td>
              <td style="border:1px solid #d9e2ec; padding:8px; vertical-align:top;">${escapeHtml(row.status)}</td>
              <td style="border:1px solid #d9e2ec; padding:8px; vertical-align:top; white-space:pre-wrap;">${escapeHtml(row.description)}</td>
              <td style="border:1px solid #d9e2ec; padding:8px; vertical-align:top; white-space:pre-wrap;">${escapeHtml(row.instructions)}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    ` : `<p>No current behavior snapshot data is available.</p>`;

    const htmlContent = `
      <div>
        <h2 style="margin:0 0 16px; color:#0f172a;">Resident Behavior Snapshot Export</h2>
        ${profileTableHtml}
        <div style="margin-bottom:16px; color:#475569;">
          <div>Exported: ${escapeHtml(formatBehaviorTimestamp(new Date().toISOString()))}</div>
        </div>
        ${snapshotTableHtml}
      </div>
    `;

    downloadHtmlContent(
      "Resident Behavior Snapshot Export",
      htmlContent,
      `resident-behavior-snapshot-${residentId || "resident"}.doc`
    );
  };

  useEffect(() => {
    if (!processingAssessmentSummary || !summaryProcessingStartedAt) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setSummaryElapsedSeconds(Math.max(0, Math.floor((Date.now() - summaryProcessingStartedAt) / 1000)));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [processingAssessmentSummary, summaryProcessingStartedAt]);

  useEffect(() => {
    if (!processingCompareAssessmentSummary || !compareProcessingStartedAt) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setCompareElapsedSeconds(Math.max(0, Math.floor((Date.now() - compareProcessingStartedAt) / 1000)));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [processingCompareAssessmentSummary, compareProcessingStartedAt]);

  useEffect(() => {
    if (!processingStandaloneCarePlanCompare || !carePlanCompareProcessingStartedAt) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setCarePlanCompareElapsedSeconds(Math.max(0, Math.floor((Date.now() - carePlanCompareProcessingStartedAt) / 1000)));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [processingStandaloneCarePlanCompare, carePlanCompareProcessingStartedAt]);

  useEffect(() => {
    if (!processingMedicationCompare || !medicationCompareProcessingStartedAt) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setMedicationCompareElapsedSeconds(Math.max(0, Math.floor((Date.now() - medicationCompareProcessingStartedAt) / 1000)));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [processingMedicationCompare, medicationCompareProcessingStartedAt]);

  useEffect(() => {
    if (!medicationCompareJobId || !processingMedicationCompare) {
      return;
    }

    let cancelled = false;

    const pollMedicationCompareJob = async () => {
      for (let attempt = 0; attempt < 90; attempt += 1) {
        if (cancelled) {
          return;
        }

        try {
          const jobDetails = await backendAPIStore.getJobDetails(medicationCompareJobId);
          const nextStatus = String(jobDetails?.jobStatus || "").trim();
          const nextSubStatus = String(jobDetails?.jobSubStatus || "").trim();
          const normalizedStatus = nextStatus.toUpperCase();

          if (!cancelled) {
            setMedicationCompareJobStatus(nextStatus);
            setMedicationCompareJobSubStatus(nextSubStatus);
          }

          if (normalizedStatus === "COMPLETED") {
            const warnings = Array.isArray(jobDetails?.marSheetPreviewWarnings) ? jobDetails.marSheetPreviewWarnings : [];
            const hydratedResult = await hydrateMedicationCompareResultFromJob(jobDetails);

            if (!cancelled) {
              setMedicationCompareWarnings(warnings);
              setMedicationCompareResultData(hydratedResult.comparisonResult);
              setMedicationCompareReportHtml(hydratedResult.reportHtml);
              setMedicationCompareFileName(String(jobDetails?.sourceFileName || medicationCompareFileName || "").trim());
              setMedicationCompareCompletedJobId(String(jobDetails?.jobId || medicationCompareJobId || "").trim());
              setLatestMedicationCompareJobId(String(jobDetails?.jobId || medicationCompareJobId || "").trim());
              setProcessingMedicationCompare(false);
              setMedicationCompareJobId("");
            }
            return;
          }

          if (normalizedStatus === "FAILED") {
            if (!cancelled) {
              setProcessingMedicationCompare(false);
              setMedicationCompareJobId("");
              toast.current?.show({
                severity: "error",
                summary: "Review failed",
                detail: String(jobDetails?.errorMessage || "Unable to compare the MAR sheet to the care plan."),
                life: 5000,
              });
            }
            return;
          }
        } catch (error: any) {
          if (!cancelled) {
            setProcessingMedicationCompare(false);
            setMedicationCompareJobId("");
            toast.current?.show({
              severity: "error",
              summary: getErrorTitle(error),
              detail: error?.message || "Unable to check the medication comparison progress.",
              life: 4500,
            });
          }
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, attempt < 10 ? 2000 : 3000));
      }

      if (!cancelled) {
        setProcessingMedicationCompare(false);
        setMedicationCompareJobId("");
        toast.current?.show({
          severity: "warn",
          summary: "Still working",
          detail: "This medication review is taking longer than expected. Please try again in a moment.",
          life: 5000,
        });
      }
    };

    void pollMedicationCompareJob();

    return () => {
      cancelled = true;
    };
  }, [
    backendAPIStore,
    medicationCompareFileName,
    medicationCompareJobId,
    processingMedicationCompare,
  ]);

  const getFeatureDialogLabel = (featureType: FeatureAccessDialogType) => {
    switch (featureType) {
      case "assessmentDelegation":
        return "Assessment & Delegation";
      case "summary":
        return "Summary";
      case "carePlanCompare":
        return "Care Plan Compare";
      default:
        return "Compare";
    }
  };

  const promptForFeatureCredits = (
    featureType: FeatureAccessDialogType,
    error: any,
    closeFeatureDialog?: () => void,
  ) => {
    const option = featureType === "summary"
      ? {
        label: "Summary",
        limit: error?.details?.usageLimit || 5,
      }
      : featureType === "carePlanCompare"
        ? {
          label: "Care Plan Compare",
          limit: error?.details?.usageLimit || 2,
        }
        : {
          label: "Compare",
          limit: error?.details?.usageLimit || 5,
        };
    const currentPeriodEnd = error?.details?.currentPeriodEnd ? new Date(error.details.currentPeriodEnd) : null;
    const isSubscribedQuotaReached = !!error?.details?.usageLimit;

    setFeatureAccessDialogType(featureType);
    setFeatureAccessDialogAvailableCredits(0);
    setFeatureAccessDialogForSubscribedUser(isSubscribedQuotaReached);
    setFeatureAccessDialogCloseAction(() => closeFeatureDialog || null);

    if (isSubscribedQuotaReached) {
      setFeatureAccessDialogMessage(
        `You have used your ${option.limit} included ${option.label.toLowerCase()} tries for this billing period. You will get ${option.limit} more tries starting ${currentPeriodEnd ? currentPeriodEnd.toLocaleDateString() : "on your next billing date"}.`
      );
    } else {
      setFeatureAccessDialogMessage(
        `${option.label} is now included through subscription only. Subscribe to continue using this feature.`
      );
    }

    setShowFeatureAccessDialog(true);
  };

  const handleAssessmentDelegationAccess = (targetPath: string) => {
    if (isAdminUser || !isProviderUser || isUserSubscribed) {
      navigate(targetPath);
      return;
    }

    setFeatureAccessDialogType("assessmentDelegation");
    setFeatureAccessDialogAvailableCredits(0);
    setFeatureAccessDialogForSubscribedUser(false);
    setFeatureAccessDialogCloseAction(null);
    setFeatureAccessDialogMessage(
      "An active subscription is required to open the Assessment and Delegation tools. Subscribe to continue."
    );
    setShowFeatureAccessDialog(true);
  };

  const openAssessmentSummaryVersion = useCallback((targetFormId?: string) => {
    const normalizedFormId = String(targetFormId || "").trim();
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", "assessment");
    if (normalizedFormId) {
      nextParams.set("formId", normalizedFormId);
    } else {
      nextParams.delete("formId");
    }
    setRequestedWorkspaceTabs((previousTabs) => (
      previousTabs.includes("assessment") ? previousTabs : [...previousTabs, "assessment"]
    ));
    setSearchParams(nextParams, { replace: true });
    setShowAssessmentVersionsDialog(false);
  }, [searchParams, setSearchParams]);

  const runStandaloneAssessmentSummary = async (useExistingCredit: boolean = false) => {
    if (!selectedSummaryAssessmentDocumentId) {
      toast.current?.show({ severity: 'error', summary: 'Assessment required', detail: 'Choose an assessment from the resident binder first.', life: 4000 });
      return;
    }

    setProcessingStandaloneSummary(true);
    setProcessingAssessmentSummary(true);
    setSummaryProcessingStartedAt(Date.now());
    setSummaryElapsedSeconds(0);
    setSummaryJobStatus("QUEUED");
    setSummaryJobSubStatus("Waiting to start");
    setAssessmentSummaryData(null);
    setShowStandaloneSummaryDialog(false);
    setShowAssessmentSummary(true);
    try {
      await backendAPIStore.init();
      const latestVersion = await getRequiredLatestVersion(
        selectedSummaryAssessmentDocumentId,
        "The selected binder assessment does not have a latest version yet.",
      );
      const jobResponse = await backendAPIStore.createStandaloneAssessmentSummaryJob({
        referenceId: residentId,
        residentId,
        homeId,
        objectKey: latestVersion.s3Key,
        useExistingCredit,
      });
      setLatestStandaloneSummaryJobId(jobResponse.jobId);
      setSummaryJobStatus("IN_PROGRESS");
      setSummaryJobSubStatus("Processing assessment");
      const html = await pollForFeatureJobResult(jobResponse.jobId, "summary");
      setAssessmentSummaryData(html);
      setShowStandaloneSummaryDialog(false);
    } catch (error: any) {
      if (error?.code === "SUBSCRIPTION_LIMIT_REACHED" || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
        promptForFeatureCredits("summary", error, () => {
          setShowStandaloneSummaryDialog(false);
          setShowAssessmentSummary(false);
        });
      } else {
        toast.current?.show(getToastMessageForSubscriptionLimitReached(error));
      }
    } finally {
      setProcessingStandaloneSummary(false);
      setProcessingAssessmentSummary(false);
      setSummaryProcessingStartedAt(null);
    }
  };

  const runStandaloneAssessmentCompare = async (useExistingCredit: boolean = false) => {
    if (!selectedCompareFirstAssessmentDocumentId || !selectedCompareSecondAssessmentDocumentId) {
      toast.current?.show({ severity: 'error', summary: 'Assessments required', detail: 'Choose both assessment documents from the resident binder first.', life: 4000 });
      return;
    }

    setProcessingStandaloneCompare(true);
    setProcessingCompareAssessmentSummary(true);
    setCompareProcessingStartedAt(Date.now());
    setCompareElapsedSeconds(0);
    setCompareJobStatus("QUEUED");
    setCompareJobSubStatus("Waiting to start");
    setCompareAssessmentSummaryData(null);
    setShowStandaloneCompareDialog(false);
    setShowCompareAssessmentPopUp(true);
    try {
      await backendAPIStore.init();
      const firstVersion = await getRequiredLatestVersion(
        selectedCompareFirstAssessmentDocumentId,
        "The previous binder assessment does not have a latest version yet.",
      );
      const secondVersion = await getRequiredLatestVersion(
        selectedCompareSecondAssessmentDocumentId,
        "The latest binder assessment does not have a latest version yet.",
      );
      const jobResponse = await backendAPIStore.createStandaloneAssessmentCompareJob({
        referenceId: residentId,
        residentId,
        homeId,
        firstObjectKey: firstVersion.s3Key,
        secondObjectKey: secondVersion.s3Key,
        useExistingCredit,
      });
      setLatestStandaloneCompareJobId(jobResponse.jobId);
      setCompareJobStatus("IN_PROGRESS");
      setCompareJobSubStatus("Processing assessments");
      const html = await pollForFeatureJobResult(jobResponse.jobId, "compare");
      setCompareAssessmentSummaryData(html);
      setShowStandaloneCompareDialog(false);
    } catch (error: any) {
      if (error?.code === "SUBSCRIPTION_LIMIT_REACHED" || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
        promptForFeatureCredits("compare", error, () => {
          setShowStandaloneCompareDialog(false);
          setShowCompareAssessmentPopUp(false);
        });
      } else {
        toast.current?.show(getToastMessageForSubscriptionLimitReached(error));
      }
    } finally {
      setProcessingStandaloneCompare(false);
      setProcessingCompareAssessmentSummary(false);
      setCompareProcessingStartedAt(null);
    }
  };

  const runStandaloneCarePlanCompare = async (useExistingCredit: boolean = false) => {
    if (!selectedCarePlanCompareFirstDocumentId || !selectedCarePlanCompareSecondDocumentId) {
      toast.current?.show({ severity: 'error', summary: 'Care plans required', detail: 'Choose both care plan documents from the resident binder first.', life: 4000 });
      return;
    }

    setProcessingStandaloneCarePlanCompare(true);
    setShowCarePlanCompareResultDialog(true);
    setCarePlanCompareProcessingStartedAt(Date.now());
    setCarePlanCompareElapsedSeconds(0);
    setCarePlanCompareJobStatus("QUEUED");
    setCarePlanCompareJobSubStatus("Waiting to start");
    setCarePlanCompareSummaryData(null);
    setShowStandaloneCarePlanCompareDialog(false);
    try {
      await backendAPIStore.init();
      const firstVersion = await getRequiredLatestVersion(
        selectedCarePlanCompareFirstDocumentId,
        "The previous binder care plan does not have a latest version yet.",
      );
      const secondVersion = await getRequiredLatestVersion(
        selectedCarePlanCompareSecondDocumentId,
        "The latest binder care plan does not have a latest version yet.",
      );
      const jobResponse = await backendAPIStore.createStandaloneCarePlanCompareJob({
        referenceId: residentId,
        residentId,
        homeId,
        firstObjectKey: firstVersion.s3Key,
        secondObjectKey: secondVersion.s3Key,
        useExistingCredit,
      });
      setLatestStandaloneCarePlanCompareJobId(jobResponse.jobId);
      setCarePlanCompareJobStatus("IN_PROGRESS");
      setCarePlanCompareJobSubStatus("Processing care plans");
      const html = await pollForFeatureJobResult(jobResponse.jobId, "carePlanCompare");
      setCarePlanCompareSummaryData(html);
      setShowStandaloneCarePlanCompareDialog(false);
    } catch (error: any) {
      if (error?.code === "SUBSCRIPTION_LIMIT_REACHED" || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
        promptForFeatureCredits("carePlanCompare", error, () => {
          setShowStandaloneCarePlanCompareDialog(false);
          setShowCarePlanCompareResultDialog(false);
        });
      } else {
        toast.current?.show(getToastMessageForSubscriptionLimitReached(error));
      }
    } finally {
      setProcessingStandaloneCarePlanCompare(false);
      setCarePlanCompareProcessingStartedAt(null);
    }
  };

  const openLatestStandaloneSummaryResult = async () => {
    if (!latestStandaloneSummaryJobId) {
      return;
    }
    setShowAssessmentSummary(true);
    setProcessingAssessmentSummary(true);
    setSummaryProcessingStartedAt(Date.now());
    setSummaryElapsedSeconds(0);
    setSummaryJobStatus("IN_PROGRESS");
    setSummaryJobSubStatus("Loading latest result");
    try {
      const result = await backendAPIStore.getStandaloneAssessmentSummaryResult(latestStandaloneSummaryJobId);
      if (result?.status === "completed" && result?.preSignedUrl) {
        const summaryResponse = await fetch(result.preSignedUrl);
        const summaryData = await summaryResponse.json();
        setAssessmentSummaryData(summaryData.response);
      } else {
        toast.current?.show({ severity: 'info', summary: 'Still processing', detail: 'The last summary result is still processing.', life: 4000 });
      }
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 5000 });
    } finally {
      setProcessingAssessmentSummary(false);
      setSummaryProcessingStartedAt(null);
    }
  };

  const openLatestStandaloneCompareResult = async () => {
    if (!latestStandaloneCompareJobId) {
      return;
    }
    setShowCompareAssessmentPopUp(true);
    setProcessingCompareAssessmentSummary(true);
    setCompareProcessingStartedAt(Date.now());
    setCompareElapsedSeconds(0);
    setCompareJobStatus("IN_PROGRESS");
    setCompareJobSubStatus("Loading latest result");
    try {
      const result = await backendAPIStore.getStandaloneAssessmentCompareResult(latestStandaloneCompareJobId);
      if (result?.status === "completed" && result?.preSignedUrl) {
        const compareResponse = await fetch(result.preSignedUrl);
        const compareData = await compareResponse.json();
        setCompareAssessmentSummaryData(compareData.response);
      } else {
        toast.current?.show({ severity: 'info', summary: 'Still processing', detail: 'The last comparison result is still processing.', life: 4000 });
      }
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 5000 });
    } finally {
      setProcessingCompareAssessmentSummary(false);
      setCompareProcessingStartedAt(null);
    }
  };

  const openLatestStandaloneCarePlanCompareResult = async () => {
    if (!latestStandaloneCarePlanCompareJobId) {
      return;
    }
    setShowCarePlanCompareResultDialog(true);
    setProcessingStandaloneCarePlanCompare(true);
    setCarePlanCompareProcessingStartedAt(Date.now());
    setCarePlanCompareElapsedSeconds(0);
    setCarePlanCompareJobStatus("IN_PROGRESS");
    setCarePlanCompareJobSubStatus("Loading latest result");
    try {
      const result = await backendAPIStore.getStandaloneCarePlanCompareResult(latestStandaloneCarePlanCompareJobId);
      if (result?.status === "completed" && result?.preSignedUrl) {
        const compareResponse = await fetch(result.preSignedUrl);
        const compareData = await compareResponse.json();
        setCarePlanCompareSummaryData(compareData.response);
      } else {
        toast.current?.show({ severity: 'info', summary: 'Still processing', detail: 'The last care plan comparison result is still processing.', life: 4000 });
      }
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 5000 });
    } finally {
      setProcessingStandaloneCarePlanCompare(false);
      setCarePlanCompareProcessingStartedAt(null);
    }
  };

  const openLatestMedicationCompareResult = async () => {
    if (!effectiveLatestMedicationCompareJobId) {
      return;
    }

    setShowMedicationCompareDialog(true);
    setProcessingMedicationCompare(true);
    setMedicationCompareProcessingStartedAt(Date.now());
    setMedicationCompareElapsedSeconds(0);
    setMedicationCompareJobStatus("IN_PROGRESS");
    setMedicationCompareJobSubStatus("Loading latest result");
    setMedicationCompareWarnings([]);
    setMedicationCompareResultData(null);
    setMedicationCompareReportHtml("");

    try {
      const jobDetails = await backendAPIStore.getJobDetails(effectiveLatestMedicationCompareJobId);
      const normalizedStatus = String(jobDetails?.jobStatus || "").trim().toUpperCase();
      if (normalizedStatus === "COMPLETED") {
        const warnings = Array.isArray(jobDetails?.marSheetPreviewWarnings) ? jobDetails.marSheetPreviewWarnings : [];
        const hydratedResult = await hydrateMedicationCompareResultFromJob(jobDetails);

        setMedicationCompareWarnings(warnings);
        setMedicationCompareResultData(hydratedResult.comparisonResult);
        setMedicationCompareReportHtml(hydratedResult.reportHtml);
        setMedicationCompareFileName(String(jobDetails?.sourceFileName || "").trim());
        setMedicationCompareCompletedJobId(String(jobDetails?.jobId || effectiveLatestMedicationCompareJobId).trim());
        setLatestMedicationCompareJobId(String(jobDetails?.jobId || effectiveLatestMedicationCompareJobId).trim());
      } else {
        toast.current?.show({ severity: "info", summary: "Still processing", detail: "The last medication comparison result is still processing.", life: 4000 });
      }
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load the last medication comparison result.", life: 5000 });
    } finally {
      setProcessingMedicationCompare(false);
      setMedicationCompareProcessingStartedAt(null);
    }
  };

  const assessmentBinderFolder = getResidentBinderFolder(RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT);

  const hydrateMedicationCompareResultFromJob = async (jobDetails: any) => {
    const comparisonPayloadKey = String(jobDetails?.comparisonResultS3ObjectKey || "").trim();
    if (comparisonPayloadKey && residentId) {
      const payload = await backendAPIStore.getResidentScopedJsonFromObjectKey(
        residentId,
        comparisonPayloadKey,
        `medication-compare-${String(jobDetails?.jobId || "result").trim() || "result"}.json`,
      );
      return {
        comparisonResult: payload?.comparison || null,
        reportHtml: String(payload?.reportHtml || ""),
      };
    }

    return {
      comparisonResult: jobDetails?.medicationComparison || jobDetails?.carePlanMedicationComparison || jobDetails?.comparisonResultSummary || null,
      reportHtml: String(jobDetails?.medicationComparisonReportHtml || jobDetails?.carePlanMedicationComparisonReportHtml || ""),
    };
  };

  const isBehaviorSnapshotEmpty = (snapshot: any) => !Array.isArray(snapshot?.behaviors) || snapshot.behaviors.length === 0;
  const isCarePlanTrackingSnapshotEmpty = (snapshot: any) =>
    !Array.isArray(snapshot?.sectionKeys) || snapshot.sectionKeys.length === 0;

  const importResidentBehaviorInsights = async (importMode: "auto" | "manual" = "manual") => {
    if (!residentId) {
      return false;
    }

    setImportingBehaviorInsights(true);
    try {
      await backendAPIStore.init();
      await backendAPIStore.importResidentBehaviorTracking(residentId, importMode);
      return true;
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Unable to import behavior tracking",
        detail: error?.message || "Please try again.",
      });
      return false;
    } finally {
      setImportingBehaviorInsights(false);
    }
  };

  const importResidentCarePlanTracking = async (importMode: "auto" | "manual" = "manual") => {
    if (!residentId) {
      return false;
    }

    setImportingCarePlanTracking(true);
    try {
      await backendAPIStore.init();
      await backendAPIStore.importResidentCarePlanTracking(residentId, importMode);
      return true;
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Unable to import care plan tracking",
        detail: error?.message || "Please try again.",
      });
      return false;
    } finally {
      setImportingCarePlanTracking(false);
    }
  };

  const loadResidentBehaviorInsights = async (autoImportIfEmpty: boolean = false) => {
    if (!residentId) {
      return;
    }

    setLoadingBehaviorInsights(true);
    try {
      await backendAPIStore.init();
      const [snapshot, timeline] = await Promise.all([
        backendAPIStore.getResidentBehaviorSnapshot(residentId),
        backendAPIStore.getResidentBehaviorTimeline(residentId, 200),
      ]);
      setResidentBehaviorSnapshot(snapshot);
      setResidentBehaviorTimeline(Array.isArray(timeline) ? timeline : []);

      if (
        autoImportIfEmpty
        && isBehaviorSnapshotEmpty(snapshot)
        && !behaviorInsightsAutoImportedRef.current
      ) {
        behaviorInsightsAutoImportedRef.current = true;
        const importSucceeded = await importResidentBehaviorInsights("auto");
        if (importSucceeded) {
          await loadResidentBehaviorInsights(false);
        }
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Unable to load behavior insights",
        detail: error?.message || "Please try again.",
      });
    } finally {
      setLoadingBehaviorInsights(false);
    }
  };

  const openBehaviorInsightsDialog = async () => {
    behaviorInsightsAutoImportedRef.current = false;
    setShowBehaviorInsightsDialog(true);
    await loadResidentBehaviorInsights(true);
  };

  const loadResidentBehaviorLogEntries = useCallback(async (options?: { page?: number; nextToken?: string; resetPagination?: boolean; suppressErrorToast?: boolean }) => {
    if (!residentId) {
      return;
    }

    const targetPage = options?.resetPagination ? 1 : Math.max(1, options?.page || residentBehaviorLogPageRef.current);
    const targetToken = options?.resetPagination ? "" : (options?.nextToken ?? residentBehaviorLogPageTokensRef.current[targetPage - 1] ?? "");

    setLoadingBehaviorLogEntries(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.getResidentBehaviorLogEntriesPage(residentId, {
        limit: 10,
        nextToken: targetToken,
      });
      setResidentBehaviorLogEntries(Array.isArray(response?.items) ? response.items : []);
      setResidentBehaviorLogPage(targetPage);
      residentBehaviorLogPageRef.current = targetPage;
      setResidentBehaviorLogNextToken(String(response?.nextToken || ""));
      setResidentBehaviorLogPageTokens((previousState) => {
        if (options?.resetPagination) {
          residentBehaviorLogPageTokensRef.current = [""];
          return [""];
        }
        const nextState = previousState.slice(0, targetPage);
        nextState[targetPage - 1] = targetToken;
        residentBehaviorLogPageTokensRef.current = nextState;
        return nextState;
      });
    } catch (error: any) {
      if (!options?.suppressErrorToast) {
        toast.current?.show({
          severity: "error",
          summary: "Unable to load daily behavior log",
          detail: error?.message || "Please try again.",
        });
      }
    } finally {
      setLoadingBehaviorLogEntries(false);
    }
  }, [backendAPIStore, residentId]);

  const loadResidentActivityTrackerSnapshots = useCallback(async () => {
    if (!residentId) {
      return;
    }

    try {
      await backendAPIStore.init();
      const [incidentResponse, dailyNotesResponse] = await Promise.all([
        backendAPIStore.getResidentIncidentReportsPage(residentId, { limit: 1 }),
        backendAPIStore.getResidentDailyNotesPage(residentId, { limit: 1 }),
      ]);
      setLatestIncidentReportEntry(Array.isArray(incidentResponse?.items) ? incidentResponse.items[0] || null : null);
      setLatestDailyNoteEntry(Array.isArray(dailyNotesResponse?.items) ? dailyNotesResponse.items[0] || null : null);
    } catch (error) {
      console.error("Error loading resident activity tracker snapshots", error);
    }
  }, [backendAPIStore, residentId]);

  useEffect(() => {
    if (!residentId || !isProviderUser || !hasResolvedSubscriptionStatus || !isUserSubscribed) {
      return;
    }

    void loadResidentBehaviorLogEntries({ suppressErrorToast: true });
    void loadResidentActivityTrackerSnapshots();
  }, [residentId, isProviderUser, hasResolvedSubscriptionStatus, isUserSubscribed, loadResidentBehaviorLogEntries, loadResidentActivityTrackerSnapshots]);

  useEffect(() => {
    const loadPrnMedicationOptions = async () => {
      if (!residentId || !isProviderUser || !hasResolvedSubscriptionStatus || !isUserSubscribed) {
        setPrnMedicationOptions([]);
        return;
      }
      setLoadingPrnMedicationOptions(true);
      try {
        const medicationResponse = await backendAPIStore.getResidentEmarMedications(residentId);
        setPrnMedicationOptions(buildBehaviorPrnMedicationOptions(medicationResponse?.items || medicationResponse || []));
      } catch {
        setPrnMedicationOptions([]);
      } finally {
        setLoadingPrnMedicationOptions(false);
      }
    };

    void loadPrnMedicationOptions();
  }, [backendAPIStore, hasResolvedSubscriptionStatus, isProviderUser, isUserSubscribed, residentId]);

  useEffect(() => {
    if (!residentId || searchParams.get("openDailyLog") !== "1") {
      return;
    }

    const openDialog = async () => {
      setShowBehaviorLogDialog(true);
      await loadResidentBehaviorInsights(true);
      await loadResidentBehaviorLogEntries({ resetPagination: true });
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("openDailyLog");
      setSearchParams(nextParams, { replace: true });
    };

    void openDialog();
  }, [residentId, searchParams, setSearchParams, loadResidentBehaviorLogEntries]);

  const openBehaviorLogDialog = async () => {
    setShowBehaviorLogDialog(true);
    await loadResidentBehaviorInsights(true);
    await loadResidentBehaviorLogEntries();
  };

  const openDailyLogImportDialog = async () => {
    await loadResidentBehaviorInsights(true);
    setShowDailyLogImportDialog(true);
  };

  const refreshResidentDailyLogTrackersAfterImport = async () => {
    await Promise.all([
      loadResidentBehaviorInsights(false),
      loadResidentBehaviorLogEntries({ resetPagination: true, suppressErrorToast: true }),
      loadResidentActivityTrackerSnapshots(),
    ]);
  };

  const createResidentBehaviorLogRecord = async (payload: Record<string, any>) => {
    if (!residentId) {
      return;
    }

    setSavingBehaviorLogEntry(true);
    try {
      const resolvedPayload = await buildBehaviorPrnSyncPayload({
        backendAPIStore,
        residentId,
        behaviorPayload: payload,
      });
      await backendAPIStore.createResidentBehaviorLogEntry(residentId, resolvedPayload);
      await loadResidentBehaviorLogEntries({ resetPagination: true });
      toast.current?.show({
        severity: "success",
        summary: "Behavior entry saved",
        detail: "Daily behavior log updated successfully.",
        life: 2500,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save behavior entry.",
        life: 4000,
      });
      throw error;
    } finally {
      setSavingBehaviorLogEntry(false);
    }
  };

  const updateResidentBehaviorLogRecord = async (behaviorLogEntryId: string, payload: Record<string, any>) => {
    if (!residentId) {
      return;
    }

    setSavingBehaviorLogEntry(true);
    try {
      const resolvedPayload = await buildBehaviorPrnSyncPayload({
        backendAPIStore,
        residentId,
        behaviorPayload: payload,
      });
      await backendAPIStore.updateResidentBehaviorLogEntry(residentId, {
        behaviorLogEntryId,
        ...resolvedPayload,
      });
      await loadResidentBehaviorLogEntries();
      toast.current?.show({
        severity: "success",
        summary: "Behavior entry updated",
        detail: "Daily behavior log updated successfully.",
        life: 2500,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to update behavior entry.",
        life: 4000,
      });
      throw error;
    } finally {
      setSavingBehaviorLogEntry(false);
    }
  };

  const deleteResidentBehaviorLogDraft = async (behaviorLogEntryId: string) => {
    if (!residentId) {
      return;
    }

    setSavingBehaviorLogEntry(true);
    try {
      await backendAPIStore.deleteResidentBehaviorLogEntry(residentId, behaviorLogEntryId);
      await loadResidentBehaviorLogEntries({ resetPagination: true });
      toast.current?.show({
        severity: "success",
        summary: "Draft deleted",
        detail: "Saved draft removed successfully.",
        life: 2500,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to delete draft.",
        life: 4000,
      });
      throw error;
    } finally {
      setSavingBehaviorLogEntry(false);
    }
  };

  const formatResidentBehaviorLogText = async (requestBody: {
    text: string;
    taskType: string;
    tone: string;
    domain: string;
    outputStyle: string;
  }) => {
    try {
      return await backendAPIStore.formatAiText(requestBody);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to polish text with AI.",
        life: 4000,
      });
      throw error;
    }
  };

  const getDefaultDateInputValue = () => {
    const currentDate = new Date();
    const offsetDate = new Date(currentDate.getTime() - (currentDate.getTimezoneOffset() * 60000));
    return offsetDate.toISOString().slice(0, 10);
  };

  const getPrintableUserName = (profile: any) =>
    String(
      `${String(profile?.firstName || "").trim()} ${String(profile?.lastName || "").trim()}`.trim()
      || profile?.name
      || "",
    ).trim();

  const openResidentDobSetup = (dateOfBirth?: string) => {
    setResidentDobDraft(String(dateOfBirth || residentProfileData?.dateOfBirth || "").trim());
    setShowResidentDobDialog(true);
  };

  const generateResidentBehaviorLogReport = async (
    profileOverride?: UserProfile | null,
    skipResidentDobCheck: boolean = false,
    residentProfileOverride?: any,
  ) => {
    const normalizedProviderOneId = String((profileOverride || currentUserProfile)?.providerOneId || "").trim();
    if (!normalizedProviderOneId) {
      setProviderOneIdDraft(normalizedProviderOneId);
      setShowProviderOneIdDialog(true);
      return;
    }

    if (!residentId || !selectedCbhsMonth) {
      return;
    }

    setGeneratingResidentCbhsReport(true);
    try {
      await backendAPIStore.init();
      const dataset = await backendAPIStore.getResidentBehaviorLogCbhsMonthlyDataset(residentId, selectedCbhsMonth);
      const normalizedResidentDob = String(
        dataset?.resident?.dateOfBirth
        || residentProfileOverride?.dateOfBirth
        || residentProfileData?.dateOfBirth
        || "",
      ).trim();
      if (!skipResidentDobCheck && !normalizedResidentDob) {
        openResidentDobSetup();
        return;
      }
      const resolvedPrintedName = getPrintableUserName(profileOverride || currentUserProfile);
      const resolvedSignatureDate = getDefaultDateInputValue();
      const response = await backendAPIStore.generateResidentBehaviorLogReportDocument(residentId, {
        dataset,
        providerOneId: normalizedProviderOneId,
        residentDob: normalizedResidentDob,
        printedName: resolvedPrintedName,
        signatureDate: resolvedSignatureDate,
      });
      if (response?.documentUrl) {
        openUrlInNewTab(response.documentUrl);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to generate the monthly CBHS/IBSS tracker report.",
        life: 4000,
      });
    } finally {
      setGeneratingResidentCbhsReport(false);
    }
  };

  const saveProviderOneIdAndGenerateResidentBehaviorReport = async () => {
    const normalizedProviderOneId = normalizePlainText(providerOneIdDraft, { maxLength: 40 }).trim();
    if (!normalizedProviderOneId) {
      toast.current?.show({
        severity: "warn",
        summary: "ProviderOne ID required",
        detail: "Enter your ProviderOne ID to continue with CBHS/IBSS report generation.",
        life: 4000,
      });
      return;
    }

    if (!currentUserProfile?.userId) {
      toast.current?.show({
        severity: "error",
        summary: "Profile unavailable",
        detail: "Unable to save ProviderOne ID right now. Please open your profile and try again.",
        life: 4000,
      });
      return;
    }

    setSavingProviderOneId(true);
    try {
      await backendAPIStore.init();
      const updatedProfile = await backendAPIStore.updateUserProfile({
        ...currentUserProfile,
        providerOneId: normalizedProviderOneId,
      });
      const nextProfile = (updatedProfile || { ...currentUserProfile, providerOneId: normalizedProviderOneId }) as UserProfile;
      setCurrentUserProfile(nextProfile);
      setProviderOneIdDraft(normalizedProviderOneId);
      setShowProviderOneIdDialog(false);
      void generateResidentBehaviorLogReport(nextProfile);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save ProviderOne ID.",
        life: 4000,
      });
    } finally {
      setSavingProviderOneId(false);
    }
  };

  const saveResidentDobAndGenerateResidentBehaviorReport = async () => {
    const normalizedResidentDob = String(residentDobDraft || "").trim();
    if (!normalizedResidentDob) {
      toast.current?.show({
        severity: "warn",
        summary: "Resident DOB required",
        detail: "Enter the resident's date of birth to continue with CBHS/IBSS report generation.",
        life: 4000,
      });
      return;
    }

    if (!residentProfileData?.residentId) {
      toast.current?.show({
        severity: "error",
        summary: "Resident profile unavailable",
        detail: "Unable to save the resident's date of birth right now.",
        life: 4000,
      });
      return;
    }

    setSavingResidentDob(true);
    try {
      await backendAPIStore.init();
      const updatedResidentProfile = await backendAPIStore.updateResident({
        ...residentProfileData,
        dateOfBirth: normalizedResidentDob,
      });
      const nextResidentProfile = updatedResidentProfile || { ...residentProfileData, dateOfBirth: normalizedResidentDob };
      setResidentProfileData(nextResidentProfile);
      setResidentDobDraft(normalizedResidentDob);
      setShowResidentDobDialog(false);
      void generateResidentBehaviorLogReport(currentUserProfile, true, nextResidentProfile);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save the resident's date of birth.",
        life: 4000,
      });
    } finally {
      setSavingResidentDob(false);
    }
  };

  const loadResidentCarePlanTracking = async (autoImportIfEmpty: boolean = false) => {
    if (!residentId) {
      return;
    }

    setLoadingCarePlanTracking(true);
    try {
      await backendAPIStore.init();
      const [snapshot, timeline] = await Promise.all([
        backendAPIStore.getResidentCarePlanTrackingSnapshot(residentId),
        backendAPIStore.getResidentCarePlanTrackingTimeline(residentId, 200),
      ]);
      setResidentCarePlanTrackingSnapshot(snapshot);
      setResidentCarePlanTrackingTimeline(Array.isArray(timeline) ? timeline : []);

      if (
        autoImportIfEmpty
        && isCarePlanTrackingSnapshotEmpty(snapshot)
        && !carePlanTrackingAutoImportedRef.current
      ) {
        carePlanTrackingAutoImportedRef.current = true;
        const importSucceeded = await importResidentCarePlanTracking("auto");
        if (importSucceeded) {
          await loadResidentCarePlanTracking(false);
        }
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Unable to load care plan tracking",
        detail: error?.message || "Please try again.",
      });
    } finally {
      setLoadingCarePlanTracking(false);
    }
  };

  const openCarePlanTrackingDialog = async () => {
    carePlanTrackingAutoImportedRef.current = false;
    setShowCarePlanTrackingDialog(true);
    await loadResidentCarePlanTracking(true);
  };

  const formatBehaviorTimestamp = (value: any) => {
    if (!value) {
      return "Unknown";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString();
  };

  const getBehaviorTimelineEventLabel = (eventType: string) => {
    switch (eventType) {
      case "manual_save":
        return "Manual psych/social update";
      case "manual_rebuild":
        return "Manual behavior import";
      case "initial_import":
        return "Initial behavior import";
      case "ncp_regenerated":
        return "Care plan regenerated";
      default:
        return "Care plan generated";
    }
  };

  const getCarePlanTimelineEventLabel = (eventType: string) => {
    switch (eventType) {
      case "manual_save":
        return "Manual care plan update";
      case "manual_rebuild":
        return "Manual care plan import";
      case "initial_import":
        return "Initial care plan import";
      case "ncp_regenerated":
        return "Care plan regenerated";
      default:
        return "Care plan generated";
    }
  };

  const getFilteredBehaviorTimeline = () => {
    if (behaviorTimelineFilter === "all") {
      return residentBehaviorTimeline;
    }

    const days = behaviorTimelineFilter === "7d" ? 7 : behaviorTimelineFilter === "30d" ? 30 : 90;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);

    return residentBehaviorTimeline.filter((entry) => {
      const recordedAt = entry?.recordedAt ? new Date(entry.recordedAt) : null;
      return recordedAt && !Number.isNaN(recordedAt.getTime()) && recordedAt >= cutoff;
    });
  };

  const currentBehaviorRows = Array.isArray(residentBehaviorSnapshot?.behaviors)
    ? residentBehaviorSnapshot.behaviors
    : [];
  const ongoingBehaviorLogEntryCount = residentBehaviorLogEntries.filter((entry: any) => entry?.isOngoing).length;
  const latestBehaviorLogEntry = residentBehaviorLogEntries[0] || null;
  const getResidentActivityEntryTimestamp = (entry: any) =>
    entry?.occurredAt
    || entry?.recordedAt
    || entry?.updatedAt
    || entry?.createdAt
    || "";
  const latestIncidentReportTimestamp = getResidentActivityEntryTimestamp(latestIncidentReportEntry);
  const latestDailyNoteTimestamp = getResidentActivityEntryTimestamp(latestDailyNoteEntry);
  const behaviorLogOptions = currentBehaviorRows
    .map((row: any) => ({
      behaviorKey: String(row?.behaviorKey || "").trim(),
      behaviorTitle: String(row?.behaviorTitle || "").trim() || String(row?.behaviorKey || "").trim() || "Behavior",
      sectionCategory: String(row?.sectionCategory || "").trim() || "Psych/Social & Cognitive",
    }))
    .filter((option: any) => option.behaviorKey);
  const getBehaviorRowsFromTimelineSnapshot = (entry: any) =>
    Array.isArray(entry?.snapshot) ? entry.snapshot : [];
  const getBehaviorTitleForTimelineEntry = (entry: any, behaviorKey: string) => {
    const entrySnapshotRows = getBehaviorRowsFromTimelineSnapshot(entry);
    const entryRow = entrySnapshotRows.find((row: any) => row?.behaviorKey === behaviorKey);
    if (entryRow?.behaviorTitle) {
      return entryRow.behaviorTitle;
    }

    const currentRow = currentBehaviorRows.find((row: any) => row?.behaviorKey === behaviorKey);
    if (currentRow?.behaviorTitle) {
      return currentRow.behaviorTitle;
    }

    return getReadableFieldPathLabel(behaviorKey);
  };
  const buildBehaviorTimelineChangeCards = (entry: any, previousEntry: any) => {
    const changeSummary = entry?.changeSummary || {};
    const nextRows = getBehaviorRowsFromTimelineSnapshot(entry);
    const previousRows = getBehaviorRowsFromTimelineSnapshot(previousEntry);
    const nextMap = new Map<string, any>(nextRows.map((row: any) => [row.behaviorKey, row]));
    const previousMap = new Map<string, any>(previousRows.map((row: any) => [row.behaviorKey, row]));
    const changedKeys = Array.from(new Set([
      ...(Array.isArray(changeSummary.addedBehaviorKeys) ? changeSummary.addedBehaviorKeys : []),
      ...(Array.isArray(changeSummary.removedBehaviorKeys) ? changeSummary.removedBehaviorKeys : []),
      ...(Array.isArray(changeSummary.updatedDetailKeys) ? changeSummary.updatedDetailKeys : []),
      ...(Array.isArray(changeSummary.updatedInstructionKeys) ? changeSummary.updatedInstructionKeys : []),
    ]));

    return changedKeys.map((behaviorKey: string) => {
      const previousRow = previousMap.get(behaviorKey) || null;
      const nextRow = nextMap.get(behaviorKey) || null;
      const behaviorTitle = nextRow?.behaviorTitle || previousRow?.behaviorTitle || getBehaviorTitleForTimelineEntry(entry, behaviorKey);
      const presentChanged = Boolean(previousRow?.present) !== Boolean(nextRow?.present);
      const detailsChanged = (previousRow?.residentDetails || "") !== (nextRow?.residentDetails || "");
      const instructionsChanged = (previousRow?.caregiverInstructions || "") !== (nextRow?.caregiverInstructions || "");
      const status = !previousRow && nextRow
        ? "added"
        : previousRow && !nextRow
          ? "removed"
          : presentChanged && nextRow?.present
            ? "added"
            : presentChanged && previousRow?.present
              ? "removed"
              : "updated";

      return {
        behaviorKey,
        behaviorTitle,
        status,
        previousRow,
        nextRow,
        presentChanged,
        detailsChanged,
        instructionsChanged,
      };
    });
  };
  const activeBehaviorRows = currentBehaviorRows.filter((row: any) =>
    row?.present || row?.residentDetails || row?.caregiverInstructions
  );
  const behaviorTimelineBehaviorOptions = [
    { label: "All behaviors", value: "all" },
    ...currentBehaviorRows
      .map((row: any) => ({
        label: row?.behaviorTitle || getReadableFieldPathLabel(row?.behaviorKey || ""),
        value: row?.behaviorKey || "",
      }))
      .filter((option) => option.value)
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const filteredBehaviorTimeline = getFilteredBehaviorTimeline();
  const visibleBehaviorTimelineEntries = filteredBehaviorTimeline
    .map((entry: any, index: number) => {
      const previousEntry = filteredBehaviorTimeline[index + 1];
      const behaviorChangeCards = buildBehaviorTimelineChangeCards(entry, previousEntry)
        .filter((changeCard: any) =>
          behaviorTimelineBehaviorFilter === "all"
            ? true
            : changeCard.behaviorKey === behaviorTimelineBehaviorFilter
        );

      return behaviorChangeCards.length > 0
        ? { entry, behaviorChangeCards }
        : null;
    })
    .filter(Boolean) as Array<{ entry: any; behaviorChangeCards: any[] }>;
  const carePlanSectionSummaries = residentCarePlanTrackingSnapshot?.sectionSummaries || {};
  const carePlanSectionKeys = Array.isArray(residentCarePlanTrackingSnapshot?.sectionKeys)
    ? residentCarePlanTrackingSnapshot.sectionKeys
    : Object.keys(carePlanSectionSummaries);
  const isPopulatedCarePlanValue = (value: any): boolean => {
    if (Array.isArray(value)) {
      return value.some((item) => isPopulatedCarePlanValue(item));
    }
    if (value && typeof value === "object") {
      return Object.values(value).some((item) => isPopulatedCarePlanValue(item));
    }
    if (value === true) return true;
    if (typeof value === "string") return value.trim().length > 0;
    if (typeof value === "number") return !Number.isNaN(value);
    return false;
  };
  const flattenPopulatedCarePlanFields = (value: any, basePath = ""): Array<{ fieldPath: string; value: any }> => {
    if (Array.isArray(value)) {
      return value.length > 0 && isPopulatedCarePlanValue(value)
        ? [{ fieldPath: basePath || "items", value }]
        : [];
    }

    if (value && typeof value === "object") {
      return Object.entries(value).flatMap(([key, nestedValue]) =>
        flattenPopulatedCarePlanFields(nestedValue, basePath ? `${basePath}.${key}` : key)
      );
    }

    return isPopulatedCarePlanValue(value)
      ? [{ fieldPath: basePath || "value", value }]
      : [];
  };
  const getReadableFieldPathLabel = (fieldPath: string) =>
    fieldPath
      .split(".")
      .filter(Boolean)
      .map((segment) =>
        segment
          .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
          .replace(/[_-]+/g, " ")
          .replace(/\s+/g, " ")
          .trim()
      )
      .map((segment) => capitalizeFirstLetter(segment))
      .join(" / ");
  const renderCarePlanTrackingValue = (value: any, depth = 0) => {
    if (value === null || value === undefined || value === "") {
      return <span style={{ ...appMutedTextStyle, fontStyle: "italic" }}>Empty</span>;
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (typeof value === "number") {
      return String(value);
    }

    if (typeof value === "string") {
      return value;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span style={{ ...appMutedTextStyle, fontStyle: "italic" }}>Empty</span>;
      }

      const primitiveValues = value.every((item) => item === null || item === undefined || ["string", "number", "boolean"].includes(typeof item));
      if (primitiveValues) {
        return (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "0.5rem",
            }}
          >
            {value.map((item, index) => (
              <div
                key={`${String(item)}-${index}`}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  padding: "0.55rem 0.7rem",
                  background: depth === 0 ? "#f8fafc" : "#ffffff",
                  color: "#475569",
                }}
              >
                {item === null || item === undefined || item === "" ? "Empty" : String(item)}
              </div>
            ))}
          </div>
        );
      }

      const objectValues = value.every((item) => item && typeof item === "object" && !Array.isArray(item));
      if (objectValues) {
        const columnKeys = Array.from(
          new Set(
            value.flatMap((item) =>
              Object.keys(item).filter((key) => item[key] !== undefined && item[key] !== null && item[key] !== "")
            )
          )
        );

        if (columnKeys.length > 0) {
          return (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  overflow: "hidden",
                  background: "#ffffff",
                }}
              >
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {columnKeys.map((columnKey) => (
                      <th
                        key={columnKey}
                        style={{
                          textAlign: "left",
                          padding: "0.7rem 0.75rem",
                          borderBottom: "1px solid #e2e8f0",
                          color: "#334155",
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getReadableFieldPathLabel(columnKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {value.map((item, rowIndex) => (
                    <tr key={rowIndex}>
                      {columnKeys.map((columnKey) => (
                        <td
                          key={`${rowIndex}-${columnKey}`}
                          style={{
                            padding: "0.7rem 0.75rem",
                            borderBottom: rowIndex === value.length - 1 ? "none" : "1px solid #e2e8f0",
                            verticalAlign: "top",
                            color: "#475569",
                            fontSize: "0.84rem",
                          }}
                        >
                          {renderCarePlanTrackingValue(item?.[columnKey], depth + 1)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
      }

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {value.map((item, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "0.6rem 0.75rem",
                background: depth === 0 ? "#f8fafc" : "#ffffff",
              }}
            >
              {renderCarePlanTrackingValue(item, depth + 1)}
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === "object") {
      const entries = Object.entries(value).filter(([, nestedValue]) => nestedValue !== undefined);
      if (entries.length === 0) {
        return <span style={{ ...appMutedTextStyle, fontStyle: "italic" }}>Empty</span>;
      }

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
          }}
        >
          {entries.map(([nestedKey, nestedValue]) => (
            <div
              key={nestedKey}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "0.6rem 0.75rem",
                background: depth === 0 ? "#f8fafc" : "#ffffff",
              }}
            >
              <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.82rem", marginBottom: "0.35rem" }}>
                {getReadableFieldPathLabel(nestedKey)}
              </div>
              <div style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                {renderCarePlanTrackingValue(nestedValue, depth + 1)}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return String(value);
  };
  const carePlanSectionFilterOptions = [
    { label: "All sections", value: "all" },
    ...carePlanSectionKeys.map((sectionKey: string) => ({
      label: carePlanSectionSummaries?.[sectionKey]?.sectionLabel || sectionKey,
      value: sectionKey,
    })),
  ];
  const getFilteredCarePlanTimeline = () => {
    let timeline = residentCarePlanTrackingTimeline;
    if (carePlanTimelineFilter !== "all") {
      const days = carePlanTimelineFilter === "7d" ? 7 : carePlanTimelineFilter === "30d" ? 30 : 90;
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      timeline = timeline.filter((entry) => {
        const recordedAt = entry?.recordedAt ? new Date(entry.recordedAt) : null;
        return recordedAt && !Number.isNaN(recordedAt.getTime()) && recordedAt >= cutoff;
      });
    }
    if (carePlanTimelineSectionFilter !== "all") {
      timeline = timeline.filter((entry) =>
        Array.isArray(entry?.changeSummary?.changedSections)
          ? entry.changeSummary.changedSections.includes(carePlanTimelineSectionFilter)
          : false
      );
    }
    return timeline;
  };
  const filteredCarePlanTimeline = getFilteredCarePlanTimeline();
  const medicationLogsBinderFolder = getResidentBinderFolder(RESIDENT_BINDER_FOLDER_CODES.MEDICATION_LOGS);
  const carePlanBinderFolder = getResidentBinderFolder(RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN);
  const legalDocumentsBinderFolder = getResidentBinderFolder(RESIDENT_BINDER_FOLDER_CODES.LEGAL_DOCUMENTS);
  const assessmentBinderDocuments = getFolderDocuments(residentBinderDocuments, assessmentBinderFolder?.folderId);
  const carePlanBinderDocuments = getFolderDocuments(residentBinderDocuments, carePlanBinderFolder?.folderId);
  const legalDocumentsBinderDocuments = getFolderDocuments(residentBinderDocuments, legalDocumentsBinderFolder?.folderId);
  const polstBinderDocument = legalDocumentsBinderDocuments.find((document) => /polst/i.test(String(document?.title || "")))
    || residentBinderDocuments.find((document) => /polst/i.test(String(document?.title || "")))
    || null;
  const hasDiagnosisData = Boolean(
    medicationDiagnosisText
    || activeAssessmentDiagnosisLines.length > 0
  );
  const hasAllergiesData = typeof resolvedProfileSnapshot.anyAllergies === "boolean" || Boolean(String(resolvedProfileSnapshot.allergiesDescribed || "").trim());
  const hasPhysicianData = Boolean(String(resolvedProfileSnapshot.primaryPhysicianName || "").trim());
  const hasPharmacyData = Boolean(String(resolvedProfileSnapshot.pharmacyName || "").trim());
  const hasEmergencyContactsData = collateralContacts.length > 0;
  const hasProgressNotesData = Boolean(latestDailyNoteEntry);
  const hasCurrentMarData = residentEmarMedicationCount > 0;
  const hasVitalTrackerHistoryData = residentEmarDailyTrackingItemCount > 0;
  const hasFaceSheetData = Boolean(
    String(resolvedProfileSnapshot.firstName || "").trim()
    && String(resolvedProfileSnapshot.lastName || "").trim()
    && String(resolvedProfileSnapshot.dateOfBirth || "").trim()
  );
  const openProfileSetupDialog = () => {
    setProfileFormData(buildProfileFormState());
    setSelectedResidentProfileImageFile(null);
    setIsEditingProfileSection(true);
  };
  const clearEmergencyPolstUploadSelection = () => {
    setEmergencyPolstUploadFile(null);
    setEmergencyPolstUploadKey((current) => current + 1);
  };
  const visibleEmergencyPacketSectionOptions = EMERGENCY_PACKET_SECTION_OPTIONS.filter((option) => (
    hasCurrentMarData || (option.value !== "vitalTrackerHistory" && option.value !== "currentMedList")
  ));
  const emergencyPacketSectionMeta: Record<string, {
    enabled: boolean;
    missingMessage?: string;
    actionLabel?: string;
    action?: () => void;
  }> = {
    faceSheet: {
      enabled: hasFaceSheetData,
      missingMessage: "Basic resident profile details are missing.",
      actionLabel: "Open Profile",
      action: openProfileSetupDialog,
    },
    currentMar: {
      enabled: hasCurrentMarData,
      missingMessage: "No active eMAR medications are set up.",
      actionLabel: "Open eMAR",
      action: () => navigate(RouterAddress.getProviderResidentEmarPagePath(residentId || "", String(homeId || ""))),
    },
    polst: {
      enabled: !!polstBinderDocument,
      missingMessage: "POLST setup required.",
    },
    currentMedList: {
      enabled: hasCurrentMarData,
      missingMessage: "No active medication list is available.",
      actionLabel: "Open eMAR",
      action: () => navigate(RouterAddress.getProviderResidentEmarPagePath(residentId || "", String(homeId || ""))),
    },
    diagnosis: {
      enabled: hasDiagnosisData,
      missingMessage: "Diagnosis details are missing.",
      actionLabel: "Open Care Plan",
      action: () => navigate(getResidentCarePlanPath(homeId || "", residentId || "")),
    },
    allergies: {
      enabled: hasAllergiesData,
      missingMessage: "Allergy details are missing.",
      actionLabel: "Open Care Plan Basics",
      action: openCarePlanBasicsDialog,
    },
    physician: {
      enabled: hasPhysicianData,
      missingMessage: "Primary physician details are missing.",
      actionLabel: "Open Contacts",
      action: openCarePlanContactsDialog,
    },
    pharmacy: {
      enabled: hasPharmacyData,
      missingMessage: "Pharmacy details are missing.",
      actionLabel: "Open Contacts",
      action: openCarePlanContactsDialog,
    },
    emergencyContacts: {
      enabled: hasEmergencyContactsData,
      missingMessage: "Emergency contacts setup required.",
      actionLabel: "Open Care Plan",
      action: () => navigate(getResidentCarePlanPath(homeId || "", residentId || "")),
    },
    progressNotes: {
      enabled: hasProgressNotesData,
      missingMessage: "No progress notes are available yet.",
      actionLabel: "Open Progress Notes",
      action: () => navigate(RouterAddress.getProviderResidentDailyNotesPagePath(residentId || "", undefined, String(homeId || ""))),
    },
    vitalTrackerHistory: {
      enabled: hasVitalTrackerHistoryData,
      missingMessage: "No eMAR vital tracker history is available yet.",
      actionLabel: "Open eMAR",
      action: () => navigate(RouterAddress.getProviderResidentEmarPagePath(residentId || "", String(homeId || ""))),
    },
  };
  useEffect(() => {
    const visibleSectionValues = new Set(visibleEmergencyPacketSectionOptions.map((option) => option.value));
    setSelectedEmergencyPacketSections((current) => {
      const next = current.filter((value) => visibleSectionValues.has(value));
      return next.length === current.length ? current : next;
    });
  }, [visibleEmergencyPacketSectionOptions]);
  const effectiveLatestMedicationCompareJobId = String(latestMedicationCompareJobId || medicationCompareCompletedJobId || "").trim();
  const isMedicationCompareReady = Boolean(medicationCompareUploadFile) || Boolean(String(medicationCompareFileName || "").trim());
  const medicationCompareHasDifferences = Boolean(
    (medicationCompareResultData?.changed || []).length ||
    (medicationCompareResultData?.newOnUploadedMar || []).length ||
    (medicationCompareResultData?.removedFromUploadedMar || []).length,
  );
  const standaloneAssessmentCompareUploadPending =
    !!compareFirstUploadFile
    || !!compareSecondUploadFile
    || uploadingBinderDialogFile === "compare-first"
    || uploadingBinderDialogFile === "compare-second";
  const standaloneCarePlanCompareUploadPending =
    !!carePlanCompareFirstUploadFile
    || !!carePlanCompareSecondUploadFile
    || uploadingBinderDialogFile === "careplan-first"
    || uploadingBinderDialogFile === "careplan-second";

  const generateDocumentSection = () => {
    const canUseAssessmentActions = doesNCPExtractedTextExist;
    const canCompareAssessments = doesNCPExtractedTextExist && doesNextNCPExtractedTextExist;

    return (
      <div style={appNeutralPanelStyle} className="resident-careplan-actions">
        <div className="resident-careplan-actions-grid">
          <div className="resident-careplan-actions-info">
            <div className="grid mb-3">
              <div className="col-12 lg:col-8">
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Assessment Workspace</div>
                <div className="mt-2" style={appMutedTextStyle}>
                  Use resident binder assessments to generate care plans, review summaries, and compare changes over time.
                </div>
                <div className="mt-3 flex flex-column gap-2" style={appMutedTextStyle}>
                  <div className="m-0">
                    Choose a DSHS or private assessment already stored in the resident binder so the same file can power NCP, summary, and comparison tools.
                  </div>
                  <div className="m-0">
                    AFH Hub uses AI to pull key details from the assessment and draft the NCP sections for you. Review and edit each section before finalizing.
                  </div>
                </div>
              </div>
              <div className="col-12 lg:col-4 flex lg:justify-content-end lg:align-items-start">
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    borderRadius: "999px",
                    padding: "0.5rem 0.9rem",
                    background: canUseAssessmentActions ? "#ecfdf3" : "#fff7ed",
                    color: canUseAssessmentActions ? "#047857" : "#b45309",
                    fontWeight: 700,
                  }}
                >
                  <i className={`pi ${canUseAssessmentActions ? "pi-check-circle" : "pi-clock"}`} />
                  <span>{canUseAssessmentActions ? "Assessment imported" : "Binder assessment needed"}</span>
                </div>
              </div>
            </div>

          </div>
          <div className="resident-careplan-action-list">
            <ul className="list-none m-0 p-0">
              {[
                {
                  icon: 'pi pi-upload',
                  label: ncpGeneratedSuccessfully ? 'Choose or Upload Binder Assessment' : 'Choose Binder Assessment',
                  buttonLabel: 'Open',
                  severity: 'warning' as const,
                  disabled: false,
                  loading: false,
                  action: openResidentNcpImporter,
                  tooltip: '',
                },
                {
                  icon: 'pi pi-file-edit',
                  label: 'Generate Care Plan',
                  buttonLabel: 'Generate',
                  severity: 'warning' as const,
                  disabled: !canUseAssessmentActions,
                  loading: generatingCarePlanDocument,
                  action: showGenerateNCPDocumentConfirmation,
                  tooltip: 'Generate a care plan document from the uploaded assessment.',
                },
                {
                  icon: 'pi pi-list',
                  label: 'View Summary',
                  buttonLabel: 'View',
                  severity: 'secondary' as const,
                  disabled: !canUseAssessmentActions,
                  loading: false,
                  action: () => { void onViewAssessmentSummaryClick(); },
                  tooltip: 'View a summary of the assessment results. Available after assessment upload.',
                },
                {
                  icon: 'pi pi-chart-bar',
                  label: 'Compare Assessments',
                  buttonLabel: 'Compare',
                  severity: 'secondary' as const,
                  disabled: !canCompareAssessments,
                  loading: false,
                  action: () => { void onCompareAssessment(); },
                  tooltip: 'Available after uploading a new assessment. Compares latest and previous assessments.',
                },
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex flex-wrap align-items-center justify-content-between gap-3 py-3 px-3 resident-careplan-action-item"
                >
                  <span className={`flex align-items-center mr-2 gap-2${item.disabled ? ' text-400' : ''}`} style={{ fontWeight: 600, color: "#0f172a" }}>
                    <i className={`${item.icon} text-primary`} />
                    <span>{item.label}</span>
                  </span>
                  <Button
                    label={item.buttonLabel}
                    severity={item.severity}
                    rounded
                    outlined
                    size="small"
                    disabled={item.disabled}
                    loading={item.loading}
                    onClick={item.action}
                  // tooltip={item.tooltip}
                  // tooltipOptions={{ showOnDisabled: true }}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ width: "100%", display: "flex", flexDirection: "column", gap: "1.25rem", padding: "1.25rem" }}
      className="resident-details-page m-2 p-component"
    >
      <Toast ref={toast}></Toast>
      <Dialog
        header="Archive limit reached"
        visible={showResidentArchiveLimitDialog}
        modal
        style={{ width: "34rem", maxWidth: "92vw" }}
        onHide={() => { setShowResidentArchiveLimitDialog(false); setResidentArchiveLimitDetails({}); }}
        footer={(
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button type="button" label="Close" severity="secondary" outlined rounded onClick={() => { setShowResidentArchiveLimitDialog(false); setResidentArchiveLimitDetails({}); }} />
            <Button type="button" label="View Subscription" icon="pi pi-credit-card" severity="warning" rounded onClick={() => { setShowResidentArchiveLimitDialog(false); setResidentArchiveLimitDetails({}); navigate(RouterAddress.BILLING); }} />
          </div>
        )}
      >
        <div style={{ color: "#334155", lineHeight: 1.6 }}>
          You have reached the maximum number of resident archives allowed in this billing period.
          {residentArchiveLimitDetails.billingPeriodStart && residentArchiveLimitDetails.billingPeriodEnd ? (
            <div style={{ marginTop: "0.75rem", fontWeight: 600 }}>
              Billing period: {new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", year: "numeric" }).format(new Date(residentArchiveLimitDetails.billingPeriodStart))} – {new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", year: "numeric" }).format(new Date(residentArchiveLimitDetails.billingPeriodEnd))}
            </div>
          ) : null}
          <div style={{ marginTop: "0.75rem" }}>You can archive more residents when the next billing period begins or after updating your subscription.</div>
        </div>
      </Dialog>
      <style>
        {`
          .resident-details-page .resident-workspace-tabs .p-tabview-nav-container {
            margin-bottom: 1rem;
          }

          .resident-details-page .resident-workspace-tabs {
            width: 100%;
          }

          .resident-details-page .resident-workspace-tabs .p-tabview-nav {
            display: flex;
            flex-wrap: wrap;
            gap: 0.55rem;
            padding: 0.45rem;
            border: 1px solid #dbe4f0;
            border-radius: 18px;
            background: linear-gradient(180deg, #f8fbff 0%, #f1f5f9 100%);
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
            width: 100%;
            max-width: 100%;
            margin: 0;
            justify-content: center;
          }

          .resident-details-page .resident-workspace-tabs .p-tabview-nav li {
            margin: 0;
          }

          .resident-details-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link {
            border: 1px solid #d7e0ea;
            border-radius: 14px;
            background: #ffffff;
            color: #475569;
            padding: 0.8rem 1rem;
            font-weight: 700;
            transition: all 0.18s ease;
            box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
          }

          .resident-details-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link:not(.p-disabled):focus {
            box-shadow: 0 0 0 0.18rem rgba(249, 115, 22, 0.16);
          }

          .resident-details-page .resident-workspace-tabs .p-tabview-nav li:not(.p-highlight) .p-tabview-nav-link:hover {
            border-color: #fdba74;
            background: linear-gradient(180deg, #ffffff 0%, #fff7ed 100%);
            color: #9a3412;
            transform: translateY(-1px);
          }

          .resident-details-page .resident-workspace-tabs .p-tabview-nav li.p-highlight .p-tabview-nav-link {
            border-color: #fdba74;
            background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 55%, #fef3c7 100%);
            color: #0f172a;
            box-shadow: 0 8px 18px rgba(249, 115, 22, 0.14);
          }

          .resident-details-page .resident-workspace-tabs .p-tabview-panels {
            padding: 0;
            background: transparent;
          }

          .resident-details-page .resident-workspace-tabs .p-tabview-panels .p-tabview-panel {
            display: flex;
            flex-direction: column;
            align-items: stretch;
          }

          .resident-details-page .resident-workspace-tabs .p-tabview-panels .p-tabview-panel > * {
            width: 100%;
            max-width: none;
            margin-left: 0;
            margin-right: 0;
          }

          .resident-details-page .emergency-polst-upload .p-fileupload-filename {
            color: #334155;
            font-weight: 600;
          }

          .resident-details-page .emergency-polst-upload .p-button {
            background: #fff7ed;
            border-color: #fdba74;
            color: #9a3412;
            box-shadow: none;
          }

          .resident-details-page .emergency-polst-upload .p-button:hover {
            background: #ffedd5;
            border-color: #fb923c;
            color: #7c2d12;
          }

          .resident-details-page .emergency-polst-upload.p-outlined .p-button,
          .resident-details-page .emergency-polst-upload.p-outlined .p-button:enabled:hover {
            background: #fff7ed;
            border-color: #fdba74;
            color: #9a3412;
            box-shadow: none;
          }

        `}
      </style>
      <ProviderSubscriptionDialog
        visible={showProviderSubscriptionDialog}
        featureName={providerSubscriptionFeatureName}
        onHide={() => setShowProviderSubscriptionDialog(false)}
      />
      <Dialog
        header={renderSurfaceHeader("Emergency Packet", "Generate one .docx packet for transfer, ER, or hospital use.", "pi pi-briefcase")}
        visible={showEmergencyPacketDialog}
        style={mobile ? { width: "98vw" } : { width: "62rem" }}
        modal
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => {
          if (!generatingEmergencyPacketDocument && uploadingBinderDialogFile !== "emergency-polst") {
            clearEmergencyPolstUploadSelection();
            setShowEmergencyPacketDialog(false);
          }
        }}
        footer={(
          <div className="flex flex-wrap justify-content-between align-items-center gap-2">
            <div style={appMutedTextStyle}>Default packet range is the last 7 days.</div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                label="Cancel"
                severity="secondary"
                outlined
                rounded
                disabled={generatingEmergencyPacketDocument || uploadingBinderDialogFile === "emergency-polst"}
                onClick={() => {
                  clearEmergencyPolstUploadSelection();
                  setShowEmergencyPacketDialog(false);
                }}
              />
              <Button
                type="button"
                label="Generate Packet"
                severity="warning"
                rounded
                loading={generatingEmergencyPacketDocument}
                disabled={selectedEmergencyPacketSections.filter((section) => emergencyPacketSectionMeta[section]?.enabled).length === 0 || uploadingBinderDialogFile === "emergency-polst"}
                onClick={() => { void generateEmergencyPacketDocument(); }}
              />
            </div>
          </div>
        )}
      >
        <div style={{ display: "grid", gap: "1rem" }}>
          <div>
            <div className="flex flex-column md:flex-row md:align-items-end md:justify-content-between gap-3" style={{ marginBottom: "0.75rem" }}>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>Sections</div>
              <div
                className="flex align-items-center gap-2"
                style={{ width: mobile ? "100%" : "auto", marginLeft: mobile ? 0 : "auto", justifyContent: mobile ? "space-between" : "flex-end" }}
              >
                <label htmlFor="emergencyPacketRange" style={{ fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap" }}>
                  Date Range
                </label>
                <div style={{ width: mobile ? "12rem" : "14rem" }}>
                  <Dropdown
                    id="emergencyPacketRange"
                    value={emergencyPacketRangeType}
                    options={EMERGENCY_PACKET_RANGE_OPTIONS as any}
                    optionLabel="label"
                    optionValue="value"
                    className="w-full"
                    onChange={(event) => setEmergencyPacketRangeType(event.value)}
                  />
                </div>
              </div>
            </div>
            {mobile ? (
              <div className="grid">
                {visibleEmergencyPacketSectionOptions.map((option) => (
                  <div className="col-12" key={option.value}>
                    <div style={{
                      border: "1px solid",
                      borderColor: emergencyPacketSectionMeta[option.value]?.enabled ? "#e2e8f0" : "#fecaca",
                      borderRadius: "14px",
                      padding: "0.85rem 1rem",
                      background: emergencyPacketSectionMeta[option.value]?.enabled ? "#ffffff" : "#fff7f7",
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.65rem",
                    }}>
                      <label className="flex align-items-start gap-2" style={{ cursor: emergencyPacketSectionMeta[option.value]?.enabled ? "pointer" : "not-allowed" }}>
                        <Checkbox
                          inputId={`emergencyPacketSection-${option.value}`}
                          checked={emergencyPacketSectionMeta[option.value]?.enabled && selectedEmergencyPacketSections.includes(option.value)}
                          disabled={!emergencyPacketSectionMeta[option.value]?.enabled}
                          onChange={(event) => {
                            setSelectedEmergencyPacketSections((current) => (
                              event.checked
                                ? Array.from(new Set([...current, option.value]))
                                : current.filter((value) => value !== option.value)
                            ));
                          }}
                        />
                        <span style={{ color: "#0f172a", fontWeight: 600, lineHeight: 1.4 }}>{option.label}</span>
                      </label>
                      {!emergencyPacketSectionMeta[option.value]?.enabled ? (
                        <div className="flex flex-column align-items-start gap-2">
                          {option.value === "currentMar" ? (
                            <Tag severity="warning" value="Set-up required" />
                          ) : (
                            <span style={{ color: "#b91c1c", fontSize: "0.85rem", fontWeight: 600, lineHeight: 1.45 }}>
                              {emergencyPacketSectionMeta[option.value]?.missingMessage || "Setup required."}
                            </span>
                          )}
                          {emergencyPacketSectionMeta[option.value]?.action && emergencyPacketSectionMeta[option.value]?.actionLabel ? (
                            <Button
                              type="button"
                              label={emergencyPacketSectionMeta[option.value]?.actionLabel}
                              severity="danger"
                              text
                              size="small"
                              onClick={() => emergencyPacketSectionMeta[option.value]?.action?.()}
                            />
                          ) : null}
                        </div>
                      ) : null}
                      {option.value === "polst" && !polstBinderDocument ? (
                        <div className="flex align-items-center gap-2 flex-wrap" style={{ paddingTop: "0.25rem" }}>
                          <FileUpload
                            key={`emergency-polst-mobile-${emergencyPolstUploadKey}`}
                            className="emergency-polst-upload"
                            mode="advanced"
                            name="emergencyPolstUpload"
                            accept={BINDER_FEATURE_FILE_ACCEPT}
                            chooseLabel="Upload"
                            auto={false}
                            customUpload
                            onSelect={(event: any) => setEmergencyPolstUploadFile(event.files?.[0] || null)}
                            onClear={clearEmergencyPolstUploadSelection}
                          />
                          {hasSelectedEmergencyPolstUploadFile ? (
                            <>
                              <Button
                                type="button"
                                label="Upload to Binder"
                                severity="warning"
                                rounded
                                size="small"
                                loading={uploadingBinderDialogFile === "emergency-polst"}
                                onClick={() => { void uploadEmergencyPolstToBinder(); }}
                              />
                              <Button
                                type="button"
                                label="Reset"
                                text
                                size="small"
                                onClick={clearEmergencyPolstUploadSelection}
                              />
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "14px", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", background: "#ffffff" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <th style={{ textAlign: "left", padding: "0.8rem 1rem", color: "#334155", fontSize: "0.85rem", fontWeight: 700, borderBottom: "1px solid #e2e8f0", width: "26%" }}>Include</th>
                      <th style={{ textAlign: "left", padding: "0.8rem 1rem", color: "#334155", fontSize: "0.85rem", fontWeight: 700, borderBottom: "1px solid #e2e8f0", width: "29%" }}>Section</th>
                      <th style={{ textAlign: "left", padding: "0.8rem 1rem", color: "#334155", fontSize: "0.85rem", fontWeight: 700, borderBottom: "1px solid #e2e8f0", width: "45%" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleEmergencyPacketSectionOptions.map((option, index) => (
                      <tr
                        key={option.value}
                        style={{
                          background: emergencyPacketSectionMeta[option.value]?.enabled ? "#ffffff" : "#fff7f7",
                        }}
                      >
                        <td style={{ padding: "0.9rem 1rem", borderBottom: index === visibleEmergencyPacketSectionOptions.length - 1 ? "none" : "1px solid #e2e8f0", verticalAlign: "top" }}>
                          <label className="flex align-items-start gap-2" style={{ cursor: emergencyPacketSectionMeta[option.value]?.enabled ? "pointer" : "not-allowed" }}>
                            <Checkbox
                              inputId={`emergencyPacketSection-${option.value}`}
                              checked={emergencyPacketSectionMeta[option.value]?.enabled && selectedEmergencyPacketSections.includes(option.value)}
                              disabled={!emergencyPacketSectionMeta[option.value]?.enabled}
                              onChange={(event) => {
                                setSelectedEmergencyPacketSections((current) => (
                                  event.checked
                                    ? Array.from(new Set([...current, option.value]))
                                    : current.filter((value) => value !== option.value)
                                ));
                              }}
                            />
                          </label>
                        </td>
                        <td style={{ padding: "0.9rem 1rem", borderBottom: index === visibleEmergencyPacketSectionOptions.length - 1 ? "none" : "1px solid #e2e8f0", verticalAlign: "top", color: "#0f172a", fontWeight: 600 }}>
                          {option.label}
                        </td>
                        <td style={{ padding: "0.9rem 1rem", borderBottom: index === visibleEmergencyPacketSectionOptions.length - 1 ? "none" : "1px solid #e2e8f0", verticalAlign: "top" }}>
                          {option.value === "polst" && !polstBinderDocument ? (
                            <div className="flex align-items-center gap-2 flex-wrap">
                              <FileUpload
                                key={`emergency-polst-desktop-${emergencyPolstUploadKey}`}
                                className="emergency-polst-upload"
                                mode="basic"
                                name="emergencyPolstUpload"
                                accept={BINDER_FEATURE_FILE_ACCEPT}
                                chooseLabel="Upload"
                                auto={false}
                                customUpload
                                onSelect={(event: any) => setEmergencyPolstUploadFile(event.files?.[0] || null)}
                                onClear={clearEmergencyPolstUploadSelection}
                              />
                              {hasSelectedEmergencyPolstUploadFile ? (
                                <>
                                  <Button
                                    type="button"
                                    label="Upload to Binder"
                                    severity="warning"
                                    rounded
                                    size="small"
                                    loading={uploadingBinderDialogFile === "emergency-polst"}
                                    onClick={() => { void uploadEmergencyPolstToBinder(); }}
                                  />
                                  <Button
                                    type="button"
                                    label="Reset"
                                    text
                                    size="small"
                                    onClick={clearEmergencyPolstUploadSelection}
                                  />
                                </>
                              ) : null}
                            </div>
                          ) : !emergencyPacketSectionMeta[option.value]?.enabled ? (
                            <div className="flex flex-wrap align-items-center gap-2">
                              {option.value === "currentMar" ? (
                                <Tag severity="warning" value="Set-up required" />
                              ) : (
                                <span style={{ color: "#b91c1c", fontSize: "0.85rem", fontWeight: 600, lineHeight: 1.45 }}>
                                  {emergencyPacketSectionMeta[option.value]?.missingMessage || "Setup required."}
                                </span>
                              )}
                              {emergencyPacketSectionMeta[option.value]?.action && emergencyPacketSectionMeta[option.value]?.actionLabel ? (
                                <Button
                                  type="button"
                                  label={emergencyPacketSectionMeta[option.value]?.actionLabel}
                                  severity="danger"
                                  text
                                  size="small"
                                  onClick={() => emergencyPacketSectionMeta[option.value]?.action?.()}
                                />
                              ) : null}
                            </div>
                          ) : (
                            <span style={{ color: "#047857", fontSize: "0.85rem", fontWeight: 600 }}>Ready</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Dialog>
      {fieldAutoSaveIndicator.visible ? (
        <div
          style={{
            position: "fixed",
            top: fieldAutoSaveIndicator.top,
            left: fieldAutoSaveIndicator.left,
            zIndex: 1200,
            pointerEvents: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.45rem",
            borderRadius: "999px",
            padding: "0.4rem 0.85rem",
            background: fieldAutoSaveIndicator.state === "saving" ? "#dbeafe" : "#ecfdf3",
            color: fieldAutoSaveIndicator.state === "saving" ? "#1d4ed8" : "#047857",
            border: `1px solid ${fieldAutoSaveIndicator.state === "saving" ? "#93c5fd" : "#a7f3d0"}`,
            boxShadow: "0 14px 28px rgba(15, 23, 42, 0.12)",
            fontSize: "0.88rem",
            fontWeight: 700,
          }}
        >
          <i className={`pi ${fieldAutoSaveIndicator.state === "saving" ? "pi-spin pi-spinner" : "pi-check-circle"}`} />
          <span>{fieldAutoSaveIndicator.message}</span>
        </div>
      ) : null}
      {
        loadingProfile ? (
          <div className="col-12 text-center d-flex justify-content-center align-items-center">
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
          </div>
        ) : residentIsArchived ? (
          <div className="col-12">
            <Dialog
              visible
              modal
              closable={false}
              closeOnEscape={false}
              dismissableMask={false}
              draggable={false}
              header={renderSurfaceHeader("Resident Archived", "Restore this resident before opening the workspace.", "pi pi-archive")}
              style={mobile ? { width: "92vw" } : appDialogStyle}
              headerStyle={appDialogHeaderStyle}
              contentStyle={appDialogContentStyle}
              onHide={() => { }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <div style={{ color: "#475569", lineHeight: 1.7 }}>
                  This resident is currently archived. Restore them to the active list before using Resident Details, Care Plan, binder tools, or any other resident actions.
                </div>
                <div style={{ ...appAccentPanelStyle, border: "1px solid #fed7aa" }}>
                  <div style={{ fontWeight: 700, color: "#9a3412" }}>What archive means</div>
                  <div className="mt-2" style={appMutedTextStyle}>
                    Archived residents stay in your records, but they are blocked from active workflows until they are restored.
                  </div>
                </div>
                {archivedResidentRestoreMessage ? (
                  <div style={{ color: "#b45309", fontWeight: 600, lineHeight: 1.6 }}>
                    {archivedResidentRestoreMessage}
                  </div>
                ) : null}
                <div className="flex flex-wrap justify-content-end gap-2">
                  <Button
                    type="button"
                    label="Back to Dashboard"
                    severity="secondary"
                    outlined
                    rounded
                    onClick={() => navigate(RouterAddress.PROVIDER_HOME)}
                  />
                  {!isAdminUser ? (
                    <Button
                      type="button"
                      label="Billing"
                      severity="secondary"
                      outlined
                      rounded
                      onClick={() => navigate(`${RouterAddress.BILLING}?tab=subscription`)}
                    />
                  ) : null}
                  <Button
                    type="button"
                    label="Restore Resident"
                    severity="warning"
                    rounded
                    loading={restoringArchivedResident || checkingArchivedResidentRestore}
                    disabled={!canRestoreArchivedResident && !isAdminUser}
                    onClick={() => { void restoreArchivedResident(); }}
                  />
                </div>
              </div>
            </Dialog>
          </div>
        ) : (
          <div className="flex flex-column gap-3" style={{ width: "100%" }}>
            <div className="flex flex-wrap justify-content-between align-items-start gap-3">
              <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                {renderSurfaceHeader(
                  residentProfileData ? `${residentProfileData.firstName} ${residentProfileData.lastName} Dashboard` : "Resident Dashboard",
                  "",
                  "pi pi-user",
                )}
              </div>
              <div className="flex-shrink-0" style={{ marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.7rem" }}>
                {profileFooterTemplate()}
              </div>
            </div>

            <div style={{ ...appSectionCardStyle, overflow: "hidden", background: "#ffffff" }}>
              <div style={{ padding: "1rem 1.15rem" }}>
                <TabView
                  className="resident-workspace-tabs"
                  scrollable
                  activeIndex={activeWorkspaceTabIndex}
                  onTabChange={(e) => {
                    const nextTab = visibleWorkspaceTabs[e.index] || "profile";
                    if (nextTab === "care-plan" || nextTab === "assessment" || nextTab === "delegation-forms" || nextTab === "emar") {
                      setRequestedWorkspaceTabs((previousTabs) => (
                        previousTabs.includes(nextTab)
                          ? previousTabs
                          : [...previousTabs, nextTab]
                      ));
                    }
                    const nextParams = new URLSearchParams(searchParams);
                    nextParams.set("tab", nextTab);
                    setSearchParams(nextParams, { replace: true });
                  }}
                >
                  {orderResidentWorkspaceTabPanels(<>
                  {doesCarePlanExist ? <TabPanel key="profile" header="Profile">
                    <div className="flex flex-column gap-2">
                      <div style={{ ...compactProfilePanelStyle, display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.8rem", flexWrap: "wrap" }}>
                        <div>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Profile Sync</div>
                          <div style={appMutedTextStyle}>Fill missing primary physician and pharmacy details using care plan data. If no data is available, fill manually.</div>
                          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                            Last synced: {clinicalContactsLastSyncedAt}
                          </div>
                        </div>
                        <div className="flex flex-wrap justify-content-end gap-2">
                          {/* <Button
                            type="button"
                            label="Emergency Packet"
                            icon="pi pi-briefcase"
                            severity="warning"
                            rounded
                            outlined
                            disabled={!residentId || generatingEmergencyPacketDocument}
                            onClick={() => {
                              clearEmergencyPolstUploadSelection();
                              setSelectedEmergencyPacketSections(
                                visibleEmergencyPacketSectionOptions
                                  .map((option) => option.value)
                                  .filter((value) => emergencyPacketSectionMeta[value]?.enabled)
                              );
                              setShowEmergencyPacketDialog(true);
                            }}
                          /> */}
                          <Button
                            type="button"
                            label="Generate Face Sheet"
                            icon="pi pi-file-word"
                            severity="secondary"
                            rounded
                            outlined
                            loading={generatingResidentProfileReportDocument}
                            disabled={!residentId || generatingResidentProfileReportDocument}
                            onClick={showGenerateResidentProfileReportConfirmation}
                          />
                          <Button
                            type="button"
                            label="Sync Profile from Care Plan"
                            icon="pi pi-sync"
                            severity="warning"
                            rounded
                            loading={syncingClinicalContactsFromCarePlan}
                            disabled={!residentId || !hasCarePlanClinicalContactSyncSource || syncingClinicalContactsFromCarePlan}
                            onClick={() => {
                              void syncResidentClinicalContactsFromCarePlan({ showToast: true });
                            }}
                          />
                          {residentProfileData?.active !== false && canArchiveResident ? (
                            <Button
                              type="button"
                              label="Archive Resident"
                              icon="pi pi-archive"
                              severity="danger"
                              rounded
                              outlined
                              onClick={() => { void onDeactivateProfile(); }}
                            />
                          ) : null}
                        </div>
                      </div>
                      <div className="grid">
                        <div className="col-12 lg:col-6">
                          <div className="resident-feature-card" style={compactProfileCardStyle}>
                            <div className="resident-feature-head" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div>
                                <div className="flex align-items-center gap-2">
                                  <span className="resident-feature-icon pi pi-id-card" />
                                  <span className="resident-feature-badge resident-feature-badge-primary">Resident Profile</span>
                                  <span className="resident-feature-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}>Source: Resident Profile</span>
                                </div>
                                <div className="mt-2" style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>Resident Details</div>
                              </div>
                              <Button
                                type="button"
                                label="Edit"
                                icon="pi pi-pencil"
                                severity="warning"
                                rounded
                                outlined
                                onClick={() => {
                                  setProfileFormData(buildProfileFormState());
                                  setSelectedResidentProfileImageFile(null);
                                  setIsEditingProfileSection(true);
                                }}
                              />
                            </div>
                            <div className="mt-3 flex flex-column align-items-center text-center" style={{ gap: "1rem" }}>
                              <div className="flex justify-content-center">
                                {residentProfileImageUrl ? (
                                  <img
                                    src={residentProfileImageUrl}
                                    alt={`${[resolvedProfileSnapshot.firstName, resolvedProfileSnapshot.lastName].filter(Boolean).join(" ") || "Resident"} profile`}
                                    width="128"
                                    height="128"
                                    onError={(event) => {
                                      refreshResidentProfileImageOnError(String((event.currentTarget as HTMLImageElement)?.src || residentProfileImageUrl));
                                    }}
                                    style={{
                                      objectFit: "cover",
                                      border: "3px solid #f2c265",
                                      borderRadius: "18px",
                                      display: "block",
                                      boxShadow: "0 10px 24px rgba(24, 93, 162, 0.12)",
                                      width: "128px",
                                      height: "128px",
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="flex align-items-center justify-content-center"
                                    style={{
                                      width: "128px",
                                      height: "128px",
                                      borderRadius: "18px",
                                      background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)",
                                      border: "2px solid #fde68a",
                                      color: "#9a3412",
                                      fontWeight: 700,
                                      fontSize: "1.75rem",
                                    }}
                                  >
                                    {[resolvedProfileSnapshot.firstName, resolvedProfileSnapshot.lastName]
                                      .filter(Boolean)
                                      .join(" ")
                                      .charAt(0) || "R"}
                                  </div>
                                )}
                              </div>
                              <div style={{ width: "100%", maxWidth: "520px", display: "flex", flexDirection: "column", gap: "0.75rem", alignItems: "center" }}>
                                <div>
                                  <div style={{ color: "#0f172a", fontSize: "1.05rem", fontWeight: 800, lineHeight: 1.3 }}>
                                    {[resolvedProfileSnapshot.firstName, resolvedProfileSnapshot.lastName].filter(Boolean).join(" ") || "Not added"}
                                  </div>
                                  <div className="mt-1" style={{ color: residentProfileData?.active === false ? "#b91c1c" : "#15803d", fontWeight: 700, fontSize: "0.88rem" }}>
                                    {residentProfileData?.active === false ? "Inactive" : "Active"}
                                  </div>
                                </div>
                                <div className="grid justify-content-center" style={compactProfileGridStyle}>
                                  {[
                                    { label: "Age", value: resolvedProfileSnapshot.age || "Not added" },
                                    { label: "Sex", value: resolvedProfileSnapshot.gender ? capitalizeFirstLetter(resolvedProfileSnapshot.gender) : "Not added" },
                                    { label: "Date of Birth", value: resolvedProfileSnapshot.dateOfBirth || "Not added" },
                                    { label: "Medicaid", value: typeof resolvedProfileSnapshot.isOnMedicaid === "boolean" ? (resolvedProfileSnapshot.isOnMedicaid ? "Yes" : "No") : "Not added" },
                                    { label: "Entry Status", value: String(residentProfileData?.residentEntryStatus || "in").trim().toLowerCase() === "out" ? "Out" : "In" },
                                  ].map((item) => (
                                    <div key={item.label} className="col-12 md:col-6">
                                      <div style={{ ...compactProfileFieldCardStyle, textAlign: "center" as const }}>
                                        <div style={compactProfileLabelStyle}>{item.label}</div>
                                        <div style={compactProfileValueStyle}>{item.value}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 lg:col-6">
                          <div className="resident-feature-card" style={compactProfileCardStyle}>
                            <div className="resident-feature-head" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div>
                                <div className="flex align-items-center gap-2">
                                  <span className="resident-feature-icon pi pi-shield" />
                                  <span className="resident-feature-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}>Source: Resident Profile</span>
                                </div>
                                <div className="mt-2" style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>Insurance</div>
                              </div>
                              <Button
                                type="button"
                                label="Edit"
                                icon="pi pi-pencil"
                                severity="warning"
                                rounded
                                outlined
                                onClick={() => {
                                  setProfileFormData(buildProfileFormState());
                                  setSelectedResidentProfileImageFile(null);
                                  setIsEditingProfileSection(true);
                                }}
                              />
                            </div>
                            <div className="grid mt-2" style={compactProfileGridStyle}>
                              {[
                                { label: "Medicaid", value: resolvedProfileSnapshot.isOnMedicaid ? "Yes" : "No", col: "col-12 md:col-4" },
                                { label: "Medicaid Client ID", value: resolvedProfileSnapshot.medicaidClientId || "Not added", col: "col-12 md:col-4" },
                                { label: "ProviderOne", value: resolvedProfileSnapshot.providerOneNumber || "Not added", col: "col-12 md:col-4" },
                                { label: "Primary Insurance", value: resolvedProfileSnapshot.primaryInsuranceName || "Not added", col: "col-12 md:col-6" },
                                { label: "Secondary Insurance", value: resolvedProfileSnapshot.secondaryInsuranceName || "Not added", col: "col-12 md:col-6" },
                                { label: "Primary Details", value: primaryInsuranceLines.length > 0 ? primaryInsuranceLines.join(" • ") : "Add carrier, member ID, subscriber, and phone details.", col: "col-12" },
                                { label: "Secondary Details", value: secondaryInsuranceLines.length > 0 ? secondaryInsuranceLines.join(" • ") : "Secondary insurance not added.", col: "col-12" },
                                { label: "Notes", value: resolvedProfileSnapshot.insuranceNotes || "Not added", col: "col-12" },
                              ].map((item) => (
                                <div key={`insurance-card-${item.label}`} className={item.col}>
                                  <div style={compactProfileFieldCardStyle}>
                                    <div style={compactProfileLabelStyle}>{item.label}</div>
                                    <div style={{ ...compactProfileValueStyle, whiteSpace: item.col === "col-12" ? "pre-wrap" : "normal" }}>{item.value}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="col-12 lg:col-6">
                          <div className="resident-feature-card" style={compactProfileCardStyle}>
                            <div className="resident-feature-head" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div>
                                <div className="flex align-items-center gap-2">
                                  <span className="resident-feature-icon pi pi-list-check" />
                                  <span className="resident-feature-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}>Source: Care Plan - Medication Management</span>
                                </div>
                                <div className="mt-2 flex flex-wrap align-items-center gap-2">
                                  <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>Medical Diagnosis</span>
                                  <Tag
                                    value={medicationDiagnosisIsCurrent ? "Current" : "Needs refresh"}
                                    severity={medicationDiagnosisIsCurrent ? "success" : "warning"}
                                  />
                                </div>
                                <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                                  Last synced: {medicationDiagnosisLastSyncedAt}
                                </div>
                              </div>
                              {medicationDiagnosisSyncRequired ? (
                                <Button
                                  type="button"
                                  label="Sync"
                                  icon="pi pi-sync"
                                  severity="warning"
                                  rounded
                                  outlined
                                  loading={syncingMedicationDiagnosisFromCarePlan}
                                  disabled={!residentId || !carePlanMedicationManagementData || syncingMedicationDiagnosisFromCarePlan}
                                  onClick={() => {
                                    void syncResidentMedicationDiagnosisFromCarePlan({ showToast: true, forceOverwrite: true });
                                  }}
                                />
                              ) : null}
                            </div>
                            <div className="grid mt-2" style={compactProfileGridStyle}>
                              <div className="col-12">
                                <div style={compactProfileFieldCardStyle}>
                                  <div style={compactProfileLabelStyle}>Diagnosis Details</div>
                                  <div style={{ ...compactProfileValueStyle, whiteSpace: "pre-wrap" }}>
                                    {visibleMedicationDiagnosisText || "No medical diagnosis details added yet."}
                                  </div>
                                  {hasMoreMedicationDiagnosisLines ? (
                                    <div className="mt-2">
                                      <Button
                                        type="button"
                                        label={showFullMedicationDiagnosisDetails ? "Show less" : "Show more"}
                                        severity="secondary"
                                        text
                                        size="small"
                                        onClick={() => setShowFullMedicationDiagnosisDetails((current) => !current)}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 lg:col-6">
                          <div className="resident-feature-card" style={compactProfileCardStyle}>
                            <div className="resident-feature-head" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div>
                                <div className="flex align-items-center gap-2">
                                  <span className="resident-feature-icon pi pi-exclamation-triangle" />
                                  <span className="resident-feature-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}>Source: Care Plan - Medication Management</span>
                                </div>
                                <div className="mt-2" style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>Allergies</div>
                              </div>
                              <Button
                                type="button"
                                label="Edit"
                                icon="pi pi-pencil"
                                severity="warning"
                                rounded
                                outlined
                                onClick={openCarePlanBasicsDialog}
                              />
                            </div>
                            <div className="grid mt-2" style={compactProfileGridStyle}>
                              <div className="col-12">
                                <div style={compactProfileFieldCardStyle}>
                                  <div className="flex flex-wrap align-items-center gap-2">
                                    <span style={compactProfileLabelStyle}>Any Allergies</span>
                                    <Tag
                                      value={typeof resolvedProfileSnapshot.anyAllergies === "boolean"
                                        ? (resolvedProfileSnapshot.anyAllergies ? "Yes" : "No")
                                        : "Not added"}
                                      severity={resolvedProfileSnapshot.anyAllergies ? "danger" : "success"}
                                    />
                                  </div>
                                </div>
                              </div>
                              {[
                                {
                                  label: "Allergies Described",
                                  value: resolvedProfileSnapshot.allergiesDescribed || "Not added",
                                  col: "col-12",
                                },
                              ].map((item) => (
                                <div key={`allergy-card-${item.label}`} className={item.col}>
                                  <div style={compactProfileFieldCardStyle}>
                                    <div style={compactProfileLabelStyle}>{item.label}</div>
                                    <div style={{ ...compactProfileValueStyle, whiteSpace: item.col === "col-12" ? "pre-wrap" : "normal" }}>{item.value}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="col-12 lg:col-6">
                          <div className="resident-feature-card" style={compactProfileCardStyle}>
                            <div className="resident-feature-head" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div>
                                <div className="flex align-items-center gap-2">
                                  <span className="resident-feature-icon pi pi-heart-fill" />
                                  <span className="resident-feature-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}>Source: Care Plan - Responsible Parties</span>
                                </div>
                                <div className="mt-2" style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>Primary Physician</div>
                              </div>
                              <Button
                                type="button"
                                label="Edit"
                                icon="pi pi-pencil"
                                severity="warning"
                                rounded
                                outlined
                                onClick={openCarePlanContactsDialog}
                              />
                            </div>
                            <div className="grid mt-2" style={compactProfileGridStyle}>
                              {[
                                { label: "Name", value: resolvedProfileSnapshot.primaryPhysicianName || "Not added", col: "col-12" },
                                { label: "Clinic", value: resolvedProfileSnapshot.primaryPhysicianClinic || "Not added", col: "col-12" },
                                { label: "Phone", value: resolvedProfileSnapshot.primaryPhysicianPhoneNumber || "Not added", col: "col-12 md:col-6" },
                                { label: "Fax", value: resolvedProfileSnapshot.primaryPhysicianFaxNumber || "Not added", col: "col-12 md:col-6" },
                                { label: "Email", value: resolvedProfileSnapshot.primaryPhysicianEmail || "Not added", col: "col-12" },
                                { label: "Address", value: resolvedProfileSnapshot.primaryPhysicianAddress || "Not added", col: "col-12" },
                              ].map((item) => (
                                <div key={`physician-card-${item.label}`} className={item.col}>
                                  <div style={compactProfileFieldCardStyle}>
                                    <div style={compactProfileLabelStyle}>{item.label}</div>
                                    <div style={{ ...compactProfileValueStyle, whiteSpace: item.label === "Address" ? "pre-wrap" : "normal" }}>{item.value}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="col-12 lg:col-6">
                          <div className="resident-feature-card" style={compactProfileCardStyle}>
                            <div className="resident-feature-head" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div>
                                <div className="flex align-items-center gap-2">
                                  <span className="resident-feature-icon pi pi-shopping-bag" />
                                  <span className="resident-feature-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}>Source: Care Plan - Responsible Parties</span>
                                </div>
                                <div className="mt-2" style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>Primary Pharmacy</div>
                              </div>
                              <Button
                                type="button"
                                label="Edit"
                                icon="pi pi-pencil"
                                severity="warning"
                                rounded
                                outlined
                                onClick={openCarePlanContactsDialog}
                              />
                            </div>
                            <div className="grid mt-2" style={compactProfileGridStyle}>
                              {[
                                { label: "Name", value: resolvedProfileSnapshot.pharmacyName || "Not added", col: "col-12" },
                                { label: "Phone", value: resolvedProfileSnapshot.pharmacyPhoneNumber || "Not added", col: "col-12 md:col-6" },
                                { label: "Fax", value: resolvedProfileSnapshot.pharmacyFaxNumber || "Not added", col: "col-12 md:col-6" },
                                { label: "Email", value: resolvedProfileSnapshot.pharmacyEmail || "Not added", col: "col-12" },
                                { label: "Address", value: resolvedProfileSnapshot.pharmacyAddress || "Not added", col: "col-12" },
                              ].map((item) => (
                                <div key={`pharmacy-card-${item.label}`} className={item.col}>
                                  <div style={compactProfileFieldCardStyle}>
                                    <div style={compactProfileLabelStyle}>{item.label}</div>
                                    <div style={{ ...compactProfileValueStyle, whiteSpace: item.label === "Address" ? "pre-wrap" : "normal" }}>{item.value}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="col-12 lg:col-6">
                          <div className="resident-feature-card" style={compactProfileCardStyle}>
                            <div className="resident-feature-head" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div>
                                <div className="flex align-items-center gap-2">
                                  <span className="resident-feature-icon pi pi-users" />
                                  <span className="resident-feature-badge" style={{ background: "#eff6ff", color: "#1d4ed8" }}>Source: Care Plan - Responsible Parties</span>
                                </div>
                                <div className="mt-2" style={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>Point of Contacts</div>
                              </div>
                              <Button
                                type="button"
                                label="Edit"
                                icon="pi pi-pencil"
                                severity="warning"
                                rounded
                                outlined
                                onClick={openCarePlanContactsDialog}
                              />
                            </div>
                            <div className="mt-2">
                              {collateralContacts.length > 0 ? (
                                <div className="grid" style={compactProfileGridStyle}>
                                  {collateralContacts.slice(0, 4).map((contact: any, index: number) => (
                                    <div key={`${contact?.fullName || "contact"}-${index}`} className="col-12">
                                      <div style={compactProfileFieldCardStyle}>
                                        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.25rem", lineHeight: 1.3 }}>{contact?.fullName || "Unnamed contact"}</div>
                                        <div className="grid" style={compactProfileGridStyle}>
                                          <div className="col-12 md:col-6"><span style={compactProfileLabelStyle}>Relationship:</span> <span style={compactProfileValueStyle}>{contact?.relationship || "Not added"}</span></div>
                                          <div className="col-12 md:col-6"><span style={compactProfileLabelStyle}>Preferred:</span> <span style={compactProfileValueStyle}>{contact?.preferredContactMethod || "Not added"}</span></div>
                                          <div className="col-12 md:col-6"><span style={compactProfileLabelStyle}>Phone:</span> <span style={compactProfileValueStyle}>{contact?.phoneNumber || "Not added"}</span></div>
                                          <div className="col-12 md:col-6"><span style={compactProfileLabelStyle}>Email:</span> <span style={compactProfileValueStyle}>{contact?.email || "Not added"}</span></div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ color: "#475569" }}>
                                  No care plan contacts added yet.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {residentProfileData?.active !== false && canArchiveResident ? (
                        <div className="flex justify-content-end" style={{ width: "100%" }}>
                          <Button
                            type="button"
                            label="Archive Resident"
                            icon="pi pi-archive"
                            severity="danger"
                            rounded
                            outlined
                            onClick={() => { void onDeactivateProfile(); }}
                          />
                        </div>
                      ) : null}
                    </div>
                  </TabPanel> : null}

                  <TabPanel key="care-plan" header="Care Plan (DSHS format)">
                    <div className="grid justify-content-center">
                      <div className="col-12 lg:col-8 xl:col-7">
                        <div className="resident-feature-card" style={{ textAlign: "center" }}>
                          <div className="resident-feature-copy" style={{ fontSize: "1rem", lineHeight: 1.65 }}>
                            <div>
                              {doesCarePlanExist
                                ? "Review and update the resident's DSHS-format care plan as their needs or services change."
                                : "Create a DSHS-format care plan using the resident's latest assessment."}
                            </div>
                            {!doesCarePlanExist ? (
                              <div className="mt-2">
                                <strong>Or</strong>, if you already have an assessment, use this workspace to create a DSHS-format care plan from it.
                              </div>
                            ) : null}
                          </div>
                          <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.85rem 0.95rem", textAlign: "center", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                              {doesCarePlanExist ? "Care plan available" : "Care plan not started yet"}
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2 justify-content-center" style={{ width: "100%", justifyContent: "center" }}>
                            <Button
                              type="button"
                              label={doesCarePlanExist ? "Update Care Plan" : "Create Care Plan"}
                              severity="warning"
                              rounded
                              outlined
                              onClick={() => navigate(getResidentCarePlanPath(homeId!, residentId!))}
                            />
                            {doesCarePlanExist ? (
                              <Button type="button" label="Search Care Plan" severity="warning" rounded outlined onClick={() => navigate(RouterAddress.getProviderResidentCarePlanPagePath(residentId!, String(homeId || "")))} />
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabPanel>

                  <TabPanel key="emar" header="eMAR">
                    <div className="grid justify-content-center">
                      <div className="col-12 lg:col-8 xl:col-7">
                        <div className="resident-feature-card" style={{ textAlign: "center" }}>
                          <div className="resident-feature-copy" style={{ fontSize: "1rem", lineHeight: 1.65 }}>
                            Set up eMAR by uploading a pharmacy MAR sheet, importing medications from the care plan, or adding medications manually. Then review active medications, chart doses, track vital items, and generate DSHS-format reports at any time.
                          </div>
                          <div className="mt-3 flex justify-content-center" style={{ width: "100%", justifyContent: "center" }}>
                            <Button
                              type="button"
                              label="Open eMAR"
                              severity="warning"
                              rounded
                              outlined
                              onClick={() => runSubscribedProviderAction("eMAR", () => {
                                navigate(RouterAddress.getProviderResidentEmarPagePath(residentId!, String(homeId || "")));
                              })}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid" style={{ display: "none" }}>
                      <div className="col-12 lg:col-7">
                        <div className="resident-feature-card">
                          <div className="resident-feature-head">
                            <span className="resident-feature-icon pi pi-plus-circle" />
                            <span className="resident-feature-badge resident-feature-badge-primary">eMAR</span>
                          </div>
                          <div className="resident-feature-title">Today's Med Pass</div>
                          <div className="resident-feature-copy">
                            Review today&apos;s medication pass progress, attention items, and pending charting before opening the full eMAR workspace.
                          </div>
                          <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                            <div className="flex flex-wrap align-items-center gap-2">
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.96rem" }}>
                                {loadingResidentEmarTodaySummary
                                  ? "Loading today's summary..."
                                  : residentEmarTodaySummary?.statusLabel || "No eMAR summary yet"}
                              </div>
                              {residentEmarTodaySummary?.statusLabel ? (
                                <Tag value={residentEmarTodaySummary.statusLabel} severity={residentEmarTodaySummary?.severity || "secondary"} />
                              ) : null}
                            </div>
                            <div className="mt-2" style={{ display: "none" }}>
                              {loadingResidentEmarTodaySummary
                                ? "Checking today's medication pass activity."
                                : residentEmarTodaySummary
                                  ? `${residentEmarTodaySummary.completedCount || 0} completed, ${residentEmarTodaySummary.pendingCount || 0} pending, ${residentEmarTodaySummary.dueNowCount || 0} due now${(residentEmarTodaySummary.missedCount || 0) > 0 ? `, ${residentEmarTodaySummary.missedCount || 0} missed` : ""}${(residentEmarTodaySummary.unscheduledNonPrnCount || 0) > 0 ? `, and ${residentEmarTodaySummary.unscheduledNonPrnCount || 0} unscheduled` : ""}${(residentEmarTodaySummary.overdueCount || 0) > 0 ? `, with ${residentEmarTodaySummary.overdueCount || 0} overdue` : ""} today.`
                                  : "Open the eMAR workspace to review medications and start charting today's med pass."}
                            </div>
                            {!loadingResidentEmarTodaySummary && residentEmarTodaySummary ? (
                              <div className="mt-3 flex flex-wrap justify-content-center gap-2">
                                <Tag value={`${residentEmarTodaySummary.activeMedicationCount || 0} active meds`} severity="info" />
                                {(residentEmarTodaySummary.completedCount || 0) > 0 ? <Tag value={`${residentEmarTodaySummary.completedCount || 0} done`} severity="success" /> : null}
                                {(residentEmarTodaySummary.pendingCount || 0) > 0 ? <Tag value={`${residentEmarTodaySummary.pendingCount || 0} pending`} severity="warning" /> : null}
                                {(residentEmarTodaySummary.dueNowCount || 0) > 0 ? <Tag value={`${residentEmarTodaySummary.dueNowCount || 0} due now`} severity="warning" /> : null}
                                {(residentEmarTodaySummary.missedCount || 0) > 0 ? <Tag value={`${residentEmarTodaySummary.missedCount || 0} missed`} severity="danger" /> : null}
                                {(residentEmarTodaySummary.unscheduledNonPrnCount || 0) > 0 ? <Tag value={`${residentEmarTodaySummary.unscheduledNonPrnCount || 0} unscheduled`} severity="warning" /> : null}
                                {(residentEmarTodaySummary.overdueCount || 0) > 0 ? <Tag value={`${residentEmarTodaySummary.overdueCount || 0} overdue`} severity="danger" /> : null}
                                {(residentEmarTodaySummary.exceptionCount || 0) > 0 ? <Tag value={`${residentEmarTodaySummary.exceptionCount || 0} exceptions`} severity="danger" /> : null}
                              </div>
                            ) : null}
                          </div>
                          <div className="mt-3 flex flex-wrap justify-content-center gap-2">
                            <Button
                              type="button"
                              label="Open eMAR"
                              icon="pi pi-plus-circle"
                              severity="warning"
                              rounded
                              outlined
                              onClick={() => runSubscribedProviderAction("eMAR", () => {
                                navigate(RouterAddress.getProviderResidentEmarPagePath(residentId!, String(homeId || "")));
                              })}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-12 lg:col-5">
                        <div className="resident-feature-card">
                          <div className="resident-feature-head">
                            <span className="resident-feature-icon pi pi-bell" />
                            <span className="resident-feature-badge resident-feature-badge-primary">Attention</span>
                          </div>
                          <div className="resident-feature-title">Today's Follow-Up</div>
                          <div className="resident-feature-copy">
                            Keep an eye on medication exceptions, PRN follow-up, and unresolved charting items that still need review today.
                          </div>
                          <div className="mt-3 flex flex-column gap-2">
                            <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                {loadingResidentEmarTodaySummary
                                  ? "Loading follow-up summary..."
                                  : residentEmarTodaySummary
                                    ? `${residentEmarTodaySummary.unreviewedExceptionCount || 0} unreviewed exception${(residentEmarTodaySummary.unreviewedExceptionCount || 0) === 1 ? "" : "s"}`
                                    : "No eMAR follow-up summary yet"}
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                {loadingResidentEmarTodaySummary
                                  ? "Checking outstanding exception review items."
                                  : residentEmarTodaySummary
                                    ? `${residentEmarTodaySummary.prnReviewCount || 0} PRN review, ${residentEmarTodaySummary.multipleUpdateCount || 0} corrected/updated again.`
                                    : "Open eMAR to review PRN notes, exceptions, and corrections."}
                              </div>
                            </div>
                            <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                {(Array.isArray(residentEmarTodaySummary?.attentionBadges) && residentEmarTodaySummary.attentionBadges.length > 0)
                                  ? "Current attention badges"
                                  : "No current attention badges"}
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {Array.isArray(residentEmarTodaySummary?.attentionBadges) && residentEmarTodaySummary.attentionBadges.length > 0 ? residentEmarTodaySummary.attentionBadges.map((badge: any, index: number) => (
                                  <Tag key={`${badge?.label || "badge"}-${index}`} value={String(badge?.label || "").trim() || "Attention"} severity={badge?.severity || "warning"} />
                                )) : (
                                  <Tag value="No follow-up flags" severity="success" />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabPanel>

                  <TabPanel key="assessment" header="Client Assessment + CarePlan (Non-DSHS format)">
                    <div className="grid justify-content-center">
                      <div className="col-12 lg:col-8 xl:col-7">
                        <div className="resident-feature-card" style={{ textAlign: "center" }}>
                          <div className="resident-feature-copy" style={{ fontSize: "1rem", lineHeight: 1.65 }}>
                            <div>
                              {assessmentFormsSummary.length > 0
                                ? "Review and update the resident's non-DSHS assessment and care plan as their needs or services change."
                                : "Create a non-DSHS assessment and care plan using the resident's records, notes, and supporting documents."}
                            </div>
                            {!assessmentFormsSummary.length ? (
                              <div className="mt-2">
                                <strong>Or</strong>, if you already have an assessment, use this workspace to create a non-DSHS-format care plan from it.
                              </div>
                            ) : null}
                          </div>
                          <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.85rem 0.95rem", textAlign: "center", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <div className="flex flex-wrap align-items-center justify-content-center gap-2" style={{ justifyContent: "center" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.96rem" }}>
                                {assessmentFormsSummary.length > 0 ? "Assessment available" : "Assessment not started yet"}
                              </div>
                            </div>
                            <div className="mt-2" style={{ display: "none" }}>
                              {loadingAssessmentDelegationSummary
                                ? "Loading assessment summary..."
                                : assessmentFormsSummary.length === 0
                                  ? "No assessment has been started yet for this resident."
                                  : activeAssessmentSummaryForm?.formId
                                    ? `Showing version V${activeAssessmentSummaryForm?.version || assessmentFormsSummary.length}${activeAssessmentSummaryForm?.modifiedAt ? ` • Updated ${new Date(activeAssessmentSummaryForm.modifiedAt).toLocaleDateString()}` : ""}`
                                    : "A saved assessment is available for review."}
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap justify-content-center gap-2" style={{ width: "100%", justifyContent: "center" }}>
                            <Button
                              type="button"
                              label={assessmentFormsSummary.length > 0 ? "Update Assessment" : "Create Assessment"}
                              severity="warning"
                              rounded
                              outlined
                              onClick={() => handleAssessmentDelegationAccess(
                                latestAssessmentSummaryForm?.formId
                                  ? `${RouterAddress.getProviderResidentAssessmentPagePath(homeId!, residentId!)}?formId=${encodeURIComponent(String(latestAssessmentSummaryForm.formId))}`
                                  : RouterAddress.getProviderResidentAssessmentPagePath(homeId!, residentId!)
                              )}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-12 lg:col-5" style={{ display: "none" }}>
                        <div className="resident-feature-card">
                          <div className="resident-feature-head">
                            <span className="resident-feature-icon pi pi-list" />
                            <span className="resident-feature-badge resident-feature-badge-primary">Latest Version</span>
                          </div>
                          <div className="resident-feature-title">Selected Assessment Details</div>
                          <div className="resident-feature-copy">
                            {activeAssessmentSummaryForm
                              ? "This summary follows the selected assessment version when a formId is present in the page URL."
                              : "Open the assessment workspace to review the latest saved details here."}
                          </div>
                          <div className="mt-3 flex flex-column gap-2">
                            <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                {activeAssessmentSummaryForm?.formId
                                  ? `Version V${activeAssessmentSummaryForm?.version || assessmentFormsSummary.length}`
                                  : "No assessment selected"}
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                {loadingActiveAssessmentStepRecords
                                  ? "Loading saved assessment details..."
                                  : activeAssessmentStep01?.assessmentDate
                                    ? `Assessment date ${activeAssessmentStep01.assessmentDate}`
                                    : activeAssessmentSummaryForm?.createdAt
                                      ? `Created ${new Date(activeAssessmentSummaryForm.createdAt).toLocaleDateString()}`
                                      : "The latest or requested assessment version will appear here once one exists."}
                              </div>
                            </div>
                            <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                {loadingActiveAssessmentStepRecords
                                  ? "Loading section summary..."
                                  : activeAssessmentStepCount > 0
                                    ? `${activeAssessmentStepCount} sections already saved`
                                    : "No saved sections yet"}
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                {loadingActiveAssessmentStepRecords
                                  ? "Checking the saved assessment sections for this version."
                                  : activeAssessmentSectionPreviewTitles.length > 0
                                    ? activeAssessmentSectionPreviewTitles.join(" • ")
                                    : "Saved assessment sections will appear here once this version has content."}
                              </div>
                            </div>
                            <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                Current Medical Diagnoses
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6, whiteSpace: "pre-line" }}>
                                {loadingActiveAssessmentStepRecords
                                  ? "Loading diagnoses..."
                                  : activeAssessmentDiagnosisLines.length > 0
                                    ? activeAssessmentDiagnosisLines.slice(0, 5).join("\n")
                                    : "No diagnoses are saved yet for this assessment version."}
                              </div>
                            </div>
                            <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                Clinical Snapshot
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                {loadingActiveAssessmentStepRecords
                                  ? "Loading resident details..."
                                  : [
                                    activeAssessmentStep01?.providerOneNumber
                                      ? `ProviderOne: ${activeAssessmentStep01.providerOneNumber}`
                                      : "",
                                    activeAssessmentStep05?.allergies
                                      ? `Allergies: ${activeAssessmentStep05.allergies}`
                                      : "",
                                    activeAssessmentStep01?.residentBirthdate
                                      ? `DOB: ${activeAssessmentStep01.residentBirthdate}`
                                      : "",
                                  ].filter(Boolean).join(" • ") || "Resident basics, allergies, and other saved clinical details will appear here once entered."}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabPanel>

                  {!isEsfResident ? <TabPanel key="delegation-forms" header="Delegation Forms">
                    <div className="grid justify-content-center">
                      <div className="col-12 lg:col-8 xl:col-7">
                        <div className="resident-feature-card" style={{ textAlign: "center" }}>
                          <div className="resident-feature-copy" style={{ fontSize: "1rem", lineHeight: 1.65 }}>
                            Create or update DSHS-format delegation forms using the resident&apos;s care plan, assessment, or your own documentation.
                          </div>
                          <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.85rem 0.95rem", textAlign: "center", display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.96rem" }}>
                              {delegationSummaryUniqueForms.length > 0 ? "Delegation forms available" : "Delegation forms not started yet"}
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap justify-content-center gap-2" style={{ width: "100%", justifyContent: "center" }}>
                            <Button type="button" label={delegationSummaryUniqueForms.length > 0 ? "Update Delegation Forms" : "Create Delegation Forms"} severity="warning" rounded outlined onClick={() => handleAssessmentDelegationAccess(RouterAddress.getProviderResidentDelegationPagePath(homeId!, residentId!))} />
                          </div>
                        </div>
                      </div>
                      <div className="col-12 lg:col-5" style={{ display: "none" }}>
                        <div className="resident-feature-card">
                          <div className="resident-feature-head">
                            <span className="resident-feature-icon pi pi-list" />
                            <span className="resident-feature-badge resident-feature-badge-primary">Existing Forms</span>
                          </div>
                          <div className="resident-feature-title">Ways To Create</div>
                          <div className="resident-feature-copy">
                            Choose the source that fits the resident&apos;s workflow and documentation you already have.
                          </div>
                          <div className="mt-3 flex flex-column gap-2">
                            <div style={{ ...appInnerCardStyle, padding: "0.85rem 0.95rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                Care Plan
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.5 }}>
                                Start from the resident&apos;s Negotiated Care Plan when that plan already captures the current support needs.
                              </div>
                            </div>
                            <div style={{ ...appInnerCardStyle, padding: "0.85rem 0.95rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                Assessment
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.5 }}>
                                Start from the latest Private Client assessment and care plan when you want delegation forms based on the resident&apos;s assessment workflow.
                              </div>
                            </div>
                            <div style={{ ...appInnerCardStyle, padding: "0.85rem 0.95rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                Create Manually
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.5 }}>
                                Start a blank delegation form when you already know exactly which form needs to be completed.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabPanel> : null}

                  {SHOW_ADL_ENTRY_POINTS ? (
                    <TabPanel key="adl-tracking" header="ADL Tracking">
                      <div className="grid justify-content-center">
                        <div className="col-12 lg:col-8 xl:col-7">
                          <div className="resident-feature-card" style={{ textAlign: "center" }}>
                            <div className="resident-feature-copy" style={{ fontSize: "1rem", lineHeight: 1.65 }}>
                              Set up ADL tasks from the resident&apos;s care plan or add them manually. Then chart daily support, review ADL activity records, and generate DSHS-format reports at any time.
                            </div>
                            <div className="mt-3 flex justify-content-center" style={{ width: "100%", justifyContent: "center" }}>
                              <Button
                                type="button"
                                label="Open ADL Tracking"
                                severity="warning"
                                rounded
                                outlined
                                onClick={() => runSubscribedProviderAction("ADL Tracking", () => {
                                  navigate(RouterAddress.getProviderResidentAdlPagePath(residentId!, String(homeId || "")));
                                })}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="grid" style={{ display: "none" }}>
                        <div className="col-12 lg:col-7">
                          <div className="resident-feature-card">
                            <div className="resident-feature-head">
                              <span className="resident-feature-icon pi pi-check-square" />
                              <span className="resident-feature-badge resident-feature-badge-primary">ADL Tracking</span>
                            </div>
                            <div className="resident-feature-title">Today's ADL Pass</div>
                            <div className="resident-feature-copy">
                              Review the resident&apos;s active ADL setup, chart today&apos;s support pass, and keep the live caregiver workflow aligned with the care plan.
                            </div>
                            <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                              <div className="flex flex-wrap align-items-center gap-2">
                                <Tag value="Provider managed" severity="warning" />
                                <Tag value="Caregiver charting" severity="info" />
                                <Tag value="Care plan guided" severity="success" />
                              </div>
                              <div className="mt-3 grid" style={{ rowGap: "0.75rem" }}>
                                <div className="col-12 md:col-6">
                                  <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>Working setup</div>
                                  <div className="mt-1" style={{ ...appMutedTextStyle, lineHeight: 1.55 }}>
                                    Build or adjust the ADL list before making it live for caregivers.
                                  </div>
                                </div>
                                <div className="col-12 md:col-6">
                                  <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>Today's pass</div>
                                  <div className="mt-1" style={{ ...appMutedTextStyle, lineHeight: 1.55 }}>
                                    Chart daily status and notes for bathing, mobility, transfers, meals, and custom ADL tasks.
                                  </div>
                                </div>
                              </div>
                              <div className="mt-3 flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  label="Open ADL Tracking"
                                  severity="warning"
                                  rounded
                                  onClick={() => runSubscribedProviderAction("ADL Tracking", () => {
                                    navigate(RouterAddress.getProviderResidentAdlPagePath(residentId!, String(homeId || "")));
                                  })}
                                />
                                <Button
                                  type="button"
                                  label="Search Care Plan"
                                  severity="warning"
                                  rounded
                                  outlined
                                  onClick={() => runSubscribedProviderAction("Care Plan Search", () => {
                                    navigate(RouterAddress.getProviderResidentCarePlanPagePath(residentId!, String(homeId || "")));
                                  })}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-12 lg:col-5">
                          <div className="resident-feature-card">
                            <div className="resident-feature-head">
                              <span className="resident-feature-icon pi pi-list-check" />
                              <span className="resident-feature-badge resident-feature-badge-primary">ADL Workflow</span>
                            </div>
                            <div className="resident-feature-title">How this works</div>
                            <div className="resident-feature-copy">
                              Keep the ADL workflow close to eMAR: one working setup, one live caregiver list, and one daily pass to chart what happened.
                            </div>
                            <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
                              <ul style={{ margin: 0, paddingLeft: "1.1rem", color: "#334155", lineHeight: 1.7 }}>
                                <li>Generate ADL tasks from the resident care plan or add custom tasks manually.</li>
                                <li>Edit or archive tasks in the ADL list before making the setup live.</li>
                                <li>Caregivers can chart only during allowed shift access and time windows.</li>
                                <li>Download the ADL report for the selected month after charting is saved.</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabPanel>
                  ) : null}

                  <TabPanel key="daily-log" header="Daily Activities Tracker">
                    <div className="flex flex-column gap-3">
                      <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                        <div style={appMutedTextStyle}>
                          Open the right tracker for behavior support, incidents, or shift notes. Each card shows the latest saved item when available.
                        </div>
                        <Button
                          type="button"
                          label="Add Previous Daily Logs"
                          icon="pi pi-upload"
                          severity="warning"
                          rounded
                          outlined
                          onClick={() => {
                            if (residentId) {
                              runSubscribedProviderAction("Daily Log Import", () => {
                                void openDailyLogImportDialog();
                              });
                            }
                          }}
                        />
                      </div>
                      <div className="grid">
                        <div className="col-12 lg:col-4">
                          <div className="resident-feature-card">
                            <div className="resident-feature-head">
                              <span className="resident-feature-icon pi pi-chart-line" />
                              <span className="resident-feature-badge resident-feature-badge-primary">Behavior Tracker</span>
                            </div>
                            <div className="resident-feature-title">Behavior Tracking</div>
                            <div className="resident-feature-copy">
                              Track behavior events, interventions, and follow-up for monthly CBHS reporting.
                            </div>
                            <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.85rem 0.95rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                {residentBehaviorLogEntries.length > 0
                                  ? `${residentBehaviorLogEntries.length} total entries • ${ongoingBehaviorLogEntryCount} ongoing`
                                  : "No entries yet"}
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                {latestBehaviorLogEntry
                                  ? `${latestBehaviorLogEntry?.behaviorTitle || "Latest behavior entry"} • ${formatBehaviorTimestamp(latestBehaviorLogEntry?.startedAt)}${latestBehaviorLogEntry?.recordedBy ? ` • By ${latestBehaviorLogEntry.recordedBy}` : ""}`
                                  : "Start a behavior tracker entry for this resident when support or monitoring begins."}
                              </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button type="button" label="Open Behavior Tracker" severity="warning" rounded outlined onClick={() => { if (residentId) { runSubscribedProviderAction("Behavior Tracking", () => { navigate(RouterAddress.getProviderResidentDailyBehaviorLogPagePath(residentId, undefined, String(homeId || ""))); }); } }} />
                            </div>
                          </div>
                        </div>

                        <div className="col-12 lg:col-4">
                          <div className="resident-feature-card">
                            <div className="resident-feature-head">
                              <span className="resident-feature-icon pi pi-exclamation-triangle" />
                              <span className="resident-feature-badge resident-feature-badge-primary">Incident Reports</span>
                            </div>
                            <div className="resident-feature-title">Incident Tracker</div>
                            <div className="resident-feature-copy">
                              Record serious or unusual events, actions taken, and who was notified.
                            </div>
                            <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.85rem 0.95rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                {String(latestIncidentReportEntry?.title || "").trim() || "No incident report saved yet"}
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                {latestIncidentReportEntry
                                  ? `${latestIncidentReportEntry?.incidentType ? `Type: ${String(latestIncidentReportEntry.incidentType).replace(/_/g, " ")}` : "Latest incident report"}${latestIncidentReportTimestamp ? ` • ${formatBehaviorTimestamp(latestIncidentReportTimestamp)}` : ""}`
                                  : "Use incident reports for unusual events, safety issues, and formal follow-up documentation."}
                              </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button type="button" label="Open Incident Reports" severity="danger" rounded outlined onClick={() => { if (residentId) { runSubscribedProviderAction("Incident Reports", () => { navigate(RouterAddress.getProviderResidentIncidentReportPagePath(residentId, undefined, String(homeId || ""))); }); } }} />
                            </div>
                          </div>
                        </div>

                        <div className="col-12 lg:col-4">
                          <div className="resident-feature-card">
                            <div className="resident-feature-head">
                              <span className="resident-feature-icon pi pi-file-edit" />
                              <span className="resident-feature-badge resident-feature-badge-primary">Progress Notes</span>
                            </div>
                            <div className="resident-feature-title">Shift Notes Tracker</div>
                            <div className="resident-feature-copy">
                              Keep a simple running note of the day, the shift, and helpful follow-up details.
                            </div>
                            <div className="mt-3" style={{ ...appInnerCardStyle, padding: "0.85rem 0.95rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                {String(latestDailyNoteEntry?.title || "").trim() || "No daily note saved yet"}
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                {latestDailyNoteEntry
                                  ? `${String(latestDailyNoteEntry?.details || latestDailyNoteEntry?.notes || "").trim().slice(0, 120) || "Latest daily note"}${latestDailyNoteTimestamp ? ` • ${formatBehaviorTimestamp(latestDailyNoteTimestamp)}` : ""}`
                                  : "Use daily notes for routine shift updates, observations, and follow-up reminders."}
                              </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button type="button" label="Open Progress Notes" severity="info" rounded outlined onClick={() => { if (residentId) { runSubscribedProviderAction("Daily Notes", () => { navigate(RouterAddress.getProviderResidentDailyNotesPagePath(residentId, undefined, String(homeId || ""))); }); } }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabPanel>

                  {SHOW_RESIDENT_SCHEDULES ? (
                    <TabPanel key="schedules" header="Schedules">
                      <ResidentSchedulesPage />
                    </TabPanel>
                  ) : null}

                  <TabPanel key="ai-tools" header="Helpful AI Tools">
                    <div className="resident-feature-card">
                      <div className="resident-feature-head">
                        <span className="resident-feature-icon pi pi-sparkles" />
                        <span className="resident-feature-badge resident-feature-badge-primary">Helpful AI Tools</span>
                      </div>
                      <div className="resident-feature-title">Helpful AI Tools</div>
                      <div className="resident-feature-copy">
                        Open one of these tools when you need a quick summary or comparison from resident binder documents.
                      </div>
                      <div className="grid mt-3">
                        <div className="col-12 md:col-6 xl:col-4">
                          <div style={{ ...appInnerCardStyle, padding: "0.9rem 1rem", height: "100%" }}>
                            <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.95rem" }}>Summarize Assessment</div>
                            <div style={{ ...appMutedTextStyle, fontSize: "0.85rem", marginTop: "0.3rem", minHeight: "2.7rem" }}>Generate a quick summary from one assessment.</div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button type="button" label="Open" severity="warning" rounded outlined size="small" data-tour="resident-summary-assessment" onClick={() => setShowStandaloneSummaryDialog(true)} />
                              {latestStandaloneSummaryJobId ? <Button type="button" label="Last Result" severity="warning" rounded outlined size="small" onClick={openLatestStandaloneSummaryResult} /> : null}
                            </div>
                          </div>
                        </div>
                        <div className="col-12 md:col-6 xl:col-4">
                          <div style={{ ...appInnerCardStyle, padding: "0.9rem 1rem", height: "100%" }}>
                            <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.95rem" }}>Compare Assessments</div>
                            <div style={{ ...appMutedTextStyle, fontSize: "0.85rem", marginTop: "0.3rem", minHeight: "2.7rem" }}>Compare two assessments to spot key changes.</div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button type="button" label="Open" severity="warning" rounded outlined size="small" data-tour="resident-compare-assessments" onClick={() => setShowStandaloneCompareDialog(true)} />
                              {latestStandaloneCompareJobId ? <Button type="button" label="Last Result" severity="warning" rounded outlined size="small" onClick={openLatestStandaloneCompareResult} /> : null}
                            </div>
                          </div>
                        </div>
                        <div className="col-12 md:col-6 xl:col-4">
                          <div style={{ ...appInnerCardStyle, padding: "0.9rem 1rem", height: "100%" }}>
                            <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.95rem" }}>Compare Care Plans</div>
                            <div style={{ ...appMutedTextStyle, fontSize: "0.85rem", marginTop: "0.3rem", minHeight: "2.7rem" }}>Review differences between two care plans.</div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button type="button" label="Open" severity="warning" rounded outlined size="small" onClick={() => setShowStandaloneCarePlanCompareDialog(true)} />
                              {latestStandaloneCarePlanCompareJobId ? <Button type="button" label="Last Result" severity="warning" rounded outlined size="small" onClick={openLatestStandaloneCarePlanCompareResult} /> : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabPanel>
                  </>)}
                </TabView>
              </div>
            </div>

            {false ? (
              <div style={{ ...appSectionCardStyle, overflow: "hidden", background: "#ffffff" }}>
                <div style={{ padding: "1rem 1.15rem" }}>
                  <div className="grid justify-content-center">
                    <div className="col-12 xl:col-10">
                      <div className="flex flex-wrap align-items-center gap-3 mb-3">
                        <div>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Resident Workspace</div>
                        </div>
                      </div>
                      <div className="grid mb-0 align-items-start">
                        <div className="col-12 xl:col-8">
                          <div className="grid mb-0">
                            <div className="col-12 md:col-6">
                              <div className="resident-feature-card">
                                <div className="resident-feature-head">
                                  <span className="resident-feature-icon pi pi-sparkles" />
                                  <span className="resident-feature-badge resident-feature-badge-primary">Care Plan</span>
                                </div>
                                <div className="resident-feature-title">Care Plan</div>
                                <div className="resident-feature-copy">
                                  <ul style={{ margin: "0.15rem 0 0", paddingLeft: "1rem" }}>
                                    <li>Create the resident negotiated care plan from the latest assessment.</li>
                                    <li>Build the Behavior and Interventions plan from the care plan draft.</li>
                                    <li>Prepare the Preliminary Service Care Plan for early planning and review.</li>
                                  </ul>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  <Button
                                    type="button"
                                    label="Open Care Plan"
                                    severity="warning"
                                    rounded
                                    outlined
                                    onClick={() => navigate(getResidentCarePlanPath(homeId!, residentId!))}
                                  />
                                  <Button
                                    type="button"
                                    label="Search Care Plan"
                                    severity="warning"
                                    rounded
                                    outlined
                                    onClick={() => navigate(RouterAddress.getProviderResidentCarePlanPagePath(residentId!, String(homeId || "")))}
                                  />
                                  <Button
                                    type="button"
                                    label="eMAR"
                                    severity="warning"
                                    rounded
                                    outlined
                                    onClick={() => runSubscribedProviderAction("eMAR", () => {
                                      navigate(RouterAddress.getProviderResidentEmarPagePath(residentId!, String(homeId || "")));
                                    })}
                                  />
                                  <Button
                                    type="button"
                                    label="ADL Tracking"
                                    severity="warning"
                                    rounded
                                    outlined
                                    onClick={() => runSubscribedProviderAction("ADL Tracking", () => {
                                      navigate(RouterAddress.getProviderResidentAdlPagePath(residentId!, String(homeId || "")));
                                    })}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12 md:col-6">
                              <div className="resident-feature-card">
                                <div className="resident-feature-head">
                                  <span className="resident-feature-icon pi pi-briefcase" />
                                  <span className="resident-feature-badge resident-feature-badge-primary">Nurse Features</span>
                                </div>
                                <div className="resident-feature-title">Assessment & Delegation</div>
                                <div className="resident-feature-copy">
                                  <ul style={{ margin: "0.15rem 0 0", paddingLeft: "1rem" }}>
                                    <li>Providers who are also Registered Nurses, can create non-medicaid resident assessment drafts from different sources:</li>
                                    <ul>
                                      <li>Resident demographics sheet</li>
                                      <li>MAR sheets, medicine lists</li>
                                      <li>Previous assessments</li>
                                      <li>Rough clinical or assessment notes</li>
                                    </ul>
                                    <li>Besides assessment this also generate Care Plan</li>
                                    <li>Manage delegation forms for this resident.</li>
                                  </ul>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  <Button
                                    type="button"
                                    label="Assessment"
                                    severity="warning"
                                    rounded
                                    outlined
                                    onClick={() => handleAssessmentDelegationAccess(RouterAddress.getProviderResidentAssessmentPagePath(homeId!, residentId!))}
                                  />
                                  <Button
                                    type="button"
                                    label="Delegation"
                                    severity="warning"
                                    rounded
                                    outlined
                                    onClick={() => handleAssessmentDelegationAccess(RouterAddress.getProviderResidentDelegationPagePath(homeId!, residentId!))}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12 md:col-6">
                              <div className="resident-feature-card">
                                <div className="resident-feature-head">
                                  <span className="resident-feature-icon pi pi-clock" />
                                  <span className="resident-feature-badge resident-feature-badge-primary">Daily Behavior Log</span>
                                </div>
                                <div className="resident-feature-title">Provider Daily Log</div>
                                <div className="resident-feature-copy">
                                  Track live behavior events, let caregivers add notes after resident access is shared, and keep a report-ready running log.
                                </div>
                                <div className="mt-3 flex flex-column gap-2">
                                  <div style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
                                    {residentBehaviorLogEntries.length > 0
                                      ? `${residentBehaviorLogEntries.length} total entries • ${ongoingBehaviorLogEntryCount} ongoing`
                                      : "No daily behavior entries logged yet for this resident."}
                                  </div>
                                  {latestBehaviorLogEntry ? (
                                    <div style={{ ...appInnerCardStyle, padding: "0.8rem 0.95rem" }}>
                                      <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                        {latestBehaviorLogEntry?.behaviorTitle || "Latest behavior entry"}
                                      </div>
                                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                        Started {formatBehaviorTimestamp(latestBehaviorLogEntry?.startedAt)}
                                        {latestBehaviorLogEntry?.recordedBy ? ` • By ${latestBehaviorLogEntry.recordedBy}` : ""}
                                      </div>
                                    </div>
                                  ) : null}
                                  <div className="flex flex-wrap gap-2 align-items-center">
                                    <Button
                                      type="button"
                                      label="Open Behavior Tracker"
                                      severity="warning"
                                      rounded
                                      outlined
                                      onClick={() => {
                                        if (residentId) {
                                          runSubscribedProviderAction("Daily Behavior Log", () => {
                                            navigate(RouterAddress.getProviderResidentDailyBehaviorLogPagePath(residentId, undefined, String(homeId || "")));
                                          });
                                        }
                                      }}
                                    />
                                    <Button
                                      type="button"
                                      label="Incident Reports"
                                      severity="danger"
                                      rounded
                                      outlined
                                      onClick={() => {
                                        if (residentId) {
                                          runSubscribedProviderAction("Incident Reports", () => {
                                            navigate(RouterAddress.getProviderResidentIncidentReportPagePath(residentId, undefined, String(homeId || "")));
                                          });
                                        }
                                      }}
                                    />
                                    <Button
                                      type="button"
                                      label="Progress Notes"
                                      severity="info"
                                      rounded
                                      outlined
                                      onClick={() => {
                                        if (residentId) {
                                          runSubscribedProviderAction("Daily Notes", () => {
                                            navigate(RouterAddress.getProviderResidentDailyNotesPagePath(residentId, undefined, String(homeId || "")));
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-12 xl:col-4">
                          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                            <div className="resident-feature-card">
                              <div className="resident-feature-head">
                                <span className="resident-feature-icon pi pi-user-edit" />
                                <span className="resident-feature-badge resident-feature-badge-primary">Profile</span>
                              </div>
                              <div className="resident-feature-title">Resident Profile</div>
                              <div className="resident-feature-copy">
                                Update the resident basics plus the shared physician and pharmacy details used across care plan, eMAR, and reports.
                              </div>
                              <div className="mt-3" style={{ ...appInnerCardStyle, padding: "1rem" }}>
                                <div className="grid">
                                  <div className="col-12 md:col-6">
                                    <label htmlFor="profileFirstName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
                                      First Name
                                    </label>
                                    <InputText
                                      id="profileFirstName"
                                      value={profileFormData?.firstName || ""}
                                      className="w-full"
                                      onChange={(e) => setProfileFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12 md:col-6">
                                    <label htmlFor="profileLastName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
                                      Last Name
                                    </label>
                                    <InputText
                                      id="profileLastName"
                                      value={profileFormData?.lastName || ""}
                                      className="w-full"
                                      onChange={(e) => setProfileFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12">
                                    <div className="flex flex-wrap gap-4">
                                      <div className="flex align-items-center gap-2">
                                        <Checkbox
                                          inputId="profileMedicaid"
                                          checked={!!profileFormData?.isOnMedicaid}
                                          onChange={(e) => setProfileFormData((prev) => ({ ...prev, isOnMedicaid: !!e.checked }))}
                                        />
                                        <label htmlFor="profileMedicaid" style={{ color: "#334155", fontWeight: 600 }}>
                                          Resident is on Medicaid
                                        </label>
                                      </div>
                                      <div className="flex align-items-center gap-2">
                                        <Checkbox
                                          inputId="profileActive"
                                          checked={profileFormData?.active !== false}
                                          onChange={(e) => setProfileFormData((prev) => ({ ...prev, active: !!e.checked }))}
                                        />
                                        <label htmlFor="profileActive" style={{ color: "#334155", fontWeight: 600 }}>
                                          Keep resident active
                                        </label>
                                      </div>
                                    </div>
                                    <div className="mt-2" style={{ color: "#64748b", fontSize: "0.82rem", lineHeight: 1.55 }}>
                                      Turn off active when the resident is discharged or moved out. Their records remain available in archived areas.
                                    </div>
                                  </div>

                                  <div className="col-12">
                                    <Divider />
                                  </div>

                                  <div className="col-12">
                                    <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Primary Physician</div>
                                  </div>
                                  <div className="col-12">
                                    <label htmlFor="profilePhysicianName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
                                      Physician Name
                                    </label>
                                    <InputText
                                      id="profilePhysicianName"
                                      value={profileFormData?.primaryPhysicianName || ""}
                                      className="w-full"
                                      onChange={(e) => setProfileFormData((prev) => ({ ...prev, primaryPhysicianName: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12">
                                    <label htmlFor="profilePhysicianClinic" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
                                      Clinic / Practice
                                    </label>
                                    <InputText
                                      id="profilePhysicianClinic"
                                      value={profileFormData?.primaryPhysicianClinic || ""}
                                      className="w-full"
                                      onChange={(e) => setProfileFormData((prev) => ({ ...prev, primaryPhysicianClinic: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12 md:col-6">
                                    <label htmlFor="profilePhysicianPhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
                                      Phone
                                    </label>
                                    <InputText
                                      id="profilePhysicianPhone"
                                      value={profileFormData?.primaryPhysicianPhoneNumber || ""}
                                      className="w-full"
                                      onChange={(e) => setProfileFormData((prev) => ({ ...prev, primaryPhysicianPhoneNumber: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12 md:col-6">
                                    <label htmlFor="profilePhysicianFax" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
                                      Fax
                                    </label>
                                    <InputText
                                      id="profilePhysicianFax"
                                      value={profileFormData?.primaryPhysicianFaxNumber || ""}
                                      className="w-full"
                                      onChange={(e) => setProfileFormData((prev) => ({ ...prev, primaryPhysicianFaxNumber: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12">
                                    <label htmlFor="profilePhysicianEmail" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
                                      Email
                                    </label>
                                    <InputText
                                      id="profilePhysicianEmail"
                                      value={profileFormData?.primaryPhysicianEmail || ""}
                                      className="w-full"
                                      onChange={(e) => setProfileFormData((prev) => ({ ...prev, primaryPhysicianEmail: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12">
                                    <label htmlFor="profilePhysicianAddress" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
                                      Address
                                    </label>
                                    <InputText
                                      id="profilePhysicianAddress"
                                      value={profileFormData?.primaryPhysicianAddress || ""}
                                      className="w-full"
                                      onChange={(e) => setProfileFormData((prev) => ({ ...prev, primaryPhysicianAddress: e.target.value }))}
                                    />
                                  </div>

                                  <div className="col-12">
                                    <Divider />
                                  </div>

                                  <div className="col-12">
                                    <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Primary Pharmacy</div>
                                  </div>
                                  <div className="col-12">
                                    <label htmlFor="profilePharmacyName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
                                      Pharmacy Name
                                    </label>
                                    <InputText
                                      id="profilePharmacyName"
                                      value={profileFormData?.pharmacyName || ""}
                                      className="w-full"
                                      onChange={(e) => setProfileFormData((prev) => ({ ...prev, pharmacyName: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12 md:col-6">
                                    <label htmlFor="profilePharmacyPhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
                                      Phone
                                    </label>
                                    <InputText
                                      id="profilePharmacyPhone"
                                      value={profileFormData?.pharmacyPhoneNumber || ""}
                                      className="w-full"
                                      onChange={(e) => setProfileFormData((prev) => ({ ...prev, pharmacyPhoneNumber: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12 md:col-6">
                                    <label htmlFor="profilePharmacyFax" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
                                      Fax
                                    </label>
                                    <InputText
                                      id="profilePharmacyFax"
                                      value={profileFormData?.pharmacyFaxNumber || ""}
                                      className="w-full"
                                      onChange={(e) => setProfileFormData((prev) => ({ ...prev, pharmacyFaxNumber: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12">
                                    <label htmlFor="profilePharmacyEmail" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
                                      Email
                                    </label>
                                    <InputText
                                      id="profilePharmacyEmail"
                                      value={profileFormData?.pharmacyEmail || ""}
                                      className="w-full"
                                      onChange={(e) => setProfileFormData((prev) => ({ ...prev, pharmacyEmail: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12">
                                    <label htmlFor="profilePharmacyAddress" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
                                      Address
                                    </label>
                                    <InputText
                                      id="profilePharmacyAddress"
                                      value={profileFormData?.pharmacyAddress || ""}
                                      className="w-full"
                                      onChange={(e) => setProfileFormData((prev) => ({ ...prev, pharmacyAddress: e.target.value }))}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="mt-3" style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                                <div style={{ ...appInnerCardStyle, padding: "0.85rem 0.95rem" }}>
                                  <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                    {residentProfileData?.primaryPhysicianName || "Primary physician not added"}
                                  </div>
                                  <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                    {physicianContactLines.length > 0 ? physicianContactLines.join(" • ") : "Add physician phone, fax, email, clinic, and address once for all resident reports."}
                                  </div>
                                </div>
                                <div style={{ ...appInnerCardStyle, padding: "0.85rem 0.95rem" }}>
                                  <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>
                                    {residentProfileData?.pharmacyName || "Primary pharmacy not added"}
                                  </div>
                                  <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.6 }}>
                                    {pharmacyContactLines.length > 0 ? pharmacyContactLines.join(" • ") : "Add pharmacy phone, fax, email, and address once for MAR and eMAR paperwork."}
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    type="button"
                                    label={savingProfileSection ? "Saving..." : "Save Profile"}
                                    icon="pi pi-save"
                                    severity="warning"
                                    rounded
                                    onClick={saveProfileSection}
                                    loading={savingProfileSection}
                                    disabled={savingProfileSection}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="resident-feature-card">
                              <div className="resident-feature-head">
                                <span className="resident-feature-icon pi pi-sliders-h" />
                                <span className="resident-feature-badge resident-feature-badge-primary">Assessment Tools</span>
                              </div>
                              <div className="resident-feature-title">Helpful AI Tools</div>
                              <div className="resident-feature-copy">
                                Use these occasional tools to summarize one assessment or compare assessment and care plan versions from the resident binder.
                              </div>
                              <div className="mt-3" style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                                <div style={{ ...appInnerCardStyle, padding: "0.75rem 0.9rem" }}>
                                  <div className="flex flex-wrap justify-content-between align-items-start gap-2">
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.95rem" }}>Summarize Assessment</div>
                                      <div style={{ ...appMutedTextStyle, fontSize: "0.85rem", marginTop: "0.2rem" }}>
                                        Generate a quick summary from one assessment.
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <Button
                                        type="button"
                                        label="Open"
                                        severity="warning"
                                        rounded
                                        outlined
                                        size="small"
                                        data-tour="resident-summary-assessment"
                                        onClick={() => setShowStandaloneSummaryDialog(true)}
                                      />
                                      {latestStandaloneSummaryJobId ? (
                                        <Button
                                          type="button"
                                          label="Last Result"
                                          severity="warning"
                                          rounded
                                          outlined
                                          size="small"
                                          onClick={openLatestStandaloneSummaryResult}
                                        />
                                      ) : null}
                                    </div>
                                  </div>
                                </div>

                                <div style={{ ...appInnerCardStyle, padding: "0.75rem 0.9rem" }}>
                                  <div className="flex flex-wrap justify-content-between align-items-start gap-2">
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.95rem" }}>Compare Assessments</div>
                                      <div style={{ ...appMutedTextStyle, fontSize: "0.85rem", marginTop: "0.2rem" }}>
                                        Compare two assessments to spot key changes.
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <Button
                                        type="button"
                                        label="Open"
                                        severity="warning"
                                        rounded
                                        outlined
                                        size="small"
                                        data-tour="resident-compare-assessments"
                                        onClick={() => setShowStandaloneCompareDialog(true)}
                                      />
                                      {latestStandaloneCompareJobId ? (
                                        <Button
                                          type="button"
                                          label="Last Result"
                                          severity="warning"
                                          rounded
                                          outlined
                                          size="small"
                                          onClick={openLatestStandaloneCompareResult}
                                        />
                                      ) : null}
                                    </div>
                                  </div>
                                </div>

                                <div style={{ ...appInnerCardStyle, padding: "0.75rem 0.9rem" }}>
                                  <div className="flex flex-wrap justify-content-between align-items-start gap-2">
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.95rem" }}>Compare Care Plans</div>
                                      <div style={{ ...appMutedTextStyle, fontSize: "0.85rem", marginTop: "0.2rem" }}>
                                        Review differences between two care plans.
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <Button
                                        type="button"
                                        label="Open"
                                        severity="warning"
                                        rounded
                                        outlined
                                        size="small"
                                        onClick={() => setShowStandaloneCarePlanCompareDialog(true)}
                                      />
                                      {latestStandaloneCarePlanCompareJobId ? (
                                        <Button
                                          type="button"
                                          label="Last Result"
                                          severity="warning"
                                          rounded
                                          outlined
                                          size="small"
                                          onClick={openLatestStandaloneCarePlanCompareResult}
                                        />
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="resident-feature-card">
                              <div className="resident-feature-head">
                                <span className="resident-feature-icon pi pi-history" />
                                <span className="resident-feature-badge resident-feature-badge-primary">Audit Tracker</span>
                              </div>
                              <div className="resident-feature-title">Audit Tracker</div>
                              <div className="resident-feature-copy">
                                Open the care plan and behavior timelines when you need to review historical changes for audits or compliance follow-up.
                              </div>
                              <div className="mt-3 flex flex-column gap-2">
                                <div style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
                                  {residentCarePlanTrackingSnapshot?.lastBuiltAt || residentBehaviorSnapshot?.lastBuiltAt
                                    ? `Latest audit update ${formatBehaviorTimestamp(residentCarePlanTrackingSnapshot?.lastBuiltAt || residentBehaviorSnapshot?.lastBuiltAt)}`
                                    : "Open a timeline to review the latest audit history."}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    type="button"
                                    label="Behavior Timeline"
                                    severity="warning"
                                    rounded
                                    outlined
                                    onClick={() => { void openBehaviorInsightsDialog(); }}
                                  />
                                  <Button
                                    type="button"
                                    label="Care Plan Timeline"
                                    severity="warning"
                                    rounded
                                    outlined
                                    onClick={() => { void openCarePlanTrackingDialog(); }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>)
      }

      {(authStore.getUserRoleType() === ROLE_TYPE_PROVIDER || isAdminUser) ? (
        <RelationshipCenterDialog
          visible={showShareResidentDialog}
          onHide={() => setShowShareResidentDialog(false)}
          currentRoleType={authStore.getUserRoleType() === ROLE_TYPE_PROVIDER ? authStore.getUserRoleType() : ROLE_TYPE_PROVIDER}
          shareOnlyMode
          residentOptions={residentProfileData?.residentId ? [{
            residentId: residentProfileData.residentId,
            residentName: `${residentProfileData.firstName || ""} ${residentProfileData.lastName || ""}`.trim() || "Resident",
          }] : []}
          initialResidentId={residentProfileData?.residentId || residentId || null}
        />
      ) : null}

      <Dialog
        header={renderSurfaceHeader("Previous Assessment Versions", "Open an earlier saved assessment summary in this tab.", "pi pi-history")}
        visible={showAssessmentVersionsDialog}
        style={mobile ? { width: "92vw" } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => setShowAssessmentVersionsDialog(false)}
      >
        {assessmentFormsSummary.length === 0 ? (
          <div style={appMutedTextStyle}>No saved assessment versions found yet.</div>
        ) : (
          <div className="flex flex-column gap-2">
            {assessmentFormsSummary.map((form: any, index: number) => {
              const normalizedFormId = String(form?.formId || "").trim();
              const isCurrent = normalizedFormId === String(activeAssessmentSummaryForm?.formId || "").trim();
              return (
                <div key={normalizedFormId || index} style={{ ...appSectionCardStyle, padding: "0.95rem", borderRadius: 18, border: "1px solid #e2e8f0" }}>
                  <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>
                        {form?.version ? `Version V${form.version}` : "Saved assessment version"}
                      </div>
                      <div style={{ ...appMutedTextStyle, marginTop: "0.25rem" }}>
                        {form?.modifiedAt
                          ? `Last updated ${new Date(form.modifiedAt).toLocaleString()}`
                          : form?.createdAt
                            ? `Created ${new Date(form.createdAt).toLocaleString()}`
                            : "Saved assessment version"}
                      </div>
                    </div>
                    <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                      <Tag value={getResidentDetailsAssessmentStatusLabel(form?.status)} severity={getResidentDetailsAssessmentStatusSeverity(form?.status)} />
                      {isCurrent ? <Tag value="Current" severity="success" /> : null}
                      <Button
                        type="button"
                        label={isCurrent ? "Open Current" : "Open Here"}
                        icon="pi pi-folder-open"
                        severity="secondary"
                        rounded
                        outlined
                        size="small"
                        onClick={() => openAssessmentSummaryVersion(normalizedFormId)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Dialog>

      <Dialog
        header={renderSurfaceHeader("DSHS Guidance", "Helpful context for completing a specific care-plan field.", "pi pi-info-circle")}
        visible={showHelpDialog}
        style={mobile ? { width: '92vw' } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => setShowHelpDialog(false)} closable closeOnEscape>
        <div>
          <p style={{ whiteSpace: 'pre-line', margin: 0 }}>{helpMessage}</p>
        </div>
      </Dialog>
      <Dialog
        header="Edit Profile"
        visible={isEditingProfileSection}
        style={mobile ? { width: "92vw" } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          setProfileFormData(buildProfileFormState());
          setProfileFormErrors({});
          setSelectedResidentProfileImageFile(null);
          if (residentId) {
            void loadResidentProfileImage(residentId);
          }
          setIsEditingProfileSection(false);
        }}
      >
        <div className="grid">
          <div className="col-12">
            <div className="flex flex-column align-items-center gap-3">
              {residentProfileImageUrl ? (
                <img
                  src={residentProfileImageUrl}
                  alt={`${[resolvedProfileSnapshot.firstName, resolvedProfileSnapshot.lastName].filter(Boolean).join(" ") || "Resident"} profile`}
                  width="150"
                  height="150"
                  onError={(event) => {
                    refreshResidentProfileImageOnError(String((event.currentTarget as HTMLImageElement)?.src || residentProfileImageUrl));
                  }}
                  style={{
                    objectFit: "cover",
                    border: "3px solid #f2c265",
                    borderRadius: "50%",
                    display: "block",
                    boxShadow: "0 10px 24px rgba(24, 93, 162, 0.12)",
                  }}
                />
              ) : (
                <div
                  className="flex align-items-center justify-content-center"
                  style={{
                    width: "150px",
                    height: "150px",
                    borderRadius: "50%",
                    background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)",
                    border: "2px solid #fde68a",
                    color: "#9a3412",
                    fontWeight: 700,
                    fontSize: "2rem",
                  }}
                >
                  {[resolvedProfileSnapshot.firstName, resolvedProfileSnapshot.lastName].filter(Boolean).join(" ").charAt(0) || "R"}
                </div>
              )}
              <div style={{ width: "100%", maxWidth: "22rem" }}>
                <FileUpload
                  mode="basic"
                  name="residentProfileImage"
                  accept="image/*"
                  maxFileSize={WEBSITE_TEMPLATE_MAX_IMAGE_FILE_SIZE_IN_BYTES}
                  chooseLabel={selectedResidentProfileImageFile ? "Replace Photo" : "Upload Photo"}
                  auto={false}
                  customUpload
                  onSelect={handleResidentProfileImageSelection}
                  onClear={() => setSelectedResidentProfileImageFile(null)}
                  className="w-full"
                  chooseOptions={{ className: "p-button-secondary p-button-outlined p-button-rounded w-full" }}
                />
              </div>
            </div>
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogFirstName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>First Name</label>
            <InputText id="profileDialogFirstName" value={profileFormData?.firstName || ""} className="w-full" onChange={(e) => updateProfileFormField("firstName", e.target.value)} />
            {renderProfileDialogError("firstName")}
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogLastName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Last Name</label>
            <InputText id="profileDialogLastName" value={profileFormData?.lastName || ""} className="w-full" onChange={(e) => updateProfileFormField("lastName", e.target.value)} />
            {renderProfileDialogError("lastName")}
          </div>
          <div className="col-12">
            <div className="flex align-items-center gap-2">
              <Checkbox inputId="profileDialogActive" checked={profileFormData?.active !== false} onChange={(e) => updateProfileFormField("active", !!e.checked)} />
              <label htmlFor="profileDialogActive" style={{ color: "#334155", fontWeight: 600 }}>Keep resident active</label>
            </div>
            <div className="mt-2" style={{ color: "#64748b", fontSize: "0.82rem", lineHeight: 1.55 }}>
              Core Care Plan fields like DOB, sex, physician, pharmacy, and contacts are maintained from the Care Plan page. Insurance details below are stored on the resident profile.
            </div>
          </div>
          <div className="col-12"><Divider align="left"><strong>Insurance</strong></Divider></div>
          <div className="col-12">
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "0.85rem 1rem",
                background: "#f8fafc",
              }}
            >
              <div className="flex align-items-center gap-2 flex-wrap">
                <Checkbox inputId="profileDialogMedicaid" checked={!!profileFormData?.isOnMedicaid} onChange={(e) => updateProfileFormField("isOnMedicaid", !!e.checked)} />
                <label htmlFor="profileDialogMedicaid" style={{ color: "#334155", fontWeight: 600 }}>On Medicaid</label>
              </div>
              <div className="mt-2" style={{ color: "#64748b", fontSize: "0.82rem", lineHeight: 1.5 }}>
                If Medicaid is enabled, add either the Medicaid Client ID or the ProviderOne Number.
              </div>
            </div>
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogMedicaidClientId" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Medicaid Client ID</label>
            <InputText id="profileDialogMedicaidClientId" value={profileFormData?.medicaidClientId || ""} className="w-full" onChange={(e) => updateProfileFormField("medicaidClientId", e.target.value)} />
            {renderProfileDialogError("medicaidClientId")}
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogProviderOneNumber" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>ProviderOne Number</label>
            <InputText id="profileDialogProviderOneNumber" value={profileFormData?.providerOneNumber || ""} className="w-full" onChange={(e) => updateProfileFormField("providerOneNumber", e.target.value)} />
            {renderProfileDialogError("providerOneNumber")}
          </div>
          <div className="col-12"><Divider align="left"><strong>Primary Insurance</strong></Divider></div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogPrimaryInsuranceName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Carrier</label>
            <InputText id="profileDialogPrimaryInsuranceName" value={profileFormData?.primaryInsuranceName || ""} className="w-full" onChange={(e) => updateProfileFormField("primaryInsuranceName", e.target.value)} />
            {renderProfileDialogError("primaryInsuranceName")}
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogPrimaryInsurancePlanType" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Plan Type</label>
            <InputText id="profileDialogPrimaryInsurancePlanType" value={profileFormData?.primaryInsurancePlanType || ""} className="w-full" onChange={(e) => updateProfileFormField("primaryInsurancePlanType", e.target.value)} />
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogPrimaryInsuranceMemberId" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Member ID</label>
            <InputText id="profileDialogPrimaryInsuranceMemberId" value={profileFormData?.primaryInsuranceMemberId || ""} className="w-full" onChange={(e) => updateProfileFormField("primaryInsuranceMemberId", e.target.value)} />
            {renderProfileDialogError("primaryInsuranceMemberId")}
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogPrimaryInsuranceGroupNumber" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Group Number</label>
            <InputText id="profileDialogPrimaryInsuranceGroupNumber" value={profileFormData?.primaryInsuranceGroupNumber || ""} className="w-full" onChange={(e) => updateProfileFormField("primaryInsuranceGroupNumber", e.target.value)} />
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogPrimaryInsuranceSubscriberName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Subscriber Name</label>
            <InputText id="profileDialogPrimaryInsuranceSubscriberName" value={profileFormData?.primaryInsuranceSubscriberName || ""} className="w-full" onChange={(e) => updateProfileFormField("primaryInsuranceSubscriberName", e.target.value)} />
            {renderProfileDialogError("primaryInsuranceSubscriberName")}
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogPrimaryInsuranceSubscriberRelationship" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Relationship</label>
            <InputText id="profileDialogPrimaryInsuranceSubscriberRelationship" value={profileFormData?.primaryInsuranceSubscriberRelationship || ""} className="w-full" onChange={(e) => updateProfileFormField("primaryInsuranceSubscriberRelationship", e.target.value)} />
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogPrimaryInsurancePhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Phone</label>
            <InputText id="profileDialogPrimaryInsurancePhone" value={profileFormData?.primaryInsurancePhone || ""} className="w-full" onChange={(e) => updateProfileFormField("primaryInsurancePhone", e.target.value)} />
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogPrimaryInsuranceClaimsPhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Claims Phone</label>
            <InputText id="profileDialogPrimaryInsuranceClaimsPhone" value={profileFormData?.primaryInsuranceClaimsPhone || ""} className="w-full" onChange={(e) => updateProfileFormField("primaryInsuranceClaimsPhone", e.target.value)} />
          </div>
          <div className="col-12"><Divider align="left"><strong>Secondary Insurance</strong></Divider></div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogSecondaryInsuranceName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Carrier</label>
            <InputText id="profileDialogSecondaryInsuranceName" value={profileFormData?.secondaryInsuranceName || ""} className="w-full" onChange={(e) => updateProfileFormField("secondaryInsuranceName", e.target.value)} />
            {renderProfileDialogError("secondaryInsuranceName")}
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogSecondaryInsurancePlanType" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Plan Type</label>
            <InputText id="profileDialogSecondaryInsurancePlanType" value={profileFormData?.secondaryInsurancePlanType || ""} className="w-full" onChange={(e) => updateProfileFormField("secondaryInsurancePlanType", e.target.value)} />
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogSecondaryInsuranceMemberId" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Member ID</label>
            <InputText id="profileDialogSecondaryInsuranceMemberId" value={profileFormData?.secondaryInsuranceMemberId || ""} className="w-full" onChange={(e) => updateProfileFormField("secondaryInsuranceMemberId", e.target.value)} />
            {renderProfileDialogError("secondaryInsuranceMemberId")}
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogSecondaryInsuranceGroupNumber" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Group Number</label>
            <InputText id="profileDialogSecondaryInsuranceGroupNumber" value={profileFormData?.secondaryInsuranceGroupNumber || ""} className="w-full" onChange={(e) => updateProfileFormField("secondaryInsuranceGroupNumber", e.target.value)} />
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogSecondaryInsuranceSubscriberName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Subscriber Name</label>
            <InputText id="profileDialogSecondaryInsuranceSubscriberName" value={profileFormData?.secondaryInsuranceSubscriberName || ""} className="w-full" onChange={(e) => updateProfileFormField("secondaryInsuranceSubscriberName", e.target.value)} />
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogSecondaryInsuranceSubscriberRelationship" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Relationship</label>
            <InputText id="profileDialogSecondaryInsuranceSubscriberRelationship" value={profileFormData?.secondaryInsuranceSubscriberRelationship || ""} className="w-full" onChange={(e) => updateProfileFormField("secondaryInsuranceSubscriberRelationship", e.target.value)} />
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogSecondaryInsurancePhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Phone</label>
            <InputText id="profileDialogSecondaryInsurancePhone" value={profileFormData?.secondaryInsurancePhone || ""} className="w-full" onChange={(e) => updateProfileFormField("secondaryInsurancePhone", e.target.value)} />
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="profileDialogSecondaryInsuranceClaimsPhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Claims Phone</label>
            <InputText id="profileDialogSecondaryInsuranceClaimsPhone" value={profileFormData?.secondaryInsuranceClaimsPhone || ""} className="w-full" onChange={(e) => updateProfileFormField("secondaryInsuranceClaimsPhone", e.target.value)} />
          </div>
          <div className="col-12">
            <label htmlFor="profileDialogInsuranceNotes" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Insurance Notes</label>
            <InputTextarea id="profileDialogInsuranceNotes" value={profileFormData?.insuranceNotes || ""} className="w-full" rows={4} autoResize onChange={(e) => updateProfileFormField("insuranceNotes", e.target.value)} />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap justify-content-end gap-2">
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            rounded
            outlined
            onClick={() => {
              setProfileFormData(buildProfileFormState());
              setProfileFormErrors({});
              setSelectedResidentProfileImageFile(null);
              if (residentId) {
                void loadResidentProfileImage(residentId);
              }
              setIsEditingProfileSection(false);
            }}
          />
          <Button type="button" label={savingProfileSection ? "Saving..." : "Save Profile"} icon="pi pi-save" severity="warning" rounded onClick={saveProfileSection} loading={savingProfileSection} disabled={savingProfileSection} />
        </div>
      </Dialog>

      <CarePlanContactsDialog
        header="Edit Care Plan Contacts"
        visible={isEditingCarePlanContacts}
        style={mobile ? { width: "92vw" } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        formData={carePlanContactsFormData}
        setFormData={setCarePlanContactsFormData}
        errors={carePlanContactsErrors}
        saving={savingCarePlanContacts}
        onSave={saveCarePlanContactsFromProfile}
        onHide={() => {
          setCarePlanContactsFormData(buildCarePlanContactsFormState());
          setCarePlanContactsErrors({});
          setIsEditingCarePlanContacts(false);
        }}
      />

      <Dialog
        header="Edit Basic Care Plan Details"
        visible={isEditingCarePlanBasics}
        style={mobile ? { width: "92vw" } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          setCarePlanBasicsFormData(buildCarePlanBasicsFormState());
          setCarePlanBasicsErrors({});
          setIsEditingCarePlanBasics(false);
        }}
      >
        <div className="grid">
          <div className="col-12 md:col-6">
            <label htmlFor="carePlanBasicDob" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Date of Birth</label>
            <Calendar
              id="carePlanBasicDob"
              value={parseCarePlanDateString(carePlanBasicsFormData?.dateOfBirth)}
              onChange={(event) => {
                const selectedDate = event.value instanceof Date ? event.value : null;
                setCarePlanBasicsFormData((prev: any) => ({ ...prev, dateOfBirth: formatDateToCarePlanString(selectedDate) }));
              }}
              dateFormat="mm/dd/yy"
              showIcon
              className="w-full"
              showButtonBar
              placeholder="MM/DD/YYYY"
            />
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="carePlanBasicSex" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Sex</label>
            <Dropdown
              id="carePlanBasicSex"
              value={carePlanBasicsFormData?.gender || ""}
              options={SEX_OPTIONS}
              optionLabel="label"
              optionValue="value"
              className="w-full"
              placeholder="Select sex"
              onChange={(event) => setCarePlanBasicsFormData((prev: any) => ({ ...prev, gender: String(event.value || "") }))}
              showClear
            />
          </div>

          <div className="col-12"><Divider /></div>

          <div className="col-12">
            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Allergies</div>
          </div>
          <div className="col-12">
            <div className="flex align-items-center gap-2">
              <Checkbox
                inputId="carePlanAnyAllergies"
                checked={!!carePlanBasicsFormData?.anyAllergies}
                onChange={(e) => {
                  const checked = !!e.checked;
                  setCarePlanBasicsFormData((prev: any) => ({ ...prev, anyAllergies: checked, allergiesDescribed: checked ? prev.allergiesDescribed : "" }));
                  setCarePlanBasicsErrors((prev) => {
                    if (!prev.allergiesDescribed) {
                      return prev;
                    }
                    const nextErrors = { ...prev };
                    delete nextErrors.allergiesDescribed;
                    return nextErrors;
                  });
                }}
              />
              <label htmlFor="carePlanAnyAllergies" style={{ color: "#334155", fontWeight: 600 }}>Any allergies</label>
            </div>
          </div>
          <div className="col-12">
            <label htmlFor="carePlanAllergiesDescribed" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Allergies Described</label>
            <InputTextarea
              id="carePlanAllergiesDescribed"
              value={carePlanBasicsFormData?.allergiesDescribed || ""}
              rows={4}
              className="w-full"
              disabled={!carePlanBasicsFormData?.anyAllergies}
              onChange={(e) => {
                setCarePlanBasicsFormData((prev: any) => ({ ...prev, allergiesDescribed: e.target.value }));
                setCarePlanBasicsErrors((prev) => {
                  if (!prev.allergiesDescribed) {
                    return prev;
                  }
                  const nextErrors = { ...prev };
                  delete nextErrors.allergiesDescribed;
                  return nextErrors;
                });
              }}
            />
            {carePlanBasicsErrors?.allergiesDescribed ? (
              <small style={{ color: "#b91c1c", display: "block", marginTop: "0.35rem" }}>
                {carePlanBasicsErrors.allergiesDescribed}
              </small>
            ) : null}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap justify-content-end gap-2">
          <Button type="button" label="Cancel" severity="secondary" rounded outlined onClick={() => {
            setCarePlanBasicsFormData(buildCarePlanBasicsFormState());
            setCarePlanBasicsErrors({});
            setIsEditingCarePlanBasics(false);
          }} />
          <Button type="button" label={savingCarePlanBasics ? "Saving..." : "Save to Care Plan"} icon="pi pi-save" severity="warning" rounded onClick={saveCarePlanBasicsFromProfile} loading={savingCarePlanBasics} disabled={savingCarePlanBasics} />
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader(
          `More ${getFeatureDialogLabel(featureAccessDialogType)} Access Needed`,
          "Choose how you want to continue.",
          "pi pi-wallet",
        )}
        visible={showFeatureAccessDialog}
        style={mobile ? { width: '92vw' } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          setShowFeatureAccessDialog(false);
          featureAccessDialogCloseAction && featureAccessDialogCloseAction();
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div style={{ color: "#596F80", lineHeight: 1.7 }}>
            {featureAccessDialogMessage}
          </div>
          <div style={{ background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", border: "1px solid #fed7aa", borderRadius: "14px", padding: "1rem" }}>
            <div style={{ fontWeight: 700, color: "#9a3412" }}>Options</div>
            <div className="mt-2" style={{ color: "#596F80", lineHeight: 1.7 }}>
              {featureAccessDialogForSubscribedUser
                ? "You can wait until your next billing date for the included usage to refresh."
                : "Open Billing to start or manage the subscription for this feature."}
            </div>
          </div>
          <div className="flex flex-wrap justify-content-end gap-2">
            {featureAccessDialogForSubscribedUser ? (
              <Button
                label="Got It"
                severity="secondary"
                outlined
                rounded
                style={{ width: "auto", alignSelf: "center" }}
                onClick={() => {
                  setShowFeatureAccessDialog(false);
                  featureAccessDialogCloseAction && featureAccessDialogCloseAction();
                }}
              />
            ) : (
              <Button
                label="View Subscription"
                severity="secondary"
                outlined
                rounded
                style={{ width: "auto", alignSelf: "center" }}
                onClick={() => {
                  setShowFeatureAccessDialog(false);
                  featureAccessDialogCloseAction && featureAccessDialogCloseAction();
                  navigate(`${RouterAddress.BILLING}?tab=subscription`);
                }}
              />
            )}
          </div>
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader("Summarize Assessment", "Choose one resident binder assessment to generate a standalone AI summary.", "pi pi-file")}
        visible={showStandaloneSummaryDialog}
        style={mobile ? { width: '92vw' } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          if (!processingStandaloneSummary) {
            setShowStandaloneSummaryDialog(false);
          }
        }}>
        <div className="flex flex-column gap-3">
          <div style={{ ...appNeutralPanelStyle, border: "1px solid #fdba74", background: "#fff7ed" }}>
            <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>Step 1: Choose the assessment</div>
            <Dropdown
              value={selectedSummaryAssessmentDocumentId}
              options={assessmentBinderDocuments}
              optionLabel="title"
              optionValue="binderDocumentId"
              onChange={(e) => setSelectedSummaryAssessmentDocumentId(e.value)}
              placeholder="Choose an assessment from the resident binder"
              className="w-full"
              itemTemplate={(option: BinderDocument) => <span>{formatBinderDocumentOptionLabel(option)}</span>}
              valueTemplate={(option: BinderDocument | null) =>
                option ? <span>{formatBinderDocumentOptionLabel(option)}</span> : <span>Choose an assessment from the resident binder</span>
              }
              emptyMessage="No binder assessments found."
            />
            <div className="mt-2" style={appMutedTextStyle}>
              Pick an existing binder assessment, or upload one below first.
            </div>
          </div>

          <div style={{ ...appInnerCardStyle, padding: "0.95rem 1rem" }}>
            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Optional: Upload a new assessment</div>
            <div style={appMutedTextStyle}>
              Upload a new assessment here to save it into the resident binder and use it immediately for this summary.
            </div>
            <FileUpload
              mode="basic"
              name="summaryBinderAssessmentUpload"
              accept={BINDER_FEATURE_FILE_ACCEPT}
              maxFileSize={RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES}
              chooseLabel={summaryUploadFile ? "Replace Assessment Upload" : "Upload Assessment to Binder"}
              auto={false}
              customUpload
              onSelect={(event) => setSummaryUploadFile(event.files?.[0] || null)}
              onClear={() => setSummaryUploadFile(null)}
              className="w-full mt-3"
              chooseOptions={{ className: "p-button-secondary p-button-outlined p-button-rounded" }}
            />
            {summaryUploadFile ? (
              <div className="mt-3 flex flex-wrap align-items-center gap-2" style={{ ...appAccentPanelStyle, padding: "0.85rem 0.95rem" }}>
                <span style={appMutedTextStyle}>Selected upload: <strong>{summaryUploadFile.name}</strong></span>
                <Button
                  label={uploadingBinderDialogFile === "summary" ? "Uploading..." : "Upload to Binder"}
                  severity="warning"
                  rounded
                  outlined
                  loading={uploadingBinderDialogFile === "summary"}
                  onClick={() => {
                    void uploadDialogFileToBinder({
                      uploadSlot: "summary",
                      file: summaryUploadFile,
                      folderCode: RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT,
                      selectDocumentId: (binderDocumentId) => setSelectedSummaryAssessmentDocumentId(binderDocumentId),
                      isPrimaryDocument: true,
                    });
                  }}
                />
              </div>
            ) : null}
          </div>

          <div style={{ ...appAccentPanelStyle, border: "1px solid #fdba74", opacity: selectedSummaryAssessmentDocumentId ? 1 : 0.72 }}>
            <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>Step 2: Generate the summary</div>
            <div style={appMutedTextStyle}>
              {selectedSummaryAssessmentDocumentId
                ? "Generate a concise AI summary from the selected assessment so you can quickly review key conditions, care needs, and notable findings."
                : "Choose an assessment in Step 1 to enable summary generation."}
            </div>
            <div className="mt-3 flex justify-content-center">
              <Button
                label="Generate Summary"
                severity="warning"
                rounded
                outlined
                style={{ width: "auto" }}
                onClick={() => { void runStandaloneAssessmentSummary(); }}
                loading={processingStandaloneSummary}
                disabled={!selectedSummaryAssessmentDocumentId}
              />
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader("Compare Assessment", "Choose two resident binder assessments to generate a standalone AI comparison.", "pi pi-chart-bar")}
        visible={showStandaloneCompareDialog}
        style={mobile ? { width: '92vw' } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          if (!processingStandaloneCompare) {
            setShowStandaloneCompareDialog(false);
          }
        }}>
        <div className="flex flex-column gap-3">
          <div style={{ ...appNeutralPanelStyle, border: "1px solid #fdba74", background: "#fff7ed" }}>
            <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>Step 1: Choose the previous assessment</div>
            <Dropdown
              value={selectedCompareFirstAssessmentDocumentId}
              options={assessmentBinderDocuments}
              optionLabel="title"
              optionValue="binderDocumentId"
              onChange={(e) => setSelectedCompareFirstAssessmentDocumentId(e.value)}
              placeholder="Choose the earlier binder assessment"
              className="w-full"
              itemTemplate={(option: BinderDocument) => <span>{formatBinderDocumentOptionLabel(option)}</span>}
              valueTemplate={(option: BinderDocument | null) =>
                option ? <span>{formatBinderDocumentOptionLabel(option)}</span> : <span>Choose the earlier binder assessment</span>
              }
              emptyMessage="No binder assessments found."
            />
            <div className="mt-2" style={appMutedTextStyle}>You can also upload the earlier assessment into binder first.</div>
            <FileUpload
              mode="basic"
              name="comparePreviousBinderAssessmentUpload"
              accept={BINDER_FEATURE_FILE_ACCEPT}
              maxFileSize={RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES}
              chooseLabel={compareFirstUploadFile ? "Replace Previous Assessment Upload" : "Upload Previous Assessment to Binder"}
              auto={false}
              customUpload
              onSelect={(event) => setCompareFirstUploadFile(event.files?.[0] || null)}
              onClear={() => setCompareFirstUploadFile(null)}
              className="w-full mt-3"
              chooseOptions={{ className: "p-button-secondary p-button-outlined p-button-rounded" }}
            />
            {compareFirstUploadFile ? (
              <div className="mt-3 flex flex-wrap align-items-center justify-content-between gap-2" style={{ ...appAccentPanelStyle, padding: "0.8rem 0.95rem" }}>
                <span style={appMutedTextStyle}>Selected upload: <strong>{compareFirstUploadFile.name}</strong></span>
                <Button
                  label={uploadingBinderDialogFile === "compare-first" ? "Uploading..." : "Upload to Binder"}
                  icon="pi pi-upload"
                  severity="warning"
                  rounded
                  loading={uploadingBinderDialogFile === "compare-first"}
                  onClick={() => {
                    void uploadDialogFileToBinder({
                      uploadSlot: "compare-first",
                      file: compareFirstUploadFile,
                      folderCode: RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT,
                      selectDocumentId: (binderDocumentId) => setSelectedCompareFirstAssessmentDocumentId(binderDocumentId),
                    });
                  }}
                />
              </div>
            ) : null}
          </div>

          {selectedCompareFirstAssessmentDocumentId ? (
            <div style={{ ...appNeutralPanelStyle, border: "1px solid #fdba74", background: "#fff7ed" }}>
              <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>Step 2: Choose the latest assessment</div>
              <Dropdown
                value={selectedCompareSecondAssessmentDocumentId}
                options={assessmentBinderDocuments}
                optionLabel="title"
                optionValue="binderDocumentId"
                onChange={(e) => setSelectedCompareSecondAssessmentDocumentId(e.value)}
                placeholder="Choose the latest binder assessment"
                className="w-full"
                itemTemplate={(option: BinderDocument) => <span>{formatBinderDocumentOptionLabel(option)}</span>}
                valueTemplate={(option: BinderDocument | null) =>
                  option ? <span>{formatBinderDocumentOptionLabel(option)}</span> : <span>Choose the latest binder assessment</span>
                }
                emptyMessage="No binder assessments found."
              />
              <div className="mt-2" style={appMutedTextStyle}>Or upload the latest assessment directly into binder.</div>
              <FileUpload
                mode="basic"
                name="compareLatestBinderAssessmentUpload"
                accept={BINDER_FEATURE_FILE_ACCEPT}
                maxFileSize={RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES}
                chooseLabel={compareSecondUploadFile ? "Replace Latest Assessment Upload" : "Upload Latest Assessment"}
                auto={false}
                customUpload
                onSelect={(event) => setCompareSecondUploadFile(event.files?.[0] || null)}
                onClear={() => setCompareSecondUploadFile(null)}
                className="w-full mt-3"
                chooseOptions={{ className: "p-button-secondary p-button-outlined p-button-rounded" }}
              />
              {compareSecondUploadFile ? (
                <div className="mt-3 flex flex-wrap align-items-center justify-content-between gap-2" style={{ ...appAccentPanelStyle, padding: "0.8rem 0.95rem" }}>
                  <span style={appMutedTextStyle}>Selected upload: <strong>{compareSecondUploadFile.name}</strong></span>
                  <Button
                    label={uploadingBinderDialogFile === "compare-second" ? "Uploading..." : "Upload to Binder"}
                    icon="pi pi-upload"
                    severity="warning"
                    rounded
                    loading={uploadingBinderDialogFile === "compare-second"}
                    onClick={() => {
                      void uploadDialogFileToBinder({
                        uploadSlot: "compare-second",
                        file: compareSecondUploadFile,
                        folderCode: RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT,
                        selectDocumentId: (binderDocumentId) => setSelectedCompareSecondAssessmentDocumentId(binderDocumentId),
                        isPrimaryDocument: true,
                      });
                    }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          <div
            style={{
              ...appAccentPanelStyle,
              border: "1px solid #fdba74",
              opacity: selectedCompareFirstAssessmentDocumentId && selectedCompareSecondAssessmentDocumentId ? 1 : 0.72,
            }}
          >
            <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>Step 3: Generate the comparison</div>
            <div style={appMutedTextStyle}>
              {selectedCompareFirstAssessmentDocumentId && selectedCompareSecondAssessmentDocumentId
                ? "Compare the two selected assessments to highlight changed care needs, condition updates, and important differences."
                : "Choose both assessments first to enable the comparison."}
            </div>
            <div className="mt-3 flex justify-content-center">
              <Button
                label="Generate Comparison"
                severity="warning"
                rounded
                outlined
                style={{ width: "auto" }}
                onClick={() => { void runStandaloneAssessmentCompare(); }}
                loading={processingStandaloneCompare}
                disabled={!selectedCompareFirstAssessmentDocumentId || !selectedCompareSecondAssessmentDocumentId || standaloneAssessmentCompareUploadPending}
              />
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader("Compare Care Plans", "Choose two resident binder care plans to generate a standalone comparison.", "pi pi-directions-alt")}
        visible={showStandaloneCarePlanCompareDialog}
        style={mobile ? { width: '92vw' } : appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          if (!processingStandaloneCarePlanCompare) {
            setShowStandaloneCarePlanCompareDialog(false);
          }
        }}>
        <div className="flex flex-column gap-3">
          <div style={{ ...appNeutralPanelStyle, border: "1px solid #fdba74", background: "#fff7ed" }}>
            <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>Step 1: Choose the previous care plan</div>
            <Dropdown
              value={selectedCarePlanCompareFirstDocumentId}
              options={carePlanBinderDocuments}
              optionLabel="title"
              optionValue="binderDocumentId"
              onChange={(e) => setSelectedCarePlanCompareFirstDocumentId(e.value)}
              placeholder="Choose the earlier binder care plan"
              className="w-full"
              itemTemplate={(option: BinderDocument) => <span>{formatBinderDocumentOptionLabel(option)}</span>}
              valueTemplate={(option: BinderDocument | null) =>
                option ? <span>{formatBinderDocumentOptionLabel(option)}</span> : <span>Choose the earlier binder care plan</span>
              }
              emptyMessage="No binder care plans found."
            />
            <div className="mt-2" style={appMutedTextStyle}>You can also upload the previous care plan into binder first.</div>
            <FileUpload
              mode="basic"
              name="comparePreviousCarePlanBinderUpload"
              accept={BINDER_FEATURE_FILE_ACCEPT}
              maxFileSize={RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES}
              chooseLabel={carePlanCompareFirstUploadFile ? "Replace Previous Care Plan Upload" : "Upload Previous Care Plan to Binder"}
              auto={false}
              customUpload
              onSelect={(event) => setCarePlanCompareFirstUploadFile(event.files?.[0] || null)}
              onClear={() => setCarePlanCompareFirstUploadFile(null)}
              className="w-full mt-3"
              chooseOptions={{ className: "p-button-secondary p-button-outlined p-button-rounded" }}
            />
            {carePlanCompareFirstUploadFile ? (
              <div className="mt-3 flex flex-wrap align-items-center justify-content-between gap-2" style={{ ...appAccentPanelStyle, padding: "0.8rem 0.95rem" }}>
                <span style={appMutedTextStyle}>Selected upload: <strong>{carePlanCompareFirstUploadFile.name}</strong></span>
                <Button
                  label={uploadingBinderDialogFile === "careplan-first" ? "Uploading..." : "Upload to Binder"}
                  icon="pi pi-upload"
                  severity="warning"
                  rounded
                  loading={uploadingBinderDialogFile === "careplan-first"}
                  onClick={() => {
                    void uploadDialogFileToBinder({
                      uploadSlot: "careplan-first",
                      file: carePlanCompareFirstUploadFile,
                      folderCode: RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN,
                      selectDocumentId: (binderDocumentId) => setSelectedCarePlanCompareFirstDocumentId(binderDocumentId),
                    });
                  }}
                />
              </div>
            ) : null}
          </div>

          {selectedCarePlanCompareFirstDocumentId ? (
            <div style={{ ...appNeutralPanelStyle, border: "1px solid #fdba74", background: "#fff7ed" }}>
              <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>Step 2: Choose the latest care plan</div>
              <Dropdown
                value={selectedCarePlanCompareSecondDocumentId}
                options={carePlanBinderDocuments}
                optionLabel="title"
                optionValue="binderDocumentId"
                onChange={(e) => setSelectedCarePlanCompareSecondDocumentId(e.value)}
                placeholder="Choose the latest binder care plan"
                className="w-full"
                itemTemplate={(option: BinderDocument) => <span>{formatBinderDocumentOptionLabel(option)}</span>}
                valueTemplate={(option: BinderDocument | null) =>
                  option ? <span>{formatBinderDocumentOptionLabel(option)}</span> : <span>Choose the latest binder care plan</span>
                }
                emptyMessage="No binder care plans found."
              />
              <div className="mt-2" style={appMutedTextStyle}>You can also upload the latest care plan into binder first.</div>
              <FileUpload
                mode="basic"
                name="compareLatestCarePlanBinderUpload"
                accept={BINDER_FEATURE_FILE_ACCEPT}
                maxFileSize={RESIDENT_ASSESSMENT_MAX_FILE_SIZE_IN_BYTES}
                chooseLabel={carePlanCompareSecondUploadFile ? "Replace Latest Care Plan Upload" : "Upload Latest Care Plan to Binder"}
                auto={false}
                customUpload
                onSelect={(event) => setCarePlanCompareSecondUploadFile(event.files?.[0] || null)}
                onClear={() => setCarePlanCompareSecondUploadFile(null)}
                className="w-full mt-3"
                chooseOptions={{ className: "p-button-secondary p-button-outlined p-button-rounded" }}
              />
              {carePlanCompareSecondUploadFile ? (
                <div className="mt-3 flex flex-wrap align-items-center justify-content-between gap-2" style={{ ...appAccentPanelStyle, padding: "0.8rem 0.95rem" }}>
                  <span style={appMutedTextStyle}>Selected upload: <strong>{carePlanCompareSecondUploadFile.name}</strong></span>
                  <Button
                    label={uploadingBinderDialogFile === "careplan-second" ? "Uploading..." : "Upload to Binder"}
                    icon="pi pi-upload"
                    severity="warning"
                    rounded
                    loading={uploadingBinderDialogFile === "careplan-second"}
                    onClick={() => {
                      void uploadDialogFileToBinder({
                        uploadSlot: "careplan-second",
                        file: carePlanCompareSecondUploadFile,
                        folderCode: RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN,
                        selectDocumentId: (binderDocumentId) => setSelectedCarePlanCompareSecondDocumentId(binderDocumentId),
                      });
                    }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          <div
            style={{
              ...appAccentPanelStyle,
              border: "1px solid #fdba74",
              opacity: selectedCarePlanCompareFirstDocumentId && selectedCarePlanCompareSecondDocumentId ? 1 : 0.72,
            }}
          >
            <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>Step 3: Generate the comparison</div>
            <div style={appMutedTextStyle}>
              {selectedCarePlanCompareFirstDocumentId && selectedCarePlanCompareSecondDocumentId
                ? "Compare the two selected care plans to highlight what was added, removed, changed, or may need follow-up review."
                : "Choose both care plans first to enable the comparison."}
            </div>
            <div className="mt-3 flex justify-content-center">
              <Button
                label="Generate Care Plan Comparison"
                severity="warning"
                rounded
                outlined
                style={{ width: "auto" }}
                onClick={() => { void runStandaloneCarePlanCompare(); }}
                loading={processingStandaloneCarePlanCompare}
                disabled={!selectedCarePlanCompareFirstDocumentId || !selectedCarePlanCompareSecondDocumentId || standaloneCarePlanCompareUploadPending}
              />
            </div>
          </div>
        </div>
      </Dialog>

      {/* Show Assessment Summary in HTML format */}
      <Dialog
        header={renderSurfaceHeader("Assessment Summary", "Review the generated summary from the latest assessment.", "pi pi-list")}
        visible={showAssessmentSummary}
        style={{ width: '90%' }}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          setShowAssessmentSummary(false);
          setShowStandaloneSummaryDialog(false);
        }} closeOnEscape={false} footer={
          <div className="flex flex-wrap justify-content-between align-items-center gap-3">
            <p style={{ margin: 0 }}>
              <i style={{ color: "orange" }}>{AI_MESSAGE_FOR_COMPLIANCE}</i>
            </p>
            {assessmentSummaryData && (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  label="Save to Binder"
                  severity="warning"
                  outlined
                  rounded
                  style={{ width: "auto" }}
                  onClick={async () => {
                    try {
                      await saveGeneratedHtmlToResidentBinder({
                        folderCode: RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT,
                        title: "Assessment Summary",
                        fileNamePrefix: "assessment-summary",
                        htmlContent: assessmentSummaryData,
                      });
                      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Assessment summary saved to resident binder.', life: 4000 });
                    } catch (error: any) {
                      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error?.message || "Unable to save assessment summary to binder.", life: 4000 });
                    }
                  }}
                />
                <Button
                  type="button"
                  label="Download Summary"
                  severity="secondary"
                  outlined
                  rounded
                  style={{ width: "auto" }}
                  onClick={() => downloadHtmlContent("Assessment Summary", assessmentSummaryData, `assessment-summary-${residentId || "resident"}.doc`)}
                />
              </div>
            )}
          </div>
        }>
        <div>
          {
            assessmentSummaryData ? (
              <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(assessmentSummaryData) }} />
            ) : (
              <div className="col-12 text-center d-flex flex-column justify-content-center align-items-center">
                <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                <div className="block mt-3" style={{ color: "#0f172a", fontWeight: 700 }}>
                  Generating summary...
                </div>
                <div className="block mt-2" style={{ color: "#596F80" }}>
                  <strong>Job Status:</strong> {summaryJobStatus || "Starting"}
                </div>
                <div className="block mt-1" style={{ color: "#596F80" }}>
                  <strong>Job Sub-Status:</strong> {summaryJobSubStatus || "Preparing summary"}
                </div>
                <div className="block mt-2" style={{ color: "#596F80" }}>
                  Elapsed time: {formatElapsedTime(summaryElapsedSeconds)}
                </div>
                <div className="block mt-2" style={{ color: "#596F80" }}>
                  This usually takes about 5 minutes. Please keep the browser open and let this request finish before starting another one.
                </div>
              </div>
            )
          }
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader("Care Plan Comparison", "Review the generated comparison between two care plans.", "pi pi-directions-alt")}
        visible={showCarePlanCompareResultDialog}
        style={{ width: '90%' }}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          setShowCarePlanCompareResultDialog(false);
          setShowStandaloneCarePlanCompareDialog(false);
        }} closeOnEscape={false} footer={
          <div className="flex flex-wrap justify-content-between align-items-center gap-3">
            <p style={{ margin: 0 }}>
              <i style={{ color: "orange" }}>{AI_MESSAGE_FOR_COMPLIANCE}</i>
            </p>
            {carePlanCompareSummaryData && (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  label="Save to Binder"
                  severity="warning"
                  outlined
                  rounded
                  style={{ width: "auto" }}
                  onClick={async () => {
                    try {
                      await saveGeneratedHtmlToResidentBinder({
                        folderCode: RESIDENT_BINDER_FOLDER_CODES.NEGOTIATED_CARE_PLAN,
                        title: "Care Plan Comparison",
                        fileNamePrefix: "care-plan-comparison",
                        htmlContent: carePlanCompareSummaryData,
                      });
                      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Care plan comparison saved to resident binder.', life: 4000 });
                    } catch (error: any) {
                      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error?.message || "Unable to save care plan comparison to binder.", life: 4000 });
                    }
                  }}
                />
                <Button
                  type="button"
                  label="Download Comparison"
                  severity="secondary"
                  outlined
                  rounded
                  style={{ width: "auto" }}
                  onClick={() => downloadHtmlContent("Care Plan Comparison", carePlanCompareSummaryData, `care-plan-comparison-${residentId || "resident"}.doc`)}
                />
              </div>
            )}
          </div>
        }>
        <div>
          {
            carePlanCompareSummaryData ? (
              <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(carePlanCompareSummaryData) }} />
            ) : (
              <div className="col-12 text-center d-flex flex-column justify-content-center align-items-center">
                <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                <div className="block mt-3" style={{ color: "#0f172a", fontWeight: 700 }}>
                  Generating care plan comparison...
                </div>
                <div className="block mt-2" style={{ color: "#596F80" }}>
                  <strong>Job Status:</strong> {carePlanCompareJobStatus || "Starting"}
                </div>
                <div className="block mt-1" style={{ color: "#596F80" }}>
                  <strong>Job Sub-Status:</strong> {carePlanCompareJobSubStatus || "Preparing comparison"}
                </div>
                <div className="block mt-2" style={{ color: "#596F80" }}>
                  Elapsed time: {formatElapsedTime(carePlanCompareElapsedSeconds)}
                </div>
                <div className="block mt-2" style={{ color: "#596F80" }}>
                  This can take up to 10 minutes for large care plans. Please keep the browser open, or reopen the latest result later from this page.
                </div>
              </div>
            )
          }
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader(
          "Compare Care Plan Med List With New MAR Sheet",
          "Upload the new MAR sheet to review any medication, use, route, or frequency changes against the resident's current care plan before fixing it.",
          "pi pi-file-check"
        )}
        visible={showMedicationCompareDialog}
        style={mobile ? { width: "95vw" } : appWideDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={closeMedicationCompareDialog}
        closeOnEscape={!processingMedicationCompare && !applyingMedicationCompareChanges}
        footer={(
          <div className="flex flex-column gap-3">
            <div className="flex flex-wrap justify-content-end gap-2">
              {medicationCompareHasDifferences ? (
                <>
                  <Button
                    type="button"
                    label="Fix in Care Plan"
                    severity="success"
                    outlined
                    rounded
                    loading={applyingMedicationCompareChanges}
                    disabled={!medicationCompareCompletedJobId}
                    onClick={() => {
                      confirmDialog({
                        message: renderConfirmMessage(
                          "Fix care plan medication list",
                          "This will update the care plan medication section using the uploaded MAR sheet medication list. Continue?",
                        ),
                        header: "Confirmation",
                        icon: "pi pi-exclamation-triangle",
                        acceptLabel: "Fix in Care Plan",
                        acceptClassName: appConfirmDialogClasses.acceptClassName,
                        rejectLabel: "Cancel",
                        rejectClassName: "p-button-text p-button-rounded",
                        accept: () => { void applyMedicationCompareChangesToCarePlan(); },
                      });
                    }}
                  />
                  <Button
                    type="button"
                    label="Download Comparison"
                    severity="secondary"
                    outlined
                    rounded
                    onClick={() => downloadHtmlContent("Care Plan Medication Comparison", medicationCompareReportHtml, `care-plan-medication-comparison-${residentId || "resident"}.doc`)}
                  />
                </>
              ) : null}
            </div>
            <p style={{ margin: 0 }}>
              <i style={{ color: "orange" }}>{AI_MESSAGE_FOR_COMPLIANCE}</i>
            </p>
          </div>
        )}
      >
        <div className="flex flex-column gap-3">
          {!processingMedicationCompare && !medicationCompareResultData ? (
            <div className="flex flex-column gap-3">
              <div style={{ ...appNeutralPanelStyle, border: "1px solid #fdba74", background: "#fff7ed" }}>
                <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>Step 1: Upload the new MAR sheet</div>
                <div style={{ ...appMutedTextStyle, marginBottom: "0.75rem" }}>
                  We will compare this MAR sheet against the resident's current care plan medication list.
                </div>
                <div style={{ ...appNeutralPanelStyle, border: "1px solid #fdba74", background: "#fff7ed", padding: "0.95rem 1rem" }}>
                  <input
                    ref={medicationCompareFileInputRef}
                    type="file"
                    accept={MAR_COMPARE_FILE_ACCEPT}
                    style={{ display: "none" }}
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      setMedicationCompareUploadFile(file);
                      setMedicationCompareFileName(String(file?.name || "").trim());
                    }}
                  />
                  <Button
                    type="button"
                    label={medicationCompareUploadFile ? "Replace New MAR Sheet" : "Upload New MAR Sheet to Compare"}
                    severity="secondary"
                    outlined
                    rounded
                    onClick={() => medicationCompareFileInputRef.current?.click()}
                    style={{ borderWidth: "1.5px" }}
                  />
                  {medicationCompareUploadFile ? (
                    <div className="mt-3" style={{ ...appAccentPanelStyle, padding: "0.85rem 0.95rem" }}>
                      <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.25rem" }}>Selected file</div>
                      <div style={appMutedTextStyle}>{medicationCompareUploadFile.name}</div>
                      <div className="mt-2">
                        <Button
                          type="button"
                          label="Clear File"
                          severity="secondary"
                          text
                          rounded
                          onClick={() => {
                            setMedicationCompareUploadFile(null);
                            setMedicationCompareFileName("");
                            if (medicationCompareFileInputRef.current) {
                              medicationCompareFileInputRef.current.value = "";
                            }
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2" style={appMutedTextStyle}>
                      Choose the new MAR sheet you want to compare against the current care plan.
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  ...appAccentPanelStyle,
                  border: "1px solid #fdba74",
                  opacity: isMedicationCompareReady ? 1 : 0.72,
                }}
              >
                <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.45rem" }}>Step 2: Run the comparison</div>
                <div style={appMutedTextStyle}>
                  {isMedicationCompareReady
                    ? "We will show any added medications or detail changes before you update the care plan."
                    : "Upload the new MAR sheet first to enable comparison."}
                </div>
                <div className="mt-3" style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    key={isMedicationCompareReady ? "medication-compare-ready" : "medication-compare-waiting"}
                    type="button"
                    label="Compare"
                    severity="warning"
                    rounded
                    disabled={!isMedicationCompareReady}
                    onClick={() => {
                      if (medicationCompareUploadFile) {
                        void startMedicationCompareReview(medicationCompareUploadFile);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {processingMedicationCompare ? (
            <div className="col-12 text-center d-flex flex-column justify-content-center align-items-center">
              <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
              <div className="block mt-3" style={{ color: "#0f172a", fontWeight: 700 }}>
                Reviewing MAR sheet
              </div>
              <div className="block mt-2" style={{ color: "#596F80" }}>
                <strong>Status:</strong> {medicationCompareJobStatus || "Starting"}
              </div>
              <div className="block mt-1" style={{ color: "#596F80" }}>
                <strong>Progress:</strong> {medicationCompareJobSubStatus || "Preparing medication comparison"}
              </div>
              <div className="block mt-2" style={{ color: "#596F80" }}>
                Elapsed time: {formatElapsedTime(medicationCompareElapsedSeconds)}
              </div>
              {medicationCompareFileName ? (
                <div className="block mt-2" style={{ color: "#596F80" }}>
                  File: {medicationCompareFileName}
                </div>
              ) : null}
            </div>
          ) : null}

          {medicationCompareResultData ? (
            <div className="flex flex-column gap-3">
              <div className="flex flex-column gap-2">
                {(medicationCompareResultData?.changed || []).map((item: any, index: number) => (
                  <div key={`medication-changed-${index}`} style={{ ...appInnerCardStyle, padding: "0.85rem 1rem" }}>
                    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{item?.uploadedEntry?.medicationName || "Medication"}</div>
                      <Tag value={Array.isArray(item?.differenceLabels) && item.differenceLabels.length ? item.differenceLabels.join(", ") : "Details changed"} severity="warning" />
                    </div>
                    <div className="mt-2 grid">
                      <div className="col-12 md:col-6">
                        <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.84rem" }}>New MAR sheet</div>
                        <div style={appMutedTextStyle}>{[item?.uploadedEntry?.dosage, item?.uploadedEntry?.route, item?.uploadedEntry?.schedule].filter(Boolean).join(" - ") || "No extra details"}</div>
                        {item?.uploadedEntry?.indication ? <div style={appMutedTextStyle}>Use: {item.uploadedEntry.indication}</div> : null}
                        {item?.uploadedEntry?.instructions ? <div style={appMutedTextStyle}>Instructions: {item.uploadedEntry.instructions}</div> : null}
                      </div>
                      <div className="col-12 md:col-6">
                        <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.84rem" }}>Current care plan source</div>
                        <div style={appMutedTextStyle}>{[item?.baselineEntry?.dosage, item?.baselineEntry?.route, item?.baselineEntry?.schedule].filter(Boolean).join(" - ") || "No extra details"}</div>
                        {item?.baselineEntry?.indication ? <div style={appMutedTextStyle}>Use: {item.baselineEntry.indication}</div> : null}
                        {item?.baselineEntry?.instructions ? <div style={appMutedTextStyle}>Instructions: {item.baselineEntry.instructions}</div> : null}
                      </div>
                    </div>
                  </div>
                ))}

                {(medicationCompareResultData?.newOnUploadedMar || []).map((item: any, index: number) => (
                  <div key={`medication-new-${index}`} style={{ ...appInnerCardStyle, padding: "0.85rem 1rem" }}>
                    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{item?.uploadedEntry?.medicationName || "Medication"}</div>
                      <Tag value="Missing in care plan" severity="danger" />
                    </div>
                    <div className="mt-1" style={appMutedTextStyle}>
                      {[item?.uploadedEntry?.dosage, item?.uploadedEntry?.route, item?.uploadedEntry?.schedule].filter(Boolean).join(" - ") || "No extra MAR details found."}
                    </div>
                    {item?.uploadedEntry?.indication ? <div style={appMutedTextStyle}>Use: {item.uploadedEntry.indication}</div> : null}
                    {item?.uploadedEntry?.instructions ? <div style={appMutedTextStyle}>Instructions: {item.uploadedEntry.instructions}</div> : null}
                  </div>
                ))}

                {(medicationCompareResultData?.removedFromUploadedMar || []).map((item: any, index: number) => (
                  <div key={`medication-removed-${index}`} style={{ ...appInnerCardStyle, padding: "0.85rem 1rem" }}>
                    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{item?.baselineEntry?.medicationName || "Medication"}</div>
                      <Tag value="Not found on new MAR" severity="secondary" />
                    </div>
                    <div className="mt-1" style={appMutedTextStyle}>
                      {[item?.baselineEntry?.dosage, item?.baselineEntry?.route, item?.baselineEntry?.schedule].filter(Boolean).join(" - ") || "No extra details found in the selected binder document."}
                    </div>
                    {item?.baselineEntry?.indication ? <div style={appMutedTextStyle}>Use: {item.baselineEntry.indication}</div> : null}
                    {item?.baselineEntry?.instructions ? <div style={appMutedTextStyle}>Instructions: {item.baselineEntry.instructions}</div> : null}
                  </div>
                ))}

                {!((medicationCompareResultData?.changed || []).length || (medicationCompareResultData?.newOnUploadedMar || []).length || (medicationCompareResultData?.removedFromUploadedMar || []).length) ? (
                  <div style={appMutedTextStyle}>No medication differences were found between the current care plan source and the new MAR sheet.</div>
                ) : null}
              </div>

              {((medicationCompareResultData?.changed || []).length || (medicationCompareResultData?.newOnUploadedMar || []).length || (medicationCompareResultData?.removedFromUploadedMar || []).length) ? (
                <div style={{ ...appNeutralPanelStyle, border: "1px solid #fdba74", background: "#fff7ed" }}>
                  <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.35rem" }}>If you click Fix in Care Plan</div>
                  <div style={appMutedTextStyle}>
                    AFH Hub will update the care plan medication section to match the uploaded MAR sheet, because the MAR sheet is treated as the latest source of truth from pharmacy.
                  </div>
                  <ul style={{ margin: "0.65rem 0 0", paddingLeft: "1.15rem", color: "#7c2d12" }}>
                    <li>Missing medications from the care plan will be added from the new MAR sheet.</li>
                    <li>Mismatched details like dosage, route, frequency, use, and instructions will be updated.</li>
                    <li>Medications in the current care plan source that are not found on the new MAR should be reviewed carefully before you confirm the update.</li>
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </Dialog>

      <Dialog
        header={renderSurfaceHeader(
          "Resident Behavior Insights",
          "Review the current provider care plan behavior snapshot and the timeline of recorded changes.",
          "pi pi-history"
        )}
        visible={showBehaviorInsightsDialog}
        style={mobile ? { width: "95vw" } : appWideDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          behaviorInsightsAutoImportedRef.current = false;
          setShowBehaviorInsightsDialog(false);
        }}
      >
        {loadingBehaviorInsights ? (
          <div className="flex justify-content-center py-6">
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
          </div>
        ) : (
          <div className="flex flex-column gap-4">
            <div className="grid">
              <div className="col-12 md:col-4">
                <div style={appInnerCardStyle}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Behaviors Tracked</div>
                  <div className="mt-2" style={{ fontSize: "1.8rem", fontWeight: 800, color: "#0f172a" }}>
                    {currentBehaviorRows.length}
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div style={appInnerCardStyle}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Currently Active</div>
                  <div className="mt-2" style={{ fontSize: "1.8rem", fontWeight: 800, color: "#0f172a" }}>
                    {activeBehaviorRows.length}
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div style={appInnerCardStyle}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Timeline Entries</div>
                  <div className="mt-2" style={{ fontSize: "1.8rem", fontWeight: 800, color: "#0f172a" }}>
                    {residentBehaviorTimeline.length}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-content-end">
              <div className="flex gap-2">
                <Button
                  type="button"
                  label="Import Now"
                  icon="pi pi-refresh"
                  severity="warning"
                  rounded
                  outlined
                  loading={importingBehaviorInsights}
                  onClick={async () => {
                    const importSucceeded = await importResidentBehaviorInsights("manual");
                    if (importSucceeded) {
                      await loadResidentBehaviorInsights(false);
                    }
                  }}
                />
                <Button
                  type="button"
                  label="Quick Export"
                  icon="pi pi-download"
                  severity="secondary"
                  rounded
                  outlined
                  onClick={behaviorInsightsActiveTab === 0 ? exportBehaviorSnapshot : exportBehaviorTimeline}
                  disabled={behaviorInsightsActiveTab === 0 ? currentBehaviorRows.length === 0 : visibleBehaviorTimelineEntries.length === 0}
                />
              </div>
            </div>

            <TabView activeIndex={behaviorInsightsActiveTab} onTabChange={(e) => setBehaviorInsightsActiveTab(e.index)}>
              <TabPanel header="Current Snapshot">
                {currentBehaviorRows.length === 0 ? (
                  <div style={appInnerCardStyle}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>No snapshot yet</div>
                    <div className="mt-2" style={appMutedTextStyle}>
                      A behavior snapshot will appear after the provider psych/social care plan is saved or regenerated. You can import the current care plan now to backfill older residents.
                    </div>
                    <Button
                      type="button"
                      label="Import Now"
                      icon="pi pi-refresh"
                      severity="warning"
                      rounded
                      outlined
                      className="mt-3"
                      loading={importingBehaviorInsights}
                      onClick={async () => {
                        const importSucceeded = await importResidentBehaviorInsights("manual");
                        if (importSucceeded) {
                          await loadResidentBehaviorInsights(false);
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex flex-column gap-3">
                    {currentBehaviorRows.map((row: any) => (
                      <div key={row.behaviorKey} style={appInnerCardStyle}>
                        <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                          <div>
                            <div style={{ fontWeight: 700, color: "#0f172a" }}>{row.behaviorTitle}</div>
                            <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
                              {row.sectionCategory}
                            </div>
                          </div>
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "0.4rem",
                              borderRadius: "999px",
                              padding: "0.35rem 0.8rem",
                              background: row.present ? "#ecfdf3" : "#f8fafc",
                              color: row.present ? "#047857" : "#64748b",
                              border: `1px solid ${row.present ? "#a7f3d0" : "#e2e8f0"}`,
                              fontWeight: 700,
                              fontSize: "0.82rem",
                            }}
                          >
                            <i className={`pi ${row.present ? "pi-check-circle" : "pi-minus-circle"}`} />
                            <span>{row.present ? "Active" : "Not documented"}</span>
                          </div>
                        </div>
                        {(row.residentDetails || row.caregiverInstructions) ? (
                          <div className="grid mt-2">
                            <div className="col-12 md:col-6">
                              <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.9rem" }}>Resident details</div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, whiteSpace: "pre-line" }}>
                                {row.residentDetails || "None entered."}
                              </div>
                            </div>
                            <div className="col-12 md:col-6">
                              <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.9rem" }}>Caregiver instructions</div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, whiteSpace: "pre-line" }}>
                                {row.caregiverInstructions || "None entered."}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </TabPanel>
              <TabPanel header="Behavior Timeline">
                <div className="grid mb-3">
                  <div className="col-12 lg:col-5">
                    <div style={appMutedTextStyle}>
                      Filter the recorded behavior changes to focus on recent updates or one specific behavior.
                    </div>
                  </div>
                  <div className="col-12 md:col-6 lg:col-3">
                    <Dropdown
                      value={behaviorTimelineFilter}
                      onChange={(e) => setBehaviorTimelineFilter(e.value)}
                      options={behaviorTimelineFilterOptions}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Filter timeline"
                      className="w-full"
                    />
                  </div>
                  <div className="col-12 md:col-6 lg:col-4">
                    <Dropdown
                      value={behaviorTimelineBehaviorFilter}
                      onChange={(e) => setBehaviorTimelineBehaviorFilter(e.value)}
                      options={behaviorTimelineBehaviorOptions}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Filter by behavior"
                      className="w-full"
                    />
                  </div>
                </div>
                {visibleBehaviorTimelineEntries.length === 0 ? (
                  <div style={appInnerCardStyle}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>No timeline entries in this range</div>
                    <div className="mt-2" style={appMutedTextStyle}>
                      {behaviorTimelineBehaviorFilter === "all"
                        ? "Try a wider time range, or save/regenerate the care plan to create a new timeline entry."
                        : "No changes were recorded for the selected behavior in this time range."}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-column gap-3">
                    {visibleBehaviorTimelineEntries.map(({ entry, behaviorChangeCards }: any) => {
                      const changeSummary = entry?.changeSummary || {};
                      const added = Array.isArray(changeSummary.addedBehaviorKeys) ? changeSummary.addedBehaviorKeys.length : 0;
                      const removed = Array.isArray(changeSummary.removedBehaviorKeys) ? changeSummary.removedBehaviorKeys.length : 0;
                      const details = Array.isArray(changeSummary.updatedDetailKeys) ? changeSummary.updatedDetailKeys.length : 0;
                      const instructions = Array.isArray(changeSummary.updatedInstructionKeys) ? changeSummary.updatedInstructionKeys.length : 0;

                      return (
                        <div key={entry.timelineEntryId} style={appInnerCardStyle}>
                          <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                            <div>
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>
                                {getBehaviorTimelineEventLabel(String(entry?.eventType || ""))}
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
                                {formatBehaviorTimestamp(entry.recordedAt)}
                              </div>
                            </div>
                            <div style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
                              By {entry.changedBy || "system"}
                            </div>
                          </div>
                          <div className="grid mt-2">
                            <div className="col-6 md:col-3">
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>{added}</div>
                              <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>Added</div>
                            </div>
                            <div className="col-6 md:col-3">
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>{removed}</div>
                              <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>Removed</div>
                            </div>
                            <div className="col-6 md:col-3">
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>{details}</div>
                              <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>Details updated</div>
                            </div>
                            <div className="col-6 md:col-3">
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>{instructions}</div>
                              <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>Instructions updated</div>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-column gap-3">
                            {behaviorChangeCards.map((changeCard: any) => {
                              const statusStyle = changeCard.status === "added"
                                ? { background: "#ecfdf3", color: "#166534", label: "Added" }
                                : changeCard.status === "removed"
                                  ? { background: "#fef2f2", color: "#991b1b", label: "Removed" }
                                  : { background: "#eff6ff", color: "#1d4ed8", label: "Updated" };

                              return (
                                <div key={`${entry.timelineEntryId}-${changeCard.behaviorKey}`} style={appNeutralPanelStyle}>
                                  <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{changeCard.behaviorTitle}</div>
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        borderRadius: "999px",
                                        padding: "0.35rem 0.75rem",
                                        background: statusStyle.background,
                                        color: statusStyle.color,
                                        fontSize: "0.82rem",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {statusStyle.label}
                                    </span>
                                  </div>
                                  <div className="mt-2 flex flex-wrap gap-3" style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                                    <span>
                                      <strong>Recorded:</strong> {formatBehaviorTimestamp(entry.recordedAt)}
                                    </span>
                                    <span>
                                      <strong>Care Plan Updated:</strong> {formatBehaviorTimestamp(entry.sourceCarePlanUpdatedAt || entry.recordedAt)}
                                    </span>
                                  </div>

                                  <div className="grid mt-3">
                                    <div className="col-12 lg:col-4">
                                      <div style={{ fontWeight: 700, color: "#64748b", fontSize: "0.8rem" }}>Behavior present</div>
                                      <div className="mt-2 flex flex-wrap gap-2 align-items-center">
                                        <span
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            borderRadius: "999px",
                                            padding: "0.3rem 0.65rem",
                                            background: changeCard.previousRow?.present ? "#fef2f2" : "#f8fafc",
                                            color: changeCard.previousRow?.present ? "#991b1b" : "#64748b",
                                            fontSize: "0.8rem",
                                            fontWeight: 700,
                                          }}
                                        >
                                          Before: {changeCard.previousRow?.present ? "Yes" : "No"}
                                        </span>
                                        <span style={{ color: "#94a3b8", fontWeight: 700 }}>→</span>
                                        <span
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            borderRadius: "999px",
                                            padding: "0.3rem 0.65rem",
                                            background: changeCard.nextRow?.present ? "#ecfdf3" : "#f8fafc",
                                            color: changeCard.nextRow?.present ? "#166534" : "#64748b",
                                            fontSize: "0.8rem",
                                            fontWeight: 700,
                                          }}
                                        >
                                          After: {changeCard.nextRow?.present ? "Yes" : "No"}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="col-12 lg:col-4">
                                      <div style={{ fontWeight: 700, color: "#64748b", fontSize: "0.8rem" }}>Description</div>
                                      <div
                                        className="mt-2"
                                        style={{
                                          background: changeCard.detailsChanged ? "#fff7ed" : "#f8fafc",
                                          border: `1px solid ${changeCard.detailsChanged ? "#fed7aa" : "#e2e8f0"}`,
                                          borderRadius: "12px",
                                          padding: "0.75rem",
                                          minHeight: "100%",
                                        }}
                                      >
                                        <div style={{ fontWeight: 700, color: "#92400e", fontSize: "0.78rem", marginBottom: "0.35rem" }}>Before</div>
                                        <div style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                                          {changeCard.previousRow?.residentDetails || "Empty"}
                                        </div>
                                        <div style={{ fontWeight: 700, color: "#92400e", fontSize: "0.78rem", marginTop: "0.75rem", marginBottom: "0.35rem" }}>After</div>
                                        <div style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                                          {changeCard.nextRow?.residentDetails || "Empty"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-12 lg:col-4">
                                      <div style={{ fontWeight: 700, color: "#64748b", fontSize: "0.8rem" }}>Instructions</div>
                                      <div
                                        className="mt-2"
                                        style={{
                                          background: changeCard.instructionsChanged ? "#eff6ff" : "#f8fafc",
                                          border: `1px solid ${changeCard.instructionsChanged ? "#bfdbfe" : "#e2e8f0"}`,
                                          borderRadius: "12px",
                                          padding: "0.75rem",
                                          minHeight: "100%",
                                        }}
                                      >
                                        <div style={{ fontWeight: 700, color: "#1d4ed8", fontSize: "0.78rem", marginBottom: "0.35rem" }}>Before</div>
                                        <div style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                                          {changeCard.previousRow?.caregiverInstructions || "Empty"}
                                        </div>
                                        <div style={{ fontWeight: 700, color: "#1d4ed8", fontSize: "0.78rem", marginTop: "0.75rem", marginBottom: "0.35rem" }}>After</div>
                                        <div style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                                          {changeCard.nextRow?.caregiverInstructions || "Empty"}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabPanel>
            </TabView>
          </div>
        )}
      </Dialog>

      <Dialog
        header={renderSurfaceHeader(
          "Daily Behavior Log",
          "Write down behaviors, support given, and follow-up notes for the day.",
          "pi pi-clock",
        )}
        visible={showBehaviorLogDialog}
        style={mobile ? { width: "95vw" } : appWideDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => setShowBehaviorLogDialog(false)}
      >
        <div className="flex flex-column gap-3">
          <div style={appInnerCardStyle}>
            <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
              <div>
                <div style={{ fontWeight: 800, color: "#0f172a" }}>Monthly CBHS/IBSS Report</div>
                <div className="mt-1" style={appMutedTextStyle}>
                  Choose a month to build the CBHS/IBSS report from daily entries.
                </div>
              </div>
              <label style={{ display: "grid", gap: "0.35rem", minWidth: "15rem" }}>
                <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>Report Month</span>
                <Calendar
                  value={getCbhsMonthDateValue(selectedCbhsMonth)}
                  onChange={(event) => setSelectedCbhsMonth(getCbhsMonthKeyFromDateValue(event.value))}
                  view="month"
                  dateFormat="MM yy"
                  showIcon
                  readOnlyInput
                  className="w-full"
                  inputClassName="w-full"
                />
              </label>
            </div>
            {generatingResidentCbhsReport ? (
              <div className="mt-2" style={appMutedTextStyle}>
                Preparing the monthly CBHS/IBSS report...
              </div>
            ) : null}
          </div>
          <DailyBehaviorLogPanel
            behaviorOptions={behaviorLogOptions}
            entries={residentBehaviorLogEntries}
            loading={loadingBehaviorLogEntries}
            saving={savingBehaviorLogEntry}
            emptyMessage="No provider behavior entries yet for this resident."
            onOpenCarePlan={() => navigate(getResidentCarePlanPath(homeId!, residentId!))}
            openCarePlanLabel="Go to Manage Care Plan"
            canGenerateReport
            collapsibleComposer
            defaultComposerVisible={false}
            collapsibleEntries
            serverEntriesPage={residentBehaviorLogPage}
            serverEntriesPageSize={10}
            serverEntriesHasPreviousPage={residentBehaviorLogPage > 1}
            serverEntriesHasNextPage={Boolean(residentBehaviorLogNextToken)}
            onEntriesPreviousPage={() => {
              const previousPage = Math.max(1, residentBehaviorLogPage - 1);
              void loadResidentBehaviorLogEntries({
                page: previousPage,
                nextToken: residentBehaviorLogPageTokens[previousPage - 1] || "",
              });
            }}
            onEntriesNextPage={() => {
              if (!residentBehaviorLogNextToken) {
                return;
              }
              const nextPage = residentBehaviorLogPage + 1;
              setResidentBehaviorLogPageTokens((previousState) => {
                const nextState = previousState.slice(0, nextPage);
                nextState[nextPage - 1] = residentBehaviorLogNextToken;
                return nextState;
              });
              void loadResidentBehaviorLogEntries({
                page: nextPage,
                nextToken: residentBehaviorLogNextToken,
              });
            }}
            canEditEntry={() => true}
            onFormatText={formatResidentBehaviorLogText}
            onCreateEntry={createResidentBehaviorLogRecord}
            onUpdateEntry={updateResidentBehaviorLogRecord}
            onDeleteDraft={deleteResidentBehaviorLogDraft}
            onGenerateReport={() => { void generateResidentBehaviorLogReport(); }}
            prnMedicationOptions={prnMedicationOptions}
            loadingPrnMedicationOptions={loadingPrnMedicationOptions}
            allowRecentEntryReuse={allowRecentEntryReuse}
          />
        </div>
      </Dialog>

      {residentId ? (
        <ResidentDailyLogImportDialog
          visible={showDailyLogImportDialog}
          residentId={residentId}
          behaviorOptions={behaviorLogOptions}
          onHide={() => setShowDailyLogImportDialog(false)}
          onImportCompleted={refreshResidentDailyLogTrackersAfterImport}
        />
      ) : null}

      <Dialog
        header={renderSurfaceHeader(
          "Resident Care Plan Tracking",
          "Review the latest provider care plan snapshot and a timeline of section and field changes.",
          "pi pi-directions-alt"
        )}
        visible={showCarePlanTrackingDialog}
        style={mobile ? { width: "95vw" } : appWideDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          carePlanTrackingAutoImportedRef.current = false;
          setShowCarePlanTrackingDialog(false);
        }}
      >
        {loadingCarePlanTracking ? (
          <div className="flex justify-content-center py-6">
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
          </div>
        ) : (
          <div className="flex flex-column gap-4">
            <div className="grid">
              <div className="col-12 md:col-4">
                <div style={appInnerCardStyle}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Sections Tracked</div>
                  <div className="mt-2" style={{ fontSize: "1.8rem", fontWeight: 800, color: "#0f172a" }}>
                    {carePlanSectionKeys.length}
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div style={appInnerCardStyle}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Timeline Entries</div>
                  <div className="mt-2" style={{ fontSize: "1.8rem", fontWeight: 800, color: "#0f172a" }}>
                    {residentCarePlanTrackingTimeline.length}
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div style={appInnerCardStyle}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Last Snapshot</div>
                  <div className="mt-2" style={{ ...appMutedTextStyle, fontWeight: 700 }}>
                    {residentCarePlanTrackingSnapshot?.lastBuiltAt
                      ? formatBehaviorTimestamp(residentCarePlanTrackingSnapshot.lastBuiltAt)
                      : "Not built yet"}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-content-end">
              <Button
                type="button"
                label="Import Now"
                icon="pi pi-refresh"
                severity="warning"
                rounded
                outlined
                loading={importingCarePlanTracking}
                onClick={async () => {
                  const importSucceeded = await importResidentCarePlanTracking("manual");
                  if (importSucceeded) {
                    await loadResidentCarePlanTracking(false);
                  }
                }}
              />
            </div>

            <TabView>
              <TabPanel header="Current Snapshot">
                {carePlanSectionKeys.length === 0 ? (
                  <div style={appInnerCardStyle}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>No care plan snapshot yet</div>
                    <div className="mt-2" style={appMutedTextStyle}>
                      A full snapshot will appear after the provider care plan is saved or regenerated. You can import the current care plan now to backfill older residents.
                    </div>
                    <Button
                      type="button"
                      label="Import Now"
                      icon="pi pi-refresh"
                      severity="warning"
                      rounded
                      outlined
                      className="mt-3"
                      loading={importingCarePlanTracking}
                      onClick={async () => {
                        const importSucceeded = await importResidentCarePlanTracking("manual");
                        if (importSucceeded) {
                          await loadResidentCarePlanTracking(false);
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex flex-column gap-3">
                    {carePlanSectionKeys.map((sectionKey: string) => {
                      const sectionSummary = carePlanSectionSummaries?.[sectionKey];
                      const sectionData = residentCarePlanTrackingSnapshot?.sections?.[sectionKey] || {};
                      const populatedEntries = flattenPopulatedCarePlanFields(sectionData);

                      return (
                        <div key={sectionKey} style={appInnerCardStyle}>
                          <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                            <div>
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>
                                {sectionSummary?.sectionLabel || sectionKey}
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
                                {sectionSummary?.populatedFieldCount || 0} populated fields
                                {sectionSummary?.populatedCollectionCount ? ` • ${sectionSummary.populatedCollectionCount} collections` : ""}
                              </div>
                            </div>
                          </div>
                          {populatedEntries.length > 0 ? (
                            <div className="grid mt-2">
                              {populatedEntries.map(({ fieldPath, value }) => (
                                <div key={`${sectionKey}-${fieldPath}`} className="col-12 md:col-6">
                                  <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.88rem" }}>
                                    {getReadableFieldPathLabel(fieldPath)}
                                  </div>
                                  <div className="mt-1" style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                                    {renderCarePlanTrackingValue(value)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-2" style={appMutedTextStyle}>No populated fields in this section right now.</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabPanel>
              <TabPanel header="Care Plan Timeline">
                <div className="grid mb-3">
                  <div className="col-12 md:col-6">
                    <Dropdown
                      value={carePlanTimelineFilter}
                      onChange={(e) => setCarePlanTimelineFilter(e.value)}
                      options={carePlanTimelineFilterOptions}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Filter by time"
                      className="w-full"
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <Dropdown
                      value={carePlanTimelineSectionFilter}
                      onChange={(e) => setCarePlanTimelineSectionFilter(e.value)}
                      options={carePlanSectionFilterOptions}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Filter by section"
                      className="w-full"
                    />
                  </div>
                </div>
                {filteredCarePlanTimeline.length === 0 ? (
                  <div style={appInnerCardStyle}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>No care plan timeline entries in this range</div>
                    <div className="mt-2" style={appMutedTextStyle}>
                      Try a wider filter or make another care plan update to create a new timeline entry.
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-column gap-3">
                    {filteredCarePlanTimeline.map((entry: any) => {
                      const changedSections = Array.isArray(entry?.changeSummary?.changedSections)
                        ? entry.changeSummary.changedSections
                        : [];
                      const fieldChangesBySection = entry?.changeSummary?.fieldChangesBySection || {};
                      const detailedFieldChangesBySection = entry?.changeSummary?.detailedFieldChangesBySection || {};
                      const visibleChangedSections = carePlanTimelineSectionFilter === "all"
                        ? changedSections
                        : changedSections.filter((sectionKey: string) => sectionKey === carePlanTimelineSectionFilter);
                      const visibleFieldChangesBySection = Object.fromEntries(
                        Object.entries(fieldChangesBySection).filter(([sectionKey]) =>
                          carePlanTimelineSectionFilter === "all" || sectionKey === carePlanTimelineSectionFilter
                        )
                      );
                      const visibleDetailedFieldChangesBySection = Object.fromEntries(
                        Object.entries(detailedFieldChangesBySection).filter(([sectionKey]) =>
                          carePlanTimelineSectionFilter === "all" || sectionKey === carePlanTimelineSectionFilter
                        )
                      );
                      const totalFieldChanges = Object.values(visibleFieldChangesBySection).reduce((sum: number, value: any) =>
                        sum + (Array.isArray(value) ? value.length : 0), 0);

                      return (
                        <div key={entry.timelineEntryId} style={appInnerCardStyle}>
                          <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                            <div>
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>
                                {getCarePlanTimelineEventLabel(String(entry?.eventType || ""))}
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
                                {formatBehaviorTimestamp(entry.recordedAt)}
                              </div>
                            </div>
                            <div style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
                              By {entry.changedBy || "system"}
                            </div>
                          </div>
                          <div className="grid mt-2">
                            <div className="col-12 md:col-4">
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>{visibleChangedSections.length}</div>
                              <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>Sections changed</div>
                            </div>
                            <div className="col-12 md:col-4">
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>{totalFieldChanges}</div>
                              <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>Field changes</div>
                            </div>
                            <div className="col-12 md:col-4">
                              <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                {visibleChangedSections.map((sectionKey: string) =>
                                  carePlanSectionSummaries?.[sectionKey]?.sectionLabel || sectionKey
                                ).join(", ") || "No sections listed"}
                              </div>
                            </div>
                          </div>
                          {visibleChangedSections.length > 0 ? (
                            <div className="mt-3 flex flex-column gap-3">
                              {visibleChangedSections.map((sectionKey: string) => {
                                const sectionLabel = carePlanSectionSummaries?.[sectionKey]?.sectionLabel || sectionKey;
                                const detailedChanges = Array.isArray(visibleDetailedFieldChangesBySection?.[sectionKey])
                                  ? (visibleDetailedFieldChangesBySection[sectionKey] as any[])
                                  : [];
                                const fallbackChanges = Array.isArray(visibleFieldChangesBySection?.[sectionKey])
                                  ? (visibleFieldChangesBySection[sectionKey] as string[])
                                  : [];

                                return (
                                  <div key={`${entry.timelineEntryId}-${sectionKey}`} style={appNeutralPanelStyle}>
                                    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{sectionLabel}</div>
                                      <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                                        {(detailedChanges.length || fallbackChanges.length)} field change(s)
                                      </div>
                                    </div>
                                    {detailedChanges.length > 0 ? (
                                      <div className="mt-3 flex flex-column gap-3">
                                        {detailedChanges.map((change: any, index: number) => (
                                          <div key={`${sectionKey}-${change.fieldPath}-${index}`} style={{ ...appInnerCardStyle, padding: "0.85rem" }}>
                                            <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                                              <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.88rem" }}>
                                                {getReadableFieldPathLabel(change.fieldPath)}
                                              </div>
                                              <div
                                                style={{
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  gap: "0.35rem",
                                                  borderRadius: "999px",
                                                  padding: "0.3rem 0.65rem",
                                                  background: "#fef3c7",
                                                  color: "#92400e",
                                                  fontSize: "0.76rem",
                                                  fontWeight: 700,
                                                }}
                                              >
                                                <i className="pi pi-arrows-h" style={{ fontSize: "0.72rem" }} />
                                                Changed
                                              </div>
                                            </div>
                                            <div className="grid mt-2">
                                              <div className="col-12 lg:col-6">
                                                <div style={{ fontWeight: 700, color: "#991b1b", fontSize: "0.8rem" }}>Before</div>
                                                <div
                                                  className="mt-1"
                                                  style={{
                                                    ...appMutedTextStyle,
                                                    whiteSpace: "pre-wrap",
                                                    background: "#fef2f2",
                                                    border: "1px solid #fecaca",
                                                    borderRadius: "12px",
                                                    padding: "0.75rem",
                                                  }}
                                                >
                                                  {renderCarePlanTrackingValue(change.previousValue)}
                                                </div>
                                              </div>
                                              <div className="col-12 lg:col-6">
                                                <div style={{ fontWeight: 700, color: "#166534", fontSize: "0.8rem" }}>After</div>
                                                <div
                                                  className="mt-1"
                                                  style={{
                                                    ...appMutedTextStyle,
                                                    whiteSpace: "pre-wrap",
                                                    background: "#f0fdf4",
                                                    border: "1px solid #bbf7d0",
                                                    borderRadius: "12px",
                                                    padding: "0.75rem",
                                                  }}
                                                >
                                                  {renderCarePlanTrackingValue(change.nextValue)}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : fallbackChanges.length > 0 ? (
                                      <div className="mt-2 flex flex-column gap-2">
                                        {fallbackChanges.map((fieldPath: string) => (
                                          <div key={`${sectionKey}-${fieldPath}`} style={appMutedTextStyle}>
                                            {getReadableFieldPathLabel(fieldPath)}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="mt-2" style={appMutedTextStyle}>No detailed field changes recorded for this section.</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabPanel>
            </TabView>
          </div>
        )}
      </Dialog>

      <Dialog
        header="ProviderOne ID Required"
        visible={showProviderOneIdDialog}
        style={mobile ? { width: "95vw" } : { width: "32rem" }}
        modal
        onHide={() => {
          if (!savingProviderOneId) {
            setShowProviderOneIdDialog(false);
          }
        }}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              rounded
              disabled={savingProviderOneId}
              onClick={() => setShowProviderOneIdDialog(false)}
            />
            <Button
              type="button"
              label="Save and Continue"
              severity="warning"
              rounded
              loading={savingProviderOneId}
              onClick={() => { void saveProviderOneIdAndGenerateResidentBehaviorReport(); }}
            />
          </div>
        )}
      >
        <div style={{ display: "grid", gap: "0.9rem" }}>
          <div style={appMutedTextStyle}>
            ProviderOne ID is needed before you can generate the CBHS/IBSS report. We'll save it to your profile so you only need to enter it once.
          </div>
          <span className="p-float-label">
            <InputText
              id="residentBehaviorProviderOneId"
              value={providerOneIdDraft}
              onChange={(event) => setProviderOneIdDraft(event.target.value)}
              maxLength={40}
              className="w-full"
            />
            <label htmlFor="residentBehaviorProviderOneId">ProviderOne ID</label>
          </span>
        </div>
      </Dialog>
      <Dialog
        header="Resident DOB Required"
        visible={showResidentDobDialog}
        style={mobile ? { width: "95vw" } : { width: "32rem" }}
        modal
        onHide={() => {
          if (!savingResidentDob) {
            setShowResidentDobDialog(false);
          }
        }}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              rounded
              disabled={savingResidentDob}
              onClick={() => setShowResidentDobDialog(false)}
            />
            <Button
              type="button"
              label="Save and Continue"
              severity="warning"
              rounded
              loading={savingResidentDob}
              onClick={() => { void saveResidentDobAndGenerateResidentBehaviorReport(); }}
            />
          </div>
        )}
      >
        <div style={{ display: "grid", gap: "0.9rem" }}>
          <div style={appMutedTextStyle}>
            Resident date of birth is needed before you can generate the CBHS/IBSS report. We'll save it to the resident profile so you only need to enter it once.
          </div>
          <label style={{ display: "grid", gap: "0.4rem" }}>
            <span style={{ fontWeight: 700, color: "#0f172a" }}>Resident Date of Birth</span>
            <input
              type="date"
              value={residentDobDraft}
              onChange={(event) => setResidentDobDraft(event.target.value)}
              className="p-inputtext p-component w-full"
            />
          </label>
        </div>
      </Dialog>

      {/* Show compare assessment summary in HTML format */}
      <Dialog
        header={renderSurfaceHeader("Assessment Comparison", "Compare the latest assessment summary with the previous one.", "pi pi-chart-bar")}
        visible={showCompareAssessmentPopUp}
        style={{ width: '90%' }}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        modal
        onHide={() => {
          setShowCompareAssessmentPopUp(false);
          setShowStandaloneCompareDialog(false);
        }} closeOnEscape={false} footer={
          <div className="flex flex-wrap justify-content-between align-items-center gap-3">
            <p style={{ margin: 0 }}>
              <i style={{ color: "orange" }}>{AI_MESSAGE_FOR_COMPLIANCE}</i>
            </p>
            {compareAssessmentSummaryData && (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  label="Save to Binder"
                  severity="warning"
                  outlined
                  rounded
                  style={{ width: "auto" }}
                  onClick={async () => {
                    try {
                      await saveGeneratedHtmlToResidentBinder({
                        folderCode: RESIDENT_BINDER_FOLDER_CODES.ASSESSMENT,
                        title: "Assessment Comparison",
                        fileNamePrefix: "assessment-comparison",
                        htmlContent: compareAssessmentSummaryData,
                      });
                      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Assessment comparison saved to resident binder.', life: 4000 });
                    } catch (error: any) {
                      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error?.message || "Unable to save assessment comparison to binder.", life: 4000 });
                    }
                  }}
                />
                <Button
                  type="button"
                  label="Download Comparison"
                  severity="secondary"
                  outlined
                  rounded
                  style={{ width: "auto" }}
                  onClick={() => downloadHtmlContent("Assessment Comparison", compareAssessmentSummaryData, `assessment-comparison-${residentId || "resident"}.doc`)}
                />
              </div>
            )}
          </div>
        }>
        <div>
          {
            compareAssessmentSummaryData ? (
              <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(compareAssessmentSummaryData) }} />
            ) : (
              <div className="col-12 text-center d-flex flex-column justify-content-center align-items-center">
                <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                <div className="block mt-3" style={{ color: "#0f172a", fontWeight: 700 }}>
                  Generating comparison...
                </div>
                <div className="block mt-2" style={{ color: "#596F80" }}>
                  <strong>Job Status:</strong> {compareJobStatus || "Starting"}
                </div>
                <div className="block mt-1" style={{ color: "#596F80" }}>
                  <strong>Job Sub-Status:</strong> {compareJobSubStatus || "Preparing comparison"}
                </div>
                <div className="block mt-2" style={{ color: "#596F80" }}>
                  Elapsed time: {formatElapsedTime(compareElapsedSeconds)}
                </div>
                <div className="block mt-2" style={{ color: "#596F80" }}>
                  This usually takes about 5 minutes. Please keep the browser open and let this request finish before starting another one.
                </div>
              </div>
            )
          }
        </div>
      </Dialog>

    </div >
  );
};

export default ResidentDetailsPage;


const getCbhsMonthDateValue = (monthKey?: string) => {
  const normalizedMonthKey = String(monthKey || "").trim();
  if (!/^\d{4}-\d{2}$/.test(normalizedMonthKey)) {
    return null;
  }

  const [year, month] = normalizedMonthKey.split("-").map((value) => Number(value));
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, 1, 12, 0, 0));
};

const getCbhsMonthKeyFromDateValue = (value?: Date | Date[] | null) => {
  const resolvedValue = Array.isArray(value) ? value[0] : value;
  if (!(resolvedValue instanceof Date) || Number.isNaN(resolvedValue.getTime())) {
    return "";
  }

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
  });
  const parts = formatter.formatToParts(resolvedValue);
  const year = parts.find((part) => part.type === "year")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  return year && month ? `${year}-${month}` : "";
};
